import type { Language } from "@src/reducers/your-reducer";
import type { OpcUaStatusCode } from "./opcua-status-codes";

type OpcUaTranslationLanguage = Exclude<Language, null>;

export const OPCUA_STATUS_CODE_TRANSLATIONS: Record<
  OpcUaTranslationLanguage,
  Record<OpcUaStatusCode, string>
> = {
  en: {
    "0x00000000": "The operation succeeded.",
    "0x40000000": "The operation was uncertain.",
    "0x80000000": "The operation failed.",
    "0x80010000": "An unexpected error occurred.",
    "0x80020000":
      "An internal error occurred as a result of a programming or configuration error.",
    "0x80030000": "Not enough memory to complete the operation.",
    "0x80040000": "An operating system resource is not available.",
    "0x80050000": "A low level communication error occurred.",
    "0x80060000":
      "Encoding halted because of invalid data in the objects being serialized.",
    "0x80070000": "Decoding halted because of invalid data in the stream.",
    "0x80080000":
      "The message encoding/decoding limits imposed by the stack have been exceeded.",
    "0x80B80000": "The request message size exceeds limits set by the server.",
    "0x80B90000":
      "The response message size exceeds limits set by the client or server.",
    "0x80090000": "An unrecognized response was received from the server.",
    "0x800A0000": "The operation timed out.",
    "0x800B0000": "The server does not support the requested service.",
    "0x800C0000":
      "The operation was cancelled because the application is shutting down.",
    "0x800D0000":
      "The operation could not complete because the client is not connected to the server.",
    "0x800E0000": "The server has stopped and cannot process any requests.",
    "0x800F0000":
      "No processing could be done because there was nothing to do.",
    "0x80100000":
      "The request could not be processed because it specified too many operations.",
    "0x80DB0000":
      "The request could not be processed because there are too many monitored items in the subscription.",
    "0x80110000":
      "The extension object cannot be (de)serialized because the data type id is not recognized.",
    "0x80120000": "The certificate provided as a parameter is not valid.",
    "0x80130000": "An error occurred verifying security.",
    "0x81140000":
      "The certificate does not meet the requirements of the security policy.",
    "0x80140000": "The certificate has expired or is not yet valid.",
    "0x80150000": "An issuer certificate has expired or is not yet valid.",
    "0x80160000":
      "The HostName used to connect to a server does not match a HostName in the certificate.",
    "0x80170000":
      "The URI specified in the ApplicationDescription does not match the URI in the certificate.",
    "0x80180000":
      "The certificate may not be used for the requested operation.",
    "0x80190000":
      "The issuer certificate may not be used for the requested operation.",
    "0x801A0000": "The certificate is not trusted.",
    "0x801B0000":
      "It was not possible to determine if the certificate has been revoked.",
    "0x801C0000":
      "It was not possible to determine if the issuer certificate has been revoked.",
    "0x801D0000": "The certificate has been revoked.",
    "0x801E0000": "The issuer certificate has been revoked.",
    "0x810D0000": "The certificate chain is incomplete.",
    "0x801F0000":
      "User does not have permission to perform the requested operation.",
    "0x80200000": "The user identity token is not valid.",
    "0x80210000":
      "The user identity token is valid but the server has rejected it.",
    "0x80220000": "The specified secure channel is no longer valid.",
    "0x80230000": "The timestamp is outside the range allowed by the server.",
    "0x80240000":
      "The nonce does appear to be not a random value or it is not the correct length.",
    "0x80250000": "The session id is not valid.",
    "0x80260000": "The session was closed by the client.",
    "0x80270000":
      "The session cannot be used because ActivateSession has not been called.",
    "0x80280000": "The subscription id is not valid.",
    "0x802A0000": "The header for the request is missing or invalid.",
    "0x802B0000": "The timestamps to return parameter is invalid.",
    "0x802C0000": "The request was cancelled by the client.",
    "0x80E50000": "Too many arguments were provided.",
    "0x810E0000":
      "The server requires a license to operate in general or to perform a service or operation, but existing license is expired.",
    "0x810F0000":
      "The server has limits on number of allowed operations / objects, based on installed licenses, and these limits where exceeded.",
    "0x81100000":
      "The server does not have a license which is required to operate in general or to perform a service or operation.",
    "0x80EE0000":
      "The Server does not have the resources to process the request at this time.",
    "0x00EF0000":
      "The log-on for the user succeeded but the user is required to change the password.",
    "0x002D0000": "The subscription was transferred to another session.",
    "0x002E0000": "The processing will complete asynchronously.",
    "0x002F0000": "Sampling has slowed down due to resource limitations.",
    "0x00300000": "The value written was accepted but was clamped.",
    "0x80310000":
      "Communication with the data source is defined, but not established, and there is no last known value available.",
    "0x80320000":
      "Waiting for the server to obtain values from the underlying data source.",
    "0x80330000":
      "The syntax the node id is not valid or refers to a node that is not valid for the operation.",
    "0x80340000":
      "The node id refers to a node that does not exist in the server address space.",
    "0x80350000": "The attribute is not supported for the specified Node.",
    "0x80360000": "The syntax of the index range parameter is invalid.",
    "0x80370000": "No data exists within the range of indexes specified.",
    "0x80EA0000": "The written data does not match the IndexRange specified.",
    "0x80380000": "The data encoding is invalid.",
    "0x80390000":
      "The server does not support the requested data encoding for the node.",
    "0x803A0000":
      "The access level does not allow reading or subscribing to the Node.",
    "0x803B0000": "The access level does not allow writing to the Node.",
    "0x803C0000": "The value was out of range.",
    "0x40F20000": "The value is over the allowed range.",
    "0x40F30000": "The value is under the allowed range.",
    "0x803D0000": "The requested operation is not supported.",
    "0x803E0000":
      "A requested item was not found or a search operation ended without success.",
    "0x803F0000": "The object cannot be used because it has been deleted.",
    "0x80400000": "Requested operation is not implemented.",
    "0x80410000": "The monitoring mode is invalid.",
    "0x80420000":
      "The monitoring item id does not refer to a valid monitored item.",
    "0x80430000": "The monitored item filter parameter is not valid.",
    "0x80440000":
      "The server does not support the requested monitored item filter.",
    "0x80450000":
      "A monitoring filter cannot be used in combination with the attribute specified.",
    "0x80460000": "A mandatory structured parameter was missing or null.",
    "0x80470000": "The event filter is not valid.",
    "0x80480000": "The content filter is not valid.",
    "0x80C10000": "An unrecognized operator was provided in a filter.",
    "0x80C20000":
      "A valid operator was provided, but the server does not provide support for this filter operator.",
    "0x80C30000":
      "The number of operands provided for the filter operator was less then expected for the operand provided.",
    "0x80490000": "The operand used in a content filter is not valid.",
    "0x80C40000":
      "The referenced element is not a valid element in the content filter.",
    "0x80C50000": "The referenced literal is not a valid value.",
    "0x804A0000": "The continuation point provide is longer valid.",
    "0x804B0000":
      "The operation could not be processed because all continuation points have been allocated.",
    "0x804C0000":
      "The reference type id does not refer to a valid reference type node.",
    "0x804D0000": "The browse direction is not valid.",
    "0x804E0000": "The node is not part of the view.",
    "0x81120000": "The number was not accepted because of a numeric overflow.",
    "0x80ED0000":
      "The locale in the requested write operation is not supported.",
    "0x80F00000": "The variable has no default value and no initial value.",
    "0x804F0000": "The ServerUri is not a valid URI.",
    "0x80500000": "No ServerName was specified.",
    "0x80510000": "No DiscoveryUrl was specified.",
    "0x80520000": "The semaphore file specified by the client is not valid.",
    "0x80530000": "The security token request type is not valid.",
    "0x80540000":
      "The security mode does not meet the requirements set by the server.",
    "0x80550000":
      "The security policy does not meet the requirements set by the server.",
    "0x80560000": "The server has reached its maximum number of sessions.",
    "0x80570000": "The user token signature is missing or invalid.",
    "0x80580000":
      "The signature generated with the client certificate is missing or invalid.",
    "0x80590000":
      "The client did not provide at least one software certificate that is valid and meets the profile requirements for the server.",
    "0x80C60000":
      "The server does not support changing the user identity assigned to the session.",
    "0x805A0000":
      "The request was cancelled by the client with the Cancel service.",
    "0x805B0000": "The parent node id does not to refer to a valid node.",
    "0x805C0000":
      "The reference could not be created because it violates constraints imposed by the data model.",
    "0x805D0000":
      "The requested node id was reject because it was either invalid or server does not allow node ids to be specified by the client.",
    "0x805E0000": "The requested node id is already used by another node.",
    "0x805F0000": "The node class is not valid.",
    "0x80600000": "The browse name is invalid.",
    "0x80610000":
      "The browse name is not unique among nodes that share the same relationship with the parent.",
    "0x80620000": "The node attributes are not valid for the node class.",
    "0x80630000":
      "The type definition node id does not reference an appropriate type node.",
    "0x80640000": "The source node id does not reference a valid node.",
    "0x80650000": "The target node id does not reference a valid node.",
    "0x80660000": "The reference type between the nodes is already defined.",
    "0x80670000":
      "The server does not allow this type of self reference on this node.",
    "0x80680000":
      "The reference type is not valid for a reference to a remote server.",
    "0x80690000": "The server will not allow the node to be deleted.",
    "0x40BC0000": "The server was not able to delete all target references.",
    "0x806A0000": "The server index is not valid.",
    "0x806B0000": "The view id does not refer to a valid view node.",
    "0x80C90000": "The view timestamp is not available or not supported.",
    "0x80CA0000": "The view parameters are not consistent with each other.",
    "0x80CB0000": "The view version is not available or not supported.",
    "0x40C00000":
      "The list of references may not be complete because the underlying system is not available.",
    "0x00BA0000":
      "The server should have followed a reference to a node in a remote server but did not. The result set may be incomplete.",
    "0x80C80000": "The provided Nodeid was not a type definition nodeid.",
    "0x406C0000":
      "One of the references to follow in the relative path references to a node in the address space in another server.",
    "0x806D0000": "The requested operation has too many matches to return.",
    "0x806E0000":
      "The requested operation requires too many resources in the server.",
    "0x806F0000": "The requested operation has no match to return.",
    "0x80700000": "The max age parameter is invalid.",
    "0x80E60000":
      "The operation is not permitted over the current secure channel.",
    "0x80710000": "The history details parameter is not valid.",
    "0x80720000": "The server does not support the requested operation.",
    "0x80BD0000": "The defined timestamp to return was invalid.",
    "0x80730000":
      "The server does not support writing the combination of value, status and timestamps provided.",
    "0x80740000":
      "The value supplied for the attribute is not of the same type as the attribute's value.",
    "0x80750000":
      "The method id does not refer to a method for the specified object.",
    "0x80760000":
      "The client did not specify all of the input arguments for the method.",
    "0x81110000":
      "The executable attribute does not allow the execution of the method.",
    "0x80770000": "The server has reached its maximum number of subscriptions.",
    "0x80780000":
      "The server has reached the maximum number of queued publish requests.",
    "0x80790000": "There is no subscription available for this session.",
    "0x807A0000": "The sequence number is unknown to the server.",
    "0x00DF0000":
      "The Server does not support retransmission queue and acknowledgement of sequence numbers is not available.",
    "0x807B0000": "The requested notification message is no longer available.",
    "0x807C0000":
      "The client of the current session does not support one or more Profiles that are necessary for the subscription.",
    "0x80BF0000": "The sub-state machine is not currently active.",
    "0x81150000": "An equivalent rule already exists.",
    "0x807D0000":
      "The server cannot process the request because it is too busy.",
    "0x807E0000": "The type of the message specified in the header invalid.",
    "0x807F0000":
      "The SecureChannelId and/or TokenId are not currently in use.",
    "0x80800000":
      "The size of the message chunk specified in the header is too large.",
    "0x80810000": "There are not enough resources to process the request.",
    "0x80820000": "An internal error occurred.",
    "0x80830000": "The server does not recognize the QueryString specified.",
    "0x80840000":
      "The request could not be sent because of a network interruption.",
    "0x80850000": "Timeout occurred while processing the request.",
    "0x80860000": "The secure channel has been closed.",
    "0x80870000": "The token has expired or is not recognized.",
    "0x80880000": "The sequence number is not valid.",
    "0x80BE0000": "The applications do not have compatible protocol versions.",
    "0x80890000":
      "There is a problem with the configuration that affects the usefulness of the value.",
    "0x808A0000":
      "The variable should receive its value from another variable, but has never been configured to do so.",
    "0x808B0000":
      "There has been a failure in the device/data source that generates the value that has affected the value.",
    "0x808C0000":
      "There has been a failure in the sensor from which the value is derived by the device/data source.",
    "0x808D0000": "The source of the data is not operational.",
    "0x808E0000": "The deadband filter is not valid.",
    "0x408F0000":
      "Communication to the data source has failed. The variable value is the last value that had a good quality.",
    "0x40900000": "Whatever was updating this value has stopped doing so.",
    "0x40910000":
      "The value is an operational value that was manually overwritten.",
    "0x40920000":
      "The value is an initial value for a variable that normally receives its value from another variable.",
    "0x40930000": "The value is at one of the sensor limits.",
    "0x40940000":
      "The value is outside of the range of values defined for this parameter.",
    "0x40950000":
      "The data value is derived from multiple sources and has less than the required number of Good sources.",
    "0x00960000": "The value has been overridden.",
    "0x00EB0000":
      "The value is derived from multiple sources and has the required number of Good sources, but less than the full number of Good sources.",
    "0x80970000":
      "This Condition refresh failed, a Condition refresh operation is already in progress.",
    "0x80980000": "This condition has already been disabled.",
    "0x80CC0000": "This condition has already been enabled.",
    "0x80990000": "Property not available, this condition is disabled.",
    "0x809A0000": "The specified event id is not recognized.",
    "0x80BB0000": "The event cannot be acknowledged.",
    "0x80CD0000": "The dialog condition is not active.",
    "0x80CE0000": "The response is not valid for the dialog.",
    "0x80CF0000": "The condition branch has already been acknowledged.",
    "0x80D00000": "The condition branch has already been confirmed.",
    "0x80D10000": "The condition has already been shelved.",
    "0x80D20000": "The condition is not currently shelved.",
    "0x80D30000": "The shelving time not within an acceptable range.",
    "0x809B0000":
      "No data exists for the requested time range or event filter.",
    "0x80D70000": "No data found to provide upper or lower bound value.",
    "0x80D80000": "The server cannot retrieve a bound for the variable.",
    "0x809D0000": "Data is missing due to collection started/stopped/lost.",
    "0x809E0000":
      "Expected data is unavailable for the requested time range due to an un-mounted volume, an off-line archive or tape, or similar reason for temporary unavailability.",
    "0x809F0000":
      "The data or event was not successfully inserted because a matching entry exists.",
    "0x80A00000":
      "The data or event was not successfully updated because no matching entry exists.",
    "0x80A10000":
      "The Client requested history using a TimestampsToReturn the Server does not support.",
    "0x00A20000":
      "The data or event was successfully inserted into the historical database.",
    "0x00A30000":
      "The data or event field was successfully replaced in the historical database.",
    "0x40A40000":
      "The aggregate value is derived from multiple values and has less than the required number of Good values.",
    "0x00A50000":
      "No data exists for the requested time range or event filter.",
    "0x00A60000":
      "More data is available in the time range beyond the number of values requested.",
    "0x80D40000":
      "The requested number of Aggregates does not match the requested number of NodeIds.",
    "0x80D50000": "The requested Aggregate is not support by the server.",
    "0x80D60000":
      "The aggregate value could not be derived due to invalid data inputs.",
    "0x80DA0000":
      "The aggregate configuration is not valid for specified node.",
    "0x00D90000":
      "The request specifies fields which are not valid for the EventType or cannot be saved by the historian.",
    "0x80E40000":
      "The request was rejected by the server because it did not meet the criteria set by the server.",
    "0x81130000": "The request has not been processed by the server yet.",
    "0x80E80000":
      "The operation is not allowed because a transaction is in progress.",
    "0x80F10000":
      "The operation failed and all changes which were part of the transaction are rolled back.",
    "0x811F0000":
      "The device identity needs a ticket before it can be accepted.",
    "0x81200000":
      "The device identity needs a ticket before it can be accepted.",
    "0x80E90000":
      "The requested operation is not allowed, because the Node is locked by a different application.",
    "0x80EC0000":
      "The requested operation is not allowed, because the Node is not locked by the application.",
    "0x00DC0000":
      "The value does not come from the real source and has been edited by the server.",
    "0x00DD0000": "There was an error in execution of these post-actions.",
    "0x40DE0000":
      "The related EngineeringUnit has been changed but the Variable Value is still provided based on the previous unit.",
    "0x00E00000":
      "A dependent value has been changed but the change has not been applied to the device.",
    "0x80E10000":
      "The related EngineeringUnit has been changed but this change has not been applied to the device. The Variable Value is still dependent on the previous unit but its status is currently Bad.",
    "0x40E20000":
      "A dependent value has been changed but the change has not been applied to the device. The quality of the dominant variable is uncertain.",
    "0x80E30000":
      "A dependent value has been changed but the change has not been applied to the device. The quality of the dominant variable is Bad.",
    "0x01160000":
      "It is delivered with a dominant Variable value when a dependent Variable has changed but the change has not been applied.",
    "0x01170000":
      "It is delivered with a dependent Variable value when a dominant Variable has changed but the change has not been applied.",
    "0x01180000":
      "It is delivered with a dependent Variable value when a dominant or dependent Variable has changed but change has not been applied.",
    "0x81190000":
      "It is delivered with a Variable value when Variable has changed but the value is not legal.",
    "0x811A0000":
      "It is delivered with a Variable value when a source Variable has changed but the value is not legal.",
    "0x811B0000":
      "It is delivered with a dependent Variable value when a dominant Variable has changed and the value is not legal.",
    "0x811C0000":
      "It is delivered with a dependent Variable value when a dominant Variable has changed, the value is not legal and the change has not been applied.",
    "0x811D0000":
      "It is delivered with a dependent Variable value when a dominant or dependent Variable has changed and the value is not legal.",
    "0x811E0000":
      "It is delivered with a dependent Variable value when a dominant or dependent Variable has changed, the value is not legal and the change has not been applied.",
    "0x00A70000": "The communication layer has raised an event.",
    "0x00A80000": "The system is shutting down.",
    "0x00A90000": "The operation is not finished and needs to be called again.",
    "0x00AA0000": "A non-critical timeout occurred.",
    "0x80AB0000": "One or more arguments are invalid.",
    "0x80AC0000": "Could not establish a network connection to remote server.",
    "0x80AD0000": "The server has disconnected from the client.",
    "0x80AE0000": "The network connection has been closed.",
    "0x80AF0000":
      "The operation cannot be completed because the object is closed, uninitialized or in some other invalid state.",
    "0x80B00000": "Cannot move beyond end of the stream.",
    "0x80B10000":
      "No data is currently available for reading from a non-blocking stream.",
    "0x80B20000": "The asynchronous operation is waiting for a response.",
    "0x80B30000": "The asynchronous operation was abandoned by the caller.",
    "0x80B40000":
      "The stream did not return all data requested (possibly because it is a non-blocking stream).",
    "0x80B50000":
      "Non blocking behaviour is required and the operation would block.",
    "0x80B60000": "A value had an invalid syntax.",
    "0x80B70000":
      "The operation could not be finished because all available connections are in use.",
    "0x42080000":
      "The value may not be accurate because the transducer is in manual mode.",
    "0x42090000": "The value is simulated.",
    "0x420A0000":
      "The value may not be accurate due to a sensor calibration fault.",
    "0x420F0000": "The value may not be accurate due to a configuration issue.",
    "0x04010000":
      "The value source supports cascade handshaking and the value has been Initialized based on an initialization request from a cascade secondary.",
    "0x04020000":
      "The value source supports cascade handshaking and is requesting initialization of a cascade primary.",
    "0x04030000":
      "The value source supports cascade handshaking, however, the source's current state does not allow for cascade.",
    "0x04040000":
      "The value source supports cascade handshaking, however, the source has not selected the corresponding cascade primary for use.",
    "0x04070000":
      "There is a fault state condition active in the value source.",
    "0x04080000":
      "A fault state condition is being requested of the destination.",
    "0x04090000":
      "The value is accurate, and the signal source supports cascade handshaking.",
    "0x80E70000":
      "The DataSet specified for the DataSetWriter creation is invalid.",
  },
  de: {
    "0x00000000": "Der Vorgang wurde erfolgreich ausgeführt.",
    "0x40000000": "Der Vorgang war unsicher.",
    "0x80000000": "Der Vorgang ist fehlgeschlagen.",
    "0x80010000": "Ein unerwarteter Fehler ist aufgetreten.",
    "0x80020000":
      "Ein interner Fehler ist infolge eines Programmier- oder Konfigurationsfehlers aufgetreten.",
    "0x80030000": "Nicht genügend Speicher, um den Vorgang abzuschließen.",
    "0x80040000": "Eine Betriebssystemressource ist nicht verfügbar.",
    "0x80050000":
      "Ein Kommunikationsfehler auf niedriger Ebene ist aufgetreten.",
    "0x80060000":
      "Die Kodierung wurde aufgrund ungültiger Daten in den zu serialisierenden Objekten abgebrochen.",
    "0x80070000":
      "Die Dekodierung wurde aufgrund ungültiger Daten im Datenstrom abgebrochen.",
    "0x80080000":
      "Die vom Stack vorgegebenen Grenzwerte für die Nachrichtenkodierung/-dekodierung wurden überschritten.",
    "0x80B80000":
      "Die Größe der Anforderungsnachricht überschreitet die vom Server festgelegten Grenzwerte.",
    "0x80B90000":
      "Die Größe der Antwortnachricht überschreitet die vom Client oder Server festgelegten Grenzwerte.",
    "0x80090000": "Vom Server wurde eine nicht erkannte Antwort empfangen.",
    "0x800A0000": "Für den Vorgang ist eine Zeitüberschreitung aufgetreten.",
    "0x800B0000": "Der Server unterstützt den angeforderten Dienst nicht.",
    "0x800C0000":
      "Der Vorgang wurde abgebrochen, da die Anwendung heruntergefahren wird.",
    "0x800D0000":
      "Der Vorgang konnte nicht abgeschlossen werden, da der Client nicht mit dem Server verbunden ist.",
    "0x800E0000":
      "Der Server wurde angehalten und kann keine Anforderungen verarbeiten.",
    "0x800F0000":
      "Es konnte keine Verarbeitung durchgeführt werden, da nichts zu tun war.",
    "0x80100000":
      "Die Anforderung konnte nicht verarbeitet werden, da sie zu viele Vorgänge angab.",
    "0x80DB0000":
      "Die Anforderung konnte nicht verarbeitet werden, da das Abonnement zu viele überwachte Elemente enthält.",
    "0x80110000":
      "Das Erweiterungsobjekt kann nicht (de-)serialisiert werden, da die Datentyp-ID nicht erkannt wird.",
    "0x80120000": "Das als Parameter bereitgestellte Zertifikat ist ungültig.",
    "0x80130000": "Bei der Sicherheitsüberprüfung ist ein Fehler aufgetreten.",
    "0x81140000":
      "Das Zertifikat erfüllt die Anforderungen der Sicherheitsrichtlinie nicht.",
    "0x80140000": "Das Zertifikat ist abgelaufen oder noch nicht gültig.",
    "0x80150000":
      "Ein Ausstellerzertifikat ist abgelaufen oder noch nicht gültig.",
    "0x80160000":
      "Der zur Verbindung mit einem Server verwendete HostName stimmt mit keinem HostName im Zertifikat überein.",
    "0x80170000":
      "Die in der ApplicationDescription angegebene URI stimmt nicht mit der URI im Zertifikat überein.",
    "0x80180000":
      "Das Zertifikat darf für den angeforderten Vorgang nicht verwendet werden.",
    "0x80190000":
      "Das Ausstellerzertifikat darf für den angeforderten Vorgang nicht verwendet werden.",
    "0x801A0000": "Dem Zertifikat wird nicht vertraut.",
    "0x801B0000":
      "Es konnte nicht ermittelt werden, ob das Zertifikat widerrufen wurde.",
    "0x801C0000":
      "Es konnte nicht ermittelt werden, ob das Ausstellerzertifikat widerrufen wurde.",
    "0x801D0000": "Das Zertifikat wurde widerrufen.",
    "0x801E0000": "Das Ausstellerzertifikat wurde widerrufen.",
    "0x810D0000": "Die Zertifikatskette ist unvollständig.",
    "0x801F0000":
      "Der Benutzer hat keine Berechtigung, den angeforderten Vorgang auszuführen.",
    "0x80200000": "Das Benutzeridentitätstoken ist ungültig.",
    "0x80210000":
      "Das Benutzeridentitätstoken ist gültig, wurde vom Server jedoch abgelehnt.",
    "0x80220000": "Der angegebene sichere Kanal ist nicht mehr gültig.",
    "0x80230000":
      "Der Zeitstempel liegt außerhalb des vom Server zulässigen Bereichs.",
    "0x80240000":
      "Die Nonce scheint kein Zufallswert zu sein oder hat nicht die richtige Länge.",
    "0x80250000": "Die Sitzungs-ID ist ungültig.",
    "0x80260000": "Die Sitzung wurde vom Client geschlossen.",
    "0x80270000":
      "Die Sitzung kann nicht verwendet werden, da ActivateSession nicht aufgerufen wurde.",
    "0x80280000": "Die Abonnement-ID ist ungültig.",
    "0x802A0000": "Der Header für die Anforderung fehlt oder ist ungültig.",
    "0x802B0000":
      "Der Parameter für die zurückzugebenden Zeitstempel ist ungültig.",
    "0x802C0000": "Die Anforderung wurde vom Client abgebrochen.",
    "0x80E50000": "Es wurden zu viele Argumente bereitgestellt.",
    "0x810E0000":
      "Der Server benötigt allgemein oder zur Ausführung eines Dienstes bzw. Vorgangs eine Lizenz, die vorhandene Lizenz ist jedoch abgelaufen.",
    "0x810F0000":
      "Der Server hat auf Grundlage der installierten Lizenzen Grenzwerte für die Anzahl zulässiger Vorgänge/Objekte, und diese Grenzwerte wurden überschritten.",
    "0x81100000":
      "Der Server verfügt nicht über die Lizenz, die allgemein oder zur Ausführung eines Dienstes bzw. Vorgangs erforderlich ist.",
    "0x80EE0000":
      "Der Server verfügt derzeit nicht über die Ressourcen, um die Anforderung zu verarbeiten.",
    "0x00EF0000":
      "Die Anmeldung des Benutzers war erfolgreich, der Benutzer muss jedoch das Kennwort ändern.",
    "0x002D0000": "Das Abonnement wurde auf eine andere Sitzung übertragen.",
    "0x002E0000": "Die Verarbeitung wird asynchron abgeschlossen.",
    "0x002F0000":
      "Das Sampling wurde aufgrund von Ressourcenbeschränkungen verlangsamt.",
    "0x00300000": "Der geschriebene Wert wurde akzeptiert, jedoch begrenzt.",
    "0x80310000":
      "Die Kommunikation mit der Datenquelle ist definiert, aber nicht hergestellt, und es ist kein letzter bekannter Wert verfügbar.",
    "0x80320000":
      "Es wird darauf gewartet, dass der Server Werte von der zugrunde liegenden Datenquelle erhält.",
    "0x80330000":
      "Die Syntax der Node-ID ist ungültig oder verweist auf einen Knoten, der für den Vorgang ungültig ist.",
    "0x80340000":
      "Die Node-ID verweist auf einen Knoten, der im Adressraum des Servers nicht existiert.",
    "0x80350000":
      "Das Attribut wird für den angegebenen Node nicht unterstützt.",
    "0x80360000": "Die Syntax des Indexbereichsparameters ist ungültig.",
    "0x80370000":
      "Innerhalb des angegebenen Indexbereichs sind keine Daten vorhanden.",
    "0x80EA0000":
      "Die geschriebenen Daten stimmen nicht mit dem angegebenen IndexRange überein.",
    "0x80380000": "Die Datenkodierung ist ungültig.",
    "0x80390000":
      "Der Server unterstützt die angeforderte Datenkodierung für den Knoten nicht.",
    "0x803A0000":
      "Die Zugriffsebene erlaubt weder das Lesen noch das Abonnieren des Node.",
    "0x803B0000": "Die Zugriffsebene erlaubt das Schreiben auf den Node nicht.",
    "0x803C0000": "Der Wert lag außerhalb des zulässigen Bereichs.",
    "0x40F20000": "Der Wert liegt über dem zulässigen Bereich.",
    "0x40F30000": "Der Wert liegt unter dem zulässigen Bereich.",
    "0x803D0000": "Der angeforderte Vorgang wird nicht unterstützt.",
    "0x803E0000":
      "Ein angefordertes Element wurde nicht gefunden oder ein Suchvorgang endete ohne Erfolg.",
    "0x803F0000":
      "Das Objekt kann nicht verwendet werden, da es gelöscht wurde.",
    "0x80400000": "Der angeforderte Vorgang ist nicht implementiert.",
    "0x80410000": "Der Überwachungsmodus ist ungültig.",
    "0x80420000":
      "Die ID des überwachten Elements verweist nicht auf ein gültiges überwachtes Element.",
    "0x80430000": "Der Filterparameter des überwachten Elements ist ungültig.",
    "0x80440000":
      "Der Server unterstützt den angeforderten Filter für überwachte Elemente nicht.",
    "0x80450000":
      "Ein Überwachungsfilter kann nicht mit dem angegebenen Attribut kombiniert werden.",
    "0x80460000":
      "Ein obligatorischer strukturierter Parameter fehlte oder war null.",
    "0x80470000": "Der Ereignisfilter ist ungültig.",
    "0x80480000": "Der Inhaltsfilter ist ungültig.",
    "0x80C10000":
      "In einem Filter wurde ein nicht erkannter Operator angegeben.",
    "0x80C20000":
      "Es wurde ein gültiger Operator angegeben, der Server unterstützt diesen Filteroperator jedoch nicht.",
    "0x80C30000":
      "Die Anzahl der für den Filteroperator bereitgestellten Operanden war geringer als für den bereitgestellten Operanden erwartet.",
    "0x80490000": "Der in einem Inhaltsfilter verwendete Operand ist ungültig.",
    "0x80C40000":
      "Das referenzierte Element ist kein gültiges Element im Inhaltsfilter.",
    "0x80C50000": "Das referenzierte Literal ist kein gültiger Wert.",
    "0x804A0000":
      "Der bereitgestellte Fortsetzungspunkt ist nicht mehr gültig.",
    "0x804B0000":
      "Der Vorgang konnte nicht verarbeitet werden, da alle Fortsetzungspunkte zugewiesen sind.",
    "0x804C0000":
      "Die Referenztyp-ID verweist nicht auf einen gültigen Referenztyp-Knoten.",
    "0x804D0000": "Die Browse-Richtung ist ungültig.",
    "0x804E0000": "Der Knoten ist nicht Teil der Ansicht.",
    "0x81120000":
      "Die Zahl wurde aufgrund eines numerischen Überlaufs nicht akzeptiert.",
    "0x80ED0000":
      "Das Gebietsschema im angeforderten Schreibvorgang wird nicht unterstützt.",
    "0x80F00000":
      "Die Variable hat weder einen Standardwert noch einen Anfangswert.",
    "0x804F0000": "Die ServerUri ist keine gültige URI.",
    "0x80500000": "Es wurde kein ServerName angegeben.",
    "0x80510000": "Es wurde keine DiscoveryUrl angegeben.",
    "0x80520000": "Die vom Client angegebene Semaphordatei ist ungültig.",
    "0x80530000": "Der Typ der Sicherheitstoken-Anforderung ist ungültig.",
    "0x80540000":
      "Der Sicherheitsmodus erfüllt die vom Server festgelegten Anforderungen nicht.",
    "0x80550000":
      "Die Sicherheitsrichtlinie erfüllt die vom Server festgelegten Anforderungen nicht.",
    "0x80560000": "Der Server hat die maximale Anzahl an Sitzungen erreicht.",
    "0x80570000": "Die Signatur des Benutzertokens fehlt oder ist ungültig.",
    "0x80580000":
      "Die mit dem Clientzertifikat erzeugte Signatur fehlt oder ist ungültig.",
    "0x80590000":
      "Der Client hat nicht mindestens ein Softwarezertifikat bereitgestellt, das gültig ist und die Profilanforderungen des Servers erfüllt.",
    "0x80C60000":
      "Der Server unterstützt das Ändern der der Sitzung zugewiesenen Benutzeridentität nicht.",
    "0x805A0000":
      "Die Anforderung wurde vom Client über den Cancel-Dienst abgebrochen.",
    "0x805B0000":
      "Die ID des übergeordneten Knotens verweist nicht auf einen gültigen Knoten.",
    "0x805C0000":
      "Die Referenz konnte nicht erstellt werden, da sie gegen vom Datenmodell auferlegte Einschränkungen verstößt.",
    "0x805D0000":
      "Die angeforderte Node-ID wurde abgelehnt, da sie entweder ungültig war oder der Server nicht zulässt, dass Node-IDs vom Client angegeben werden.",
    "0x805E0000":
      "Die angeforderte Node-ID wird bereits von einem anderen Knoten verwendet.",
    "0x805F0000": "Die Knotenklasse ist ungültig.",
    "0x80600000": "Der Browse-Name ist ungültig.",
    "0x80610000":
      "Der Browse-Name ist unter Knoten mit derselben Beziehung zum übergeordneten Knoten nicht eindeutig.",
    "0x80620000": "Die Knotenattribute sind für die Knotenklasse ungültig.",
    "0x80630000":
      "Die Node-ID der Typdefinition verweist nicht auf einen geeigneten Typknoten.",
    "0x80640000": "Die Quell-Node-ID verweist nicht auf einen gültigen Knoten.",
    "0x80650000": "Die Ziel-Node-ID verweist nicht auf einen gültigen Knoten.",
    "0x80660000": "Der Referenztyp zwischen den Knoten ist bereits definiert.",
    "0x80670000":
      "Der Server erlaubt diese Art von Selbstreferenz für diesen Knoten nicht.",
    "0x80680000":
      "Der Referenztyp ist für eine Referenz auf einen entfernten Server ungültig.",
    "0x80690000": "Der Server erlaubt das Löschen des Knotens nicht.",
    "0x40BC0000": "Der Server konnte nicht alle Zielreferenzen löschen.",
    "0x806A0000": "Der Serverindex ist ungültig.",
    "0x806B0000":
      "Die Ansichts-ID verweist nicht auf einen gültigen Ansichtsknoten.",
    "0x80C90000":
      "Der Ansichtszeitstempel ist nicht verfügbar oder wird nicht unterstützt.",
    "0x80CA0000": "Die Ansichtsparameter sind untereinander nicht konsistent.",
    "0x80CB0000":
      "Die Ansichtsversion ist nicht verfügbar oder wird nicht unterstützt.",
    "0x40C00000":
      "Die Liste der Referenzen ist möglicherweise unvollständig, da das zugrunde liegende System nicht verfügbar ist.",
    "0x00BA0000":
      "Der Server hätte einer Referenz zu einem Knoten auf einem entfernten Server folgen sollen, tat dies jedoch nicht. Die Ergebnismenge ist möglicherweise unvollständig.",
    "0x80C80000": "Die bereitgestellte Nodeid war kein Typdefinitions-Nodeid.",
    "0x406C0000":
      "Eine der im relativen Pfad zu verfolgenden Referenzen verweist auf einen Knoten im Adressraum eines anderen Servers.",
    "0x806D0000":
      "Der angeforderte Vorgang hat zu viele Treffer, um sie zurückzugeben.",
    "0x806E0000":
      "Der angeforderte Vorgang benötigt zu viele Ressourcen auf dem Server.",
    "0x806F0000":
      "Der angeforderte Vorgang hat keinen zurückzugebenden Treffer.",
    "0x80700000": "Der Parameter für das maximale Alter ist ungültig.",
    "0x80E60000":
      "Der Vorgang ist über den aktuellen sicheren Kanal nicht zulässig.",
    "0x80710000": "Der Parameter für Verlaufsdetails ist ungültig.",
    "0x80720000": "Der Server unterstützt den angeforderten Vorgang nicht.",
    "0x80BD0000": "Der definierte zurückzugebende Zeitstempel war ungültig.",
    "0x80730000":
      "Der Server unterstützt das Schreiben der bereitgestellten Kombination aus Wert, Status und Zeitstempeln nicht.",
    "0x80740000":
      "Der für das Attribut bereitgestellte Wert hat nicht denselben Typ wie der Wert des Attributs.",
    "0x80750000":
      "Die Methoden-ID verweist nicht auf eine Methode für das angegebene Objekt.",
    "0x80760000":
      "Der Client hat nicht alle Eingabeargumente für die Methode angegeben.",
    "0x81110000":
      "Das ausführbare Attribut erlaubt die Ausführung der Methode nicht.",
    "0x80770000": "Der Server hat die maximale Anzahl an Abonnements erreicht.",
    "0x80780000":
      "Der Server hat die maximale Anzahl an eingereihten Publish-Anforderungen erreicht.",
    "0x80790000": "Für diese Sitzung ist kein Abonnement verfügbar.",
    "0x807A0000": "Die Sequenznummer ist dem Server unbekannt.",
    "0x00DF0000":
      "Der Server unterstützt keine Warteschlange für erneute Übertragungen und die Bestätigung von Sequenznummern ist nicht verfügbar.",
    "0x807B0000":
      "Die angeforderte Benachrichtigungsnachricht ist nicht mehr verfügbar.",
    "0x807C0000":
      "Der Client der aktuellen Sitzung unterstützt ein oder mehrere für das Abonnement erforderliche Profile nicht.",
    "0x80BF0000": "Die Unterzustandsmaschine ist derzeit nicht aktiv.",
    "0x81150000": "Eine gleichwertige Regel existiert bereits.",
    "0x807D0000":
      "Der Server kann die Anforderung nicht verarbeiten, da er zu ausgelastet ist.",
    "0x807E0000": "Der im Header angegebene Nachrichtentyp ist ungültig.",
    "0x807F0000":
      "SecureChannelId und/oder TokenId werden derzeit nicht verwendet.",
    "0x80800000":
      "Die Größe des im Header angegebenen Nachrichtenblocks ist zu groß.",
    "0x80810000":
      "Es sind nicht genügend Ressourcen vorhanden, um die Anforderung zu verarbeiten.",
    "0x80820000": "Ein interner Fehler ist aufgetreten.",
    "0x80830000": "Der Server erkennt die angegebene QueryString nicht.",
    "0x80840000":
      "Die Anforderung konnte aufgrund einer Netzwerkunterbrechung nicht gesendet werden.",
    "0x80850000":
      "Bei der Verarbeitung der Anforderung ist eine Zeitüberschreitung aufgetreten.",
    "0x80860000": "Der sichere Kanal wurde geschlossen.",
    "0x80870000": "Das Token ist abgelaufen oder wird nicht erkannt.",
    "0x80880000": "Die Sequenznummer ist ungültig.",
    "0x80BE0000": "Die Anwendungen haben keine kompatiblen Protokollversionen.",
    "0x80890000":
      "Es gibt ein Konfigurationsproblem, das die Verwendbarkeit des Werts beeinträchtigt.",
    "0x808A0000":
      "Die Variable sollte ihren Wert von einer anderen Variable erhalten, wurde dafür jedoch nie konfiguriert.",
    "0x808B0000":
      "Im Gerät bzw. in der Datenquelle, die den Wert erzeugt, ist ein Fehler aufgetreten, der den Wert beeinträchtigt hat.",
    "0x808C0000":
      "Im Sensor, aus dem der Wert vom Gerät bzw. von der Datenquelle abgeleitet wird, ist ein Fehler aufgetreten.",
    "0x808D0000": "Die Datenquelle ist nicht betriebsbereit.",
    "0x808E0000": "Der Totbandfilter ist ungültig.",
    "0x408F0000":
      "Die Kommunikation mit der Datenquelle ist fehlgeschlagen. Der Variablenwert ist der letzte Wert mit guter Qualität.",
    "0x40900000": "Die Aktualisierung dieses Werts wurde beendet.",
    "0x40910000":
      "Der Wert ist ein Betriebswert, der manuell überschrieben wurde.",
    "0x40920000":
      "Der Wert ist ein Anfangswert für eine Variable, die ihren Wert normalerweise von einer anderen Variable erhält.",
    "0x40930000": "Der Wert liegt an einem der Sensorgrenzwerte.",
    "0x40940000":
      "Der Wert liegt außerhalb des für diesen Parameter definierten Wertebereichs.",
    "0x40950000":
      "Der Datenwert wird aus mehreren Quellen abgeleitet und hat weniger als die erforderliche Anzahl an Good-Quellen.",
    "0x00960000": "Der Wert wurde überschrieben.",
    "0x00EB0000":
      "Der Wert wird aus mehreren Quellen abgeleitet und hat die erforderliche Anzahl an Good-Quellen, jedoch weniger als die vollständige Anzahl an Good-Quellen.",
    "0x80970000":
      "Diese Aktualisierung der Condition ist fehlgeschlagen; eine Condition-Aktualisierung läuft bereits.",
    "0x80980000": "Diese Condition wurde bereits deaktiviert.",
    "0x80CC0000": "Diese Condition wurde bereits aktiviert.",
    "0x80990000":
      "Eigenschaft nicht verfügbar; diese Condition ist deaktiviert.",
    "0x809A0000": "Die angegebene Ereignis-ID wird nicht erkannt.",
    "0x80BB0000": "Das Ereignis kann nicht bestätigt werden.",
    "0x80CD0000": "Die Dialog-Condition ist nicht aktiv.",
    "0x80CE0000": "Die Antwort ist für den Dialog ungültig.",
    "0x80CF0000": "Der Condition-Zweig wurde bereits bestätigt.",
    "0x80D00000": "Der Condition-Zweig wurde bereits endgültig bestätigt.",
    "0x80D10000": "Die Condition wurde bereits zurückgestellt.",
    "0x80D20000": "Die Condition ist derzeit nicht zurückgestellt.",
    "0x80D30000":
      "Die Rückstellzeit liegt nicht innerhalb eines akzeptablen Bereichs.",
    "0x809B0000":
      "Für den angeforderten Zeitraum oder Ereignisfilter sind keine Daten vorhanden.",
    "0x80D70000":
      "Es wurden keine Daten gefunden, um einen oberen oder unteren Grenzwert bereitzustellen.",
    "0x80D80000": "Der Server kann keinen Grenzwert für die Variable abrufen.",
    "0x809D0000":
      "Daten fehlen, weil die Erfassung gestartet, gestoppt oder verloren wurde.",
    "0x809E0000":
      "Die erwarteten Daten sind für den angeforderten Zeitraum aufgrund eines nicht eingehängten Volumes, eines Offline-Archivs oder Bands oder aus einem ähnlichen Grund vorübergehend nicht verfügbar.",
    "0x809F0000":
      "Die Daten oder das Ereignis wurden nicht erfolgreich eingefügt, da ein passender Eintrag vorhanden ist.",
    "0x80A00000":
      "Die Daten oder das Ereignis wurden nicht erfolgreich aktualisiert, da kein passender Eintrag vorhanden ist.",
    "0x80A10000":
      "Der Client hat Verlaufsdaten mit einem vom Server nicht unterstützten TimestampsToReturn angefordert.",
    "0x00A20000":
      "Die Daten oder das Ereignis wurden erfolgreich in die Verlaufsdatenbank eingefügt.",
    "0x00A30000":
      "Das Daten- oder Ereignisfeld wurde erfolgreich in der Verlaufsdatenbank ersetzt.",
    "0x40A40000":
      "Der Aggregatwert wird aus mehreren Werten abgeleitet und hat weniger als die erforderliche Anzahl an Good-Werten.",
    "0x00A50000":
      "Für den angeforderten Zeitraum oder Ereignisfilter sind keine Daten vorhanden.",
    "0x00A60000":
      "Im Zeitraum sind über die angeforderte Anzahl von Werten hinaus weitere Daten verfügbar.",
    "0x80D40000":
      "Die angeforderte Anzahl an Aggregates stimmt nicht mit der angeforderten Anzahl an NodeIds überein.",
    "0x80D50000":
      "Das angeforderte Aggregate wird vom Server nicht unterstützt.",
    "0x80D60000":
      "Der Aggregatwert konnte aufgrund ungültiger Dateneingaben nicht ermittelt werden.",
    "0x80DA0000":
      "Die Aggregatkonfiguration ist für den angegebenen Knoten ungültig.",
    "0x00D90000":
      "Die Anforderung gibt Felder an, die für den EventType ungültig sind oder vom Historian nicht gespeichert werden können.",
    "0x80E40000":
      "Die Anforderung wurde vom Server abgelehnt, da sie die vom Server festgelegten Kriterien nicht erfüllte.",
    "0x81130000": "Die Anforderung wurde vom Server noch nicht verarbeitet.",
    "0x80E80000": "Der Vorgang ist nicht zulässig, da eine Transaktion läuft.",
    "0x80F10000":
      "Der Vorgang ist fehlgeschlagen und alle Änderungen, die Teil der Transaktion waren, werden zurückgerollt.",
    "0x811F0000":
      "Die Geräteidentität benötigt ein Ticket, bevor sie akzeptiert werden kann.",
    "0x81200000":
      "Die Geräteidentität benötigt ein Ticket, bevor sie akzeptiert werden kann.",
    "0x80E90000":
      "Der angeforderte Vorgang ist nicht zulässig, da der Node von einer anderen Anwendung gesperrt ist.",
    "0x80EC0000":
      "Der angeforderte Vorgang ist nicht zulässig, da der Node von der Anwendung nicht gesperrt ist.",
    "0x00DC0000":
      "Der Wert stammt nicht aus der realen Quelle und wurde vom Server bearbeitet.",
    "0x00DD0000":
      "Bei der Ausführung dieser Nachaktionen ist ein Fehler aufgetreten.",
    "0x40DE0000":
      "Die zugehörige EngineeringUnit wurde geändert, der Variable-Wert wird jedoch weiterhin auf Basis der vorherigen Einheit bereitgestellt.",
    "0x00E00000":
      "Ein abhängiger Wert wurde geändert, die Änderung wurde jedoch nicht auf das Gerät angewendet.",
    "0x80E10000":
      "Die zugehörige EngineeringUnit wurde geändert, diese Änderung wurde jedoch nicht auf das Gerät angewendet. Der Variable-Wert hängt weiterhin von der vorherigen Einheit ab, sein Status ist derzeit jedoch Bad.",
    "0x40E20000":
      "Ein abhängiger Wert wurde geändert, die Änderung wurde jedoch nicht auf das Gerät angewendet. Die Qualität der dominanten Variable ist unsicher.",
    "0x80E30000":
      "Ein abhängiger Wert wurde geändert, die Änderung wurde jedoch nicht auf das Gerät angewendet. Die Qualität der dominanten Variable ist Bad.",
    "0x01160000":
      "Er wird zusammen mit einem dominanten Variable-Wert geliefert, wenn eine abhängige Variable geändert wurde, die Änderung jedoch nicht angewendet wurde.",
    "0x01170000":
      "Er wird zusammen mit einem abhängigen Variable-Wert geliefert, wenn eine dominante Variable geändert wurde, die Änderung jedoch nicht angewendet wurde.",
    "0x01180000":
      "Er wird zusammen mit einem abhängigen Variable-Wert geliefert, wenn eine dominante oder abhängige Variable geändert wurde, die Änderung jedoch nicht angewendet wurde.",
    "0x81190000":
      "Er wird zusammen mit einem Variable-Wert geliefert, wenn die Variable geändert wurde, der Wert jedoch unzulässig ist.",
    "0x811A0000":
      "Er wird zusammen mit einem Variable-Wert geliefert, wenn eine Quellvariable geändert wurde, der Wert jedoch unzulässig ist.",
    "0x811B0000":
      "Er wird zusammen mit einem abhängigen Variable-Wert geliefert, wenn eine dominante Variable geändert wurde und der Wert unzulässig ist.",
    "0x811C0000":
      "Er wird zusammen mit einem abhängigen Variable-Wert geliefert, wenn eine dominante Variable geändert wurde, der Wert unzulässig ist und die Änderung nicht angewendet wurde.",
    "0x811D0000":
      "Er wird zusammen mit einem abhängigen Variable-Wert geliefert, wenn eine dominante oder abhängige Variable geändert wurde und der Wert unzulässig ist.",
    "0x811E0000":
      "Er wird zusammen mit einem abhängigen Variable-Wert geliefert, wenn eine dominante oder abhängige Variable geändert wurde, der Wert unzulässig ist und die Änderung nicht angewendet wurde.",
    "0x00A70000": "Die Kommunikationsschicht hat ein Ereignis ausgelöst.",
    "0x00A80000": "Das System wird heruntergefahren.",
    "0x00A90000":
      "Der Vorgang ist nicht abgeschlossen und muss erneut aufgerufen werden.",
    "0x00AA0000": "Eine nicht kritische Zeitüberschreitung ist aufgetreten.",
    "0x80AB0000": "Ein oder mehrere Argumente sind ungültig.",
    "0x80AC0000":
      "Es konnte keine Netzwerkverbindung zum entfernten Server hergestellt werden.",
    "0x80AD0000": "Der Server hat die Verbindung zum Client getrennt.",
    "0x80AE0000": "Die Netzwerkverbindung wurde geschlossen.",
    "0x80AF0000":
      "Der Vorgang kann nicht abgeschlossen werden, da das Objekt geschlossen, nicht initialisiert oder in einem anderen ungültigen Zustand ist.",
    "0x80B00000":
      "Über das Ende des Datenstroms hinaus kann nicht navigiert werden.",
    "0x80B10000":
      "Zum Lesen aus einem nicht blockierenden Datenstrom sind derzeit keine Daten verfügbar.",
    "0x80B20000": "Der asynchrone Vorgang wartet auf eine Antwort.",
    "0x80B30000": "Der asynchrone Vorgang wurde vom Aufrufer abgebrochen.",
    "0x80B40000":
      "Der Datenstrom hat nicht alle angeforderten Daten zurückgegeben (möglicherweise weil es sich um einen nicht blockierenden Datenstrom handelt).",
    "0x80B50000":
      "Nicht blockierendes Verhalten ist erforderlich und der Vorgang würde blockieren.",
    "0x80B60000": "Ein Wert hatte eine ungültige Syntax.",
    "0x80B70000":
      "Der Vorgang konnte nicht abgeschlossen werden, da alle verfügbaren Verbindungen verwendet werden.",
    "0x42080000":
      "Der Wert ist möglicherweise ungenau, da sich der Messumformer im manuellen Modus befindet.",
    "0x42090000": "Der Wert ist simuliert.",
    "0x420A0000":
      "Der Wert ist möglicherweise aufgrund eines Sensor-Kalibrierungsfehlers ungenau.",
    "0x420F0000":
      "Der Wert ist möglicherweise aufgrund eines Konfigurationsproblems ungenau.",
    "0x04010000":
      "Die Wertquelle unterstützt Cascade-Handshaking und der Wert wurde auf Grundlage einer Initialisierungsanforderung von einer sekundären Cascade initialisiert.",
    "0x04020000":
      "Die Wertquelle unterstützt Cascade-Handshaking und fordert die Initialisierung einer primären Cascade an.",
    "0x04030000":
      "Die Wertquelle unterstützt Cascade-Handshaking, der aktuelle Zustand der Quelle erlaubt jedoch keine Cascade.",
    "0x04040000":
      "Die Wertquelle unterstützt Cascade-Handshaking, die Quelle hat jedoch die entsprechende primäre Cascade noch nicht zur Verwendung ausgewählt.",
    "0x04070000": "In der Wertquelle ist eine Fehlerzustandsbedingung aktiv.",
    "0x04080000": "Vom Ziel wird eine Fehlerzustandsbedingung angefordert.",
    "0x04090000":
      "Der Wert ist genau und die Signalquelle unterstützt Cascade-Handshaking.",
    "0x80E70000":
      "Das für die Erstellung des DataSetWriter angegebene DataSet ist ungültig.",
  },
  es: {
    "0x00000000": "La operación se completó correctamente.",
    "0x40000000": "La operación tuvo un resultado incierto.",
    "0x80000000": "La operación falló.",
    "0x80010000": "Se produjo un error inesperado.",
    "0x80020000":
      "Se produjo un error interno como resultado de un error de programación o configuración.",
    "0x80030000": "No hay suficiente memoria para completar la operación.",
    "0x80040000": "Un recurso del sistema operativo no está disponible.",
    "0x80050000": "Se produjo un error de comunicación de bajo nivel.",
    "0x80060000":
      "La codificación se detuvo debido a datos no válidos en los objetos que se estaban serializando.",
    "0x80070000":
      "La decodificación se detuvo debido a datos no válidos en el flujo.",
    "0x80080000":
      "Se han superado los límites de codificación/decodificación de mensajes impuestos por la pila.",
    "0x80B80000":
      "El tamaño del mensaje de solicitud supera los límites establecidos por el servidor.",
    "0x80B90000":
      "El tamaño del mensaje de respuesta supera los límites establecidos por el cliente o el servidor.",
    "0x80090000": "Se recibió del servidor una respuesta no reconocida.",
    "0x800A0000": "La operación agotó el tiempo de espera.",
    "0x800B0000": "El servidor no admite el servicio solicitado.",
    "0x800C0000":
      "La operación se canceló porque la aplicación se está cerrando.",
    "0x800D0000":
      "La operación no pudo completarse porque el cliente no está conectado al servidor.",
    "0x800E0000":
      "El servidor se ha detenido y no puede procesar ninguna solicitud.",
    "0x800F0000":
      "No se pudo realizar ningún procesamiento porque no había nada que hacer.",
    "0x80100000":
      "La solicitud no pudo procesarse porque especificaba demasiadas operaciones.",
    "0x80DB0000":
      "La solicitud no pudo procesarse porque hay demasiados elementos supervisados en la suscripción.",
    "0x80110000":
      "El objeto de extensión no puede (des)serializarse porque no se reconoce el ID del tipo de datos.",
    "0x80120000": "El certificado proporcionado como parámetro no es válido.",
    "0x80130000": "Se produjo un error al verificar la seguridad.",
    "0x81140000":
      "El certificado no cumple los requisitos de la política de seguridad.",
    "0x80140000": "El certificado ha caducado o todavía no es válido.",
    "0x80150000":
      "Un certificado del emisor ha caducado o todavía no es válido.",
    "0x80160000":
      "El HostName utilizado para conectarse a un servidor no coincide con ningún HostName del certificado.",
    "0x80170000":
      "El URI especificado en ApplicationDescription no coincide con el URI del certificado.",
    "0x80180000":
      "El certificado no puede utilizarse para la operación solicitada.",
    "0x80190000":
      "El certificado del emisor no puede utilizarse para la operación solicitada.",
    "0x801A0000": "El certificado no es de confianza.",
    "0x801B0000":
      "No fue posible determinar si el certificado ha sido revocado.",
    "0x801C0000":
      "No fue posible determinar si el certificado del emisor ha sido revocado.",
    "0x801D0000": "El certificado ha sido revocado.",
    "0x801E0000": "El certificado del emisor ha sido revocado.",
    "0x810D0000": "La cadena de certificados está incompleta.",
    "0x801F0000":
      "El usuario no tiene permiso para realizar la operación solicitada.",
    "0x80200000": "El token de identidad del usuario no es válido.",
    "0x80210000":
      "El token de identidad del usuario es válido, pero el servidor lo ha rechazado.",
    "0x80220000": "El canal seguro especificado ya no es válido.",
    "0x80230000":
      "La marca de tiempo está fuera del intervalo permitido por el servidor.",
    "0x80240000":
      "El nonce parece no ser un valor aleatorio o no tiene la longitud correcta.",
    "0x80250000": "El ID de sesión no es válido.",
    "0x80260000": "La sesión fue cerrada por el cliente.",
    "0x80270000":
      "La sesión no puede utilizarse porque no se ha llamado a ActivateSession.",
    "0x80280000": "El ID de suscripción no es válido.",
    "0x802A0000": "La cabecera de la solicitud falta o no es válida.",
    "0x802B0000": "El parámetro de marcas de tiempo a devolver no es válido.",
    "0x802C0000": "La solicitud fue cancelada por el cliente.",
    "0x80E50000": "Se proporcionaron demasiados argumentos.",
    "0x810E0000":
      "El servidor requiere una licencia para funcionar en general o para ejecutar un servicio u operación, pero la licencia existente ha caducado.",
    "0x810F0000":
      "El servidor tiene límites sobre el número de operaciones/objetos permitidos, basados en las licencias instaladas, y dichos límites se han superado.",
    "0x81100000":
      "El servidor no dispone de una licencia necesaria para funcionar en general o para ejecutar un servicio u operación.",
    "0x80EE0000":
      "El servidor no dispone actualmente de los recursos necesarios para procesar la solicitud.",
    "0x00EF0000":
      "El inicio de sesión del usuario se realizó correctamente, pero el usuario debe cambiar la contraseña.",
    "0x002D0000": "La suscripción se transfirió a otra sesión.",
    "0x002E0000": "El procesamiento se completará de forma asíncrona.",
    "0x002F0000":
      "El muestreo se ha ralentizado debido a limitaciones de recursos.",
    "0x00300000":
      "El valor escrito fue aceptado, pero se limitó al rango permitido.",
    "0x80310000":
      "La comunicación con la fuente de datos está definida, pero no establecida, y no hay disponible ningún último valor conocido.",
    "0x80320000":
      "Esperando a que el servidor obtenga valores de la fuente de datos subyacente.",
    "0x80330000":
      "La sintaxis del ID de nodo no es válida o hace referencia a un nodo que no es válido para la operación.",
    "0x80340000":
      "El ID de nodo hace referencia a un nodo que no existe en el espacio de direcciones del servidor.",
    "0x80350000": "El atributo no es compatible con el Node especificado.",
    "0x80360000": "La sintaxis del parámetro de rango de índices no es válida.",
    "0x80370000": "No existen datos dentro del rango de índices especificado.",
    "0x80EA0000":
      "Los datos escritos no coinciden con el IndexRange especificado.",
    "0x80380000": "La codificación de datos no es válida.",
    "0x80390000":
      "El servidor no admite la codificación de datos solicitada para el nodo.",
    "0x803A0000": "El nivel de acceso no permite leer ni suscribirse al Node.",
    "0x803B0000": "El nivel de acceso no permite escribir en el Node.",
    "0x803C0000": "El valor estaba fuera de rango.",
    "0x40F20000": "El valor está por encima del rango permitido.",
    "0x40F30000": "El valor está por debajo del rango permitido.",
    "0x803D0000": "La operación solicitada no es compatible.",
    "0x803E0000":
      "No se encontró un elemento solicitado o una operación de búsqueda terminó sin éxito.",
    "0x803F0000": "El objeto no puede utilizarse porque ha sido eliminado.",
    "0x80400000": "La operación solicitada no está implementada.",
    "0x80410000": "El modo de supervisión no es válido.",
    "0x80420000":
      "El ID del elemento supervisado no hace referencia a un elemento supervisado válido.",
    "0x80430000":
      "El parámetro de filtro del elemento supervisado no es válido.",
    "0x80440000":
      "El servidor no admite el filtro solicitado para el elemento supervisado.",
    "0x80450000":
      "No se puede utilizar un filtro de supervisión en combinación con el atributo especificado.",
    "0x80460000": "Faltaba un parámetro estructurado obligatorio o era null.",
    "0x80470000": "El filtro de eventos no es válido.",
    "0x80480000": "El filtro de contenido no es válido.",
    "0x80C10000": "Se proporcionó un operador no reconocido en un filtro.",
    "0x80C20000":
      "Se proporcionó un operador válido, pero el servidor no admite este operador de filtro.",
    "0x80C30000":
      "El número de operandos proporcionados para el operador de filtro era menor de lo esperado para el operando proporcionado.",
    "0x80490000":
      "El operando utilizado en un filtro de contenido no es válido.",
    "0x80C40000":
      "El elemento referenciado no es un elemento válido en el filtro de contenido.",
    "0x80C50000": "El literal referenciado no es un valor válido.",
    "0x804A0000": "El punto de continuación proporcionado ya no es válido.",
    "0x804B0000":
      "La operación no pudo procesarse porque se han asignado todos los puntos de continuación.",
    "0x804C0000":
      "El ID del tipo de referencia no hace referencia a un nodo de tipo de referencia válido.",
    "0x804D0000": "La dirección de exploración no es válida.",
    "0x804E0000": "El nodo no forma parte de la vista.",
    "0x81120000":
      "El número no fue aceptado debido a un desbordamiento numérico.",
    "0x80ED0000":
      "La configuración regional de la operación de escritura solicitada no es compatible.",
    "0x80F00000": "La variable no tiene valor predeterminado ni valor inicial.",
    "0x804F0000": "ServerUri no es un URI válido.",
    "0x80500000": "No se especificó ningún ServerName.",
    "0x80510000": "No se especificó ningún DiscoveryUrl.",
    "0x80520000":
      "El archivo de semáforo especificado por el cliente no es válido.",
    "0x80530000": "El tipo de solicitud de token de seguridad no es válido.",
    "0x80540000":
      "El modo de seguridad no cumple los requisitos establecidos por el servidor.",
    "0x80550000":
      "La política de seguridad no cumple los requisitos establecidos por el servidor.",
    "0x80560000": "El servidor ha alcanzado su número máximo de sesiones.",
    "0x80570000": "La firma del token de usuario falta o no es válida.",
    "0x80580000":
      "La firma generada con el certificado del cliente falta o no es válida.",
    "0x80590000":
      "El cliente no proporcionó al menos un certificado de software válido que cumpla los requisitos de perfil del servidor.",
    "0x80C60000":
      "El servidor no admite cambiar la identidad de usuario asignada a la sesión.",
    "0x805A0000":
      "La solicitud fue cancelada por el cliente mediante el servicio Cancel.",
    "0x805B0000": "El ID del nodo padre no hace referencia a un nodo válido.",
    "0x805C0000":
      "No se pudo crear la referencia porque infringe las restricciones impuestas por el modelo de datos.",
    "0x805D0000":
      "El ID de nodo solicitado fue rechazado porque era no válido o porque el servidor no permite que el cliente especifique IDs de nodo.",
    "0x805E0000":
      "El ID de nodo solicitado ya está siendo utilizado por otro nodo.",
    "0x805F0000": "La clase de nodo no es válida.",
    "0x80600000": "El nombre de exploración no es válido.",
    "0x80610000":
      "El nombre de exploración no es único entre los nodos que comparten la misma relación con el nodo padre.",
    "0x80620000":
      "Los atributos del nodo no son válidos para la clase de nodo.",
    "0x80630000":
      "El ID de nodo de definición de tipo no hace referencia a un nodo de tipo apropiado.",
    "0x80640000":
      "El ID del nodo de origen no hace referencia a un nodo válido.",
    "0x80650000":
      "El ID del nodo de destino no hace referencia a un nodo válido.",
    "0x80660000": "El tipo de referencia entre los nodos ya está definido.",
    "0x80670000":
      "El servidor no permite este tipo de autorreferencia en este nodo.",
    "0x80680000":
      "El tipo de referencia no es válido para una referencia a un servidor remoto.",
    "0x80690000": "El servidor no permitirá eliminar el nodo.",
    "0x40BC0000":
      "El servidor no pudo eliminar todas las referencias de destino.",
    "0x806A0000": "El índice del servidor no es válido.",
    "0x806B0000":
      "El ID de vista no hace referencia a un nodo de vista válido.",
    "0x80C90000":
      "La marca de tiempo de la vista no está disponible o no es compatible.",
    "0x80CA0000": "Los parámetros de la vista no son coherentes entre sí.",
    "0x80CB0000":
      "La versión de la vista no está disponible o no es compatible.",
    "0x40C00000":
      "La lista de referencias puede estar incompleta porque el sistema subyacente no está disponible.",
    "0x00BA0000":
      "El servidor debería haber seguido una referencia a un nodo en un servidor remoto, pero no lo hizo. El conjunto de resultados puede estar incompleto.",
    "0x80C80000":
      "El Nodeid proporcionado no era un Nodeid de definición de tipo.",
    "0x406C0000":
      "Una de las referencias que deben seguirse en la ruta relativa hace referencia a un nodo en el espacio de direcciones de otro servidor.",
    "0x806D0000":
      "La operación solicitada tiene demasiadas coincidencias para devolverlas.",
    "0x806E0000":
      "La operación solicitada requiere demasiados recursos en el servidor.",
    "0x806F0000":
      "La operación solicitada no tiene ninguna coincidencia que devolver.",
    "0x80700000": "El parámetro de antigüedad máxima no es válido.",
    "0x80E60000":
      "La operación no está permitida sobre el canal seguro actual.",
    "0x80710000": "El parámetro de detalles históricos no es válido.",
    "0x80720000": "El servidor no admite la operación solicitada.",
    "0x80BD0000": "La marca de tiempo definida para devolver no era válida.",
    "0x80730000":
      "El servidor no admite escribir la combinación proporcionada de valor, estado y marcas de tiempo.",
    "0x80740000":
      "El valor proporcionado para el atributo no es del mismo tipo que el valor del atributo.",
    "0x80750000":
      "El ID del método no hace referencia a un método para el objeto especificado.",
    "0x80760000":
      "El cliente no especificó todos los argumentos de entrada del método.",
    "0x81110000": "El atributo ejecutable no permite la ejecución del método.",
    "0x80770000": "El servidor ha alcanzado su número máximo de suscripciones.",
    "0x80780000":
      "El servidor ha alcanzado el número máximo de solicitudes Publish en cola.",
    "0x80790000": "No hay ninguna suscripción disponible para esta sesión.",
    "0x807A0000": "El número de secuencia es desconocido para el servidor.",
    "0x00DF0000":
      "El servidor no admite la cola de retransmisión y no está disponible la confirmación de números de secuencia.",
    "0x807B0000":
      "El mensaje de notificación solicitado ya no está disponible.",
    "0x807C0000":
      "El cliente de la sesión actual no admite uno o más Profiles necesarios para la suscripción.",
    "0x80BF0000": "La máquina de subestados no está activa actualmente.",
    "0x81150000": "Ya existe una regla equivalente.",
    "0x807D0000":
      "El servidor no puede procesar la solicitud porque está demasiado ocupado.",
    "0x807E0000":
      "El tipo de mensaje especificado en la cabecera no es válido.",
    "0x807F0000": "SecureChannelId y/o TokenId no están actualmente en uso.",
    "0x80800000":
      "El tamaño del fragmento de mensaje especificado en la cabecera es demasiado grande.",
    "0x80810000": "No hay suficientes recursos para procesar la solicitud.",
    "0x80820000": "Se produjo un error interno.",
    "0x80830000": "El servidor no reconoce la QueryString especificada.",
    "0x80840000":
      "La solicitud no pudo enviarse debido a una interrupción de red.",
    "0x80850000":
      "Se produjo un tiempo de espera agotado al procesar la solicitud.",
    "0x80860000": "El canal seguro se ha cerrado.",
    "0x80870000": "El token ha caducado o no se reconoce.",
    "0x80880000": "El número de secuencia no es válido.",
    "0x80BE0000":
      "Las aplicaciones no tienen versiones de protocolo compatibles.",
    "0x80890000":
      "Hay un problema con la configuración que afecta a la utilidad del valor.",
    "0x808A0000":
      "La variable debería recibir su valor de otra variable, pero nunca se configuró para hacerlo.",
    "0x808B0000":
      "Se produjo un fallo en el dispositivo/la fuente de datos que genera el valor y que ha afectado al valor.",
    "0x808C0000":
      "Se produjo un fallo en el sensor del que el dispositivo/la fuente de datos deriva el valor.",
    "0x808D0000": "La fuente de los datos no está operativa.",
    "0x808E0000": "El filtro de banda muerta no es válido.",
    "0x408F0000":
      "La comunicación con la fuente de datos ha fallado. El valor de la variable es el último valor que tenía calidad Good.",
    "0x40900000": "Lo que actualizaba este valor ha dejado de hacerlo.",
    "0x40910000":
      "El valor es un valor operativo que fue sobrescrito manualmente.",
    "0x40920000":
      "El valor es un valor inicial para una variable que normalmente recibe su valor de otra variable.",
    "0x40930000": "El valor se encuentra en uno de los límites del sensor.",
    "0x40940000":
      "El valor está fuera del rango de valores definido para este parámetro.",
    "0x40950000":
      "El valor de datos se deriva de múltiples fuentes y tiene menos fuentes Good de las requeridas.",
    "0x00960000": "El valor ha sido sobrescrito.",
    "0x00EB0000":
      "El valor se deriva de múltiples fuentes y tiene el número requerido de fuentes Good, pero menos que el número total de fuentes Good.",
    "0x80970000":
      "Esta actualización de la Condition falló; ya hay una operación de actualización de Condition en curso.",
    "0x80980000": "Esta Condition ya ha sido deshabilitada.",
    "0x80CC0000": "Esta Condition ya ha sido habilitada.",
    "0x80990000": "Propiedad no disponible; esta Condition está deshabilitada.",
    "0x809A0000": "El ID de evento especificado no se reconoce.",
    "0x80BB0000": "El evento no puede ser reconocido.",
    "0x80CD0000": "La Condition del diálogo no está activa.",
    "0x80CE0000": "La respuesta no es válida para el diálogo.",
    "0x80CF0000": "La rama de la Condition ya ha sido reconocida.",
    "0x80D00000": "La rama de la Condition ya ha sido confirmada.",
    "0x80D10000": "La Condition ya ha sido aplazada.",
    "0x80D20000": "La Condition no está actualmente aplazada.",
    "0x80D30000":
      "El tiempo de aplazamiento no está dentro de un rango aceptable.",
    "0x809B0000":
      "No existen datos para el rango de tiempo o filtro de eventos solicitado.",
    "0x80D70000":
      "No se encontraron datos para proporcionar un valor de límite superior o inferior.",
    "0x80D80000": "El servidor no puede recuperar un límite para la variable.",
    "0x809D0000":
      "Faltan datos debido a que la recopilación se inició, se detuvo o se perdió.",
    "0x809E0000":
      "Los datos esperados no están disponibles para el rango de tiempo solicitado debido a un volumen no montado, un archivo o cinta fuera de línea, o una razón similar de indisponibilidad temporal.",
    "0x809F0000":
      "Los datos o el evento no se insertaron correctamente porque existe una entrada coincidente.",
    "0x80A00000":
      "Los datos o el evento no se actualizaron correctamente porque no existe una entrada coincidente.",
    "0x80A10000":
      "El cliente solicitó historial utilizando un TimestampsToReturn que el servidor no admite.",
    "0x00A20000":
      "Los datos o el evento se insertaron correctamente en la base de datos histórica.",
    "0x00A30000":
      "El campo de datos o evento se reemplazó correctamente en la base de datos histórica.",
    "0x40A40000":
      "El valor agregado se deriva de varios valores y tiene menos valores Good de los requeridos.",
    "0x00A50000":
      "No existen datos para el rango de tiempo o filtro de eventos solicitado.",
    "0x00A60000":
      "Hay más datos disponibles en el rango de tiempo más allá del número de valores solicitados.",
    "0x80D40000":
      "El número solicitado de Aggregates no coincide con el número solicitado de NodeIds.",
    "0x80D50000": "El Aggregate solicitado no es compatible con el servidor.",
    "0x80D60000":
      "No se pudo derivar el valor agregado debido a entradas de datos no válidas.",
    "0x80DA0000":
      "La configuración del agregado no es válida para el nodo especificado.",
    "0x00D90000":
      "La solicitud especifica campos que no son válidos para el EventType o que no pueden ser guardados por el historian.",
    "0x80E40000":
      "La solicitud fue rechazada por el servidor porque no cumplía los criterios establecidos por el servidor.",
    "0x81130000": "La solicitud todavía no ha sido procesada por el servidor.",
    "0x80E80000":
      "La operación no está permitida porque hay una transacción en curso.",
    "0x80F10000":
      "La operación falló y todos los cambios que formaban parte de la transacción se revierten.",
    "0x811F0000":
      "La identidad del dispositivo necesita un ticket antes de poder ser aceptada.",
    "0x81200000":
      "La identidad del dispositivo necesita un ticket antes de poder ser aceptada.",
    "0x80E90000":
      "La operación solicitada no está permitida porque el Node está bloqueado por otra aplicación.",
    "0x80EC0000":
      "La operación solicitada no está permitida porque el Node no está bloqueado por la aplicación.",
    "0x00DC0000":
      "El valor no procede de la fuente real y ha sido editado por el servidor.",
    "0x00DD0000":
      "Se produjo un error durante la ejecución de estas acciones posteriores.",
    "0x40DE0000":
      "La EngineeringUnit relacionada ha cambiado, pero el valor de la Variable se sigue proporcionando basándose en la unidad anterior.",
    "0x00E00000":
      "Se ha cambiado un valor dependiente, pero el cambio no se ha aplicado al dispositivo.",
    "0x80E10000":
      "La EngineeringUnit relacionada ha cambiado, pero este cambio no se ha aplicado al dispositivo. El valor de la Variable sigue dependiendo de la unidad anterior, pero su estado actual es Bad.",
    "0x40E20000":
      "Se ha cambiado un valor dependiente, pero el cambio no se ha aplicado al dispositivo. La calidad de la variable dominante es incierta.",
    "0x80E30000":
      "Se ha cambiado un valor dependiente, pero el cambio no se ha aplicado al dispositivo. La calidad de la variable dominante es Bad.",
    "0x01160000":
      "Se entrega con un valor de Variable dominante cuando una Variable dependiente ha cambiado, pero el cambio no se ha aplicado.",
    "0x01170000":
      "Se entrega con un valor de Variable dependiente cuando una Variable dominante ha cambiado, pero el cambio no se ha aplicado.",
    "0x01180000":
      "Se entrega con un valor de Variable dependiente cuando una Variable dominante o dependiente ha cambiado, pero el cambio no se ha aplicado.",
    "0x81190000":
      "Se entrega con un valor de Variable cuando la Variable ha cambiado, pero el valor no es válido.",
    "0x811A0000":
      "Se entrega con un valor de Variable cuando una Variable de origen ha cambiado, pero el valor no es válido.",
    "0x811B0000":
      "Se entrega con un valor de Variable dependiente cuando una Variable dominante ha cambiado y el valor no es válido.",
    "0x811C0000":
      "Se entrega con un valor de Variable dependiente cuando una Variable dominante ha cambiado, el valor no es válido y el cambio no se ha aplicado.",
    "0x811D0000":
      "Se entrega con un valor de Variable dependiente cuando una Variable dominante o dependiente ha cambiado y el valor no es válido.",
    "0x811E0000":
      "Se entrega con un valor de Variable dependiente cuando una Variable dominante o dependiente ha cambiado, el valor no es válido y el cambio no se ha aplicado.",
    "0x00A70000": "La capa de comunicación ha generado un evento.",
    "0x00A80000": "El sistema se está cerrando.",
    "0x00A90000": "La operación no ha finalizado y debe llamarse de nuevo.",
    "0x00AA0000": "Se produjo un tiempo de espera no crítico.",
    "0x80AB0000": "Uno o más argumentos no son válidos.",
    "0x80AC0000":
      "No se pudo establecer una conexión de red con el servidor remoto.",
    "0x80AD0000": "El servidor se ha desconectado del cliente.",
    "0x80AE0000": "La conexión de red se ha cerrado.",
    "0x80AF0000":
      "La operación no puede completarse porque el objeto está cerrado, no inicializado o en algún otro estado no válido.",
    "0x80B00000": "No se puede avanzar más allá del final del flujo.",
    "0x80B10000":
      "Actualmente no hay datos disponibles para leer de un flujo no bloqueante.",
    "0x80B20000": "La operación asíncrona está esperando una respuesta.",
    "0x80B30000": "La operación asíncrona fue abandonada por el llamador.",
    "0x80B40000":
      "El flujo no devolvió todos los datos solicitados, posiblemente porque es un flujo no bloqueante.",
    "0x80B50000":
      "Se requiere un comportamiento no bloqueante y la operación se bloquearía.",
    "0x80B60000": "Un valor tenía una sintaxis no válida.",
    "0x80B70000":
      "La operación no pudo finalizar porque todas las conexiones disponibles están en uso.",
    "0x42080000":
      "El valor puede no ser preciso porque el transductor está en modo manual.",
    "0x42090000": "El valor es simulado.",
    "0x420A0000":
      "El valor puede no ser preciso debido a un fallo de calibración del sensor.",
    "0x420F0000":
      "El valor puede no ser preciso debido a un problema de configuración.",
    "0x04010000":
      "La fuente del valor admite el protocolo de enlace en cascada y el valor se ha inicializado basándose en una solicitud de inicialización de un secundario de la cascada.",
    "0x04020000":
      "La fuente del valor admite el protocolo de enlace en cascada y está solicitando la inicialización de un primario de la cascada.",
    "0x04030000":
      "La fuente del valor admite el protocolo de enlace en cascada; sin embargo, el estado actual de la fuente no permite la cascada.",
    "0x04040000":
      "La fuente del valor admite el protocolo de enlace en cascada; sin embargo, la fuente no ha seleccionado el primario de cascada correspondiente para su uso.",
    "0x04070000":
      "Hay una condición de estado de fallo activa en la fuente del valor.",
    "0x04080000":
      "Se está solicitando al destino una condición de estado de fallo.",
    "0x04090000":
      "El valor es preciso y la fuente de señal admite el protocolo de enlace en cascada.",
    "0x80E70000":
      "El DataSet especificado para la creación del DataSetWriter no es válido.",
  },
  it: {
    "0x00000000": "L'operazione è riuscita.",
    "0x40000000": "L'operazione ha avuto un esito incerto.",
    "0x80000000": "L'operazione non è riuscita.",
    "0x80010000": "Si è verificato un errore imprevisto.",
    "0x80020000":
      "Si è verificato un errore interno a causa di un errore di programmazione o configurazione.",
    "0x80030000": "Memoria insufficiente per completare l'operazione.",
    "0x80040000": "Una risorsa del sistema operativo non è disponibile.",
    "0x80050000":
      "Si è verificato un errore di comunicazione di basso livello.",
    "0x80060000":
      "La codifica è stata interrotta a causa di dati non validi negli oggetti da serializzare.",
    "0x80070000":
      "La decodifica è stata interrotta a causa di dati non validi nel flusso.",
    "0x80080000":
      "Sono stati superati i limiti di codifica/decodifica dei messaggi imposti dallo stack.",
    "0x80B80000":
      "La dimensione del messaggio di richiesta supera i limiti impostati dal server.",
    "0x80B90000":
      "La dimensione del messaggio di risposta supera i limiti impostati dal client o dal server.",
    "0x80090000": "È stata ricevuta dal server una risposta non riconosciuta.",
    "0x800A0000": "L'operazione è scaduta per timeout.",
    "0x800B0000": "Il server non supporta il servizio richiesto.",
    "0x800C0000":
      "L'operazione è stata annullata perché l'applicazione è in fase di arresto.",
    "0x800D0000":
      "L'operazione non può essere completata perché il client non è connesso al server.",
    "0x800E0000":
      "Il server si è arrestato e non può elaborare alcuna richiesta.",
    "0x800F0000":
      "Non è stato possibile eseguire alcuna elaborazione perché non c'era nulla da fare.",
    "0x80100000":
      "La richiesta non può essere elaborata perché specifica troppe operazioni.",
    "0x80DB0000":
      "La richiesta non può essere elaborata perché nella sottoscrizione sono presenti troppi elementi monitorati.",
    "0x80110000":
      "L'oggetto di estensione non può essere (de)serializzato perché l'ID del tipo di dati non è riconosciuto.",
    "0x80120000": "Il certificato fornito come parametro non è valido.",
    "0x80130000":
      "Si è verificato un errore durante la verifica della sicurezza.",
    "0x81140000":
      "Il certificato non soddisfa i requisiti della policy di sicurezza.",
    "0x80140000": "Il certificato è scaduto o non è ancora valido.",
    "0x80150000":
      "Un certificato dell'emittente è scaduto o non è ancora valido.",
    "0x80160000":
      "L'HostName utilizzato per connettersi a un server non corrisponde a un HostName presente nel certificato.",
    "0x80170000":
      "L'URI specificato in ApplicationDescription non corrisponde all'URI nel certificato.",
    "0x80180000":
      "Il certificato non può essere utilizzato per l'operazione richiesta.",
    "0x80190000":
      "Il certificato dell'emittente non può essere utilizzato per l'operazione richiesta.",
    "0x801A0000": "Il certificato non è attendibile.",
    "0x801B0000":
      "Non è stato possibile determinare se il certificato è stato revocato.",
    "0x801C0000":
      "Non è stato possibile determinare se il certificato dell'emittente è stato revocato.",
    "0x801D0000": "Il certificato è stato revocato.",
    "0x801E0000": "Il certificato dell'emittente è stato revocato.",
    "0x810D0000": "La catena di certificati è incompleta.",
    "0x801F0000":
      "L'utente non dispone dell'autorizzazione per eseguire l'operazione richiesta.",
    "0x80200000": "Il token di identità dell'utente non è valido.",
    "0x80210000":
      "Il token di identità dell'utente è valido, ma il server lo ha rifiutato.",
    "0x80220000": "Il canale sicuro specificato non è più valido.",
    "0x80230000":
      "Il timestamp è al di fuori dell'intervallo consentito dal server.",
    "0x80240000":
      "Il nonce sembra non essere un valore casuale oppure non ha la lunghezza corretta.",
    "0x80250000": "L'ID della sessione non è valido.",
    "0x80260000": "La sessione è stata chiusa dal client.",
    "0x80270000":
      "La sessione non può essere utilizzata perché ActivateSession non è stato chiamato.",
    "0x80280000": "L'ID della sottoscrizione non è valido.",
    "0x802A0000": "L'header della richiesta è mancante o non valido.",
    "0x802B0000":
      "Il parametro relativo ai timestamp da restituire non è valido.",
    "0x802C0000": "La richiesta è stata annullata dal client.",
    "0x80E50000": "Sono stati forniti troppi argomenti.",
    "0x810E0000":
      "Il server richiede una licenza per funzionare in generale o per eseguire un servizio o un'operazione, ma la licenza esistente è scaduta.",
    "0x810F0000":
      "Il server ha limiti sul numero di operazioni/oggetti consentiti in base alle licenze installate e tali limiti sono stati superati.",
    "0x81100000":
      "Il server non dispone di una licenza necessaria per funzionare in generale o per eseguire un servizio o un'operazione.",
    "0x80EE0000":
      "Il server non dispone al momento delle risorse necessarie per elaborare la richiesta.",
    "0x00EF0000":
      "L'accesso dell'utente è riuscito, ma l'utente deve cambiare la password.",
    "0x002D0000": "La sottoscrizione è stata trasferita a un'altra sessione.",
    "0x002E0000": "L'elaborazione verrà completata in modo asincrono.",
    "0x002F0000":
      "Il campionamento è rallentato a causa di limitazioni delle risorse.",
    "0x00300000": "Il valore scritto è stato accettato, ma è stato limitato.",
    "0x80310000":
      "La comunicazione con la sorgente dati è definita ma non stabilita e non è disponibile alcun ultimo valore noto.",
    "0x80320000":
      "In attesa che il server ottenga i valori dalla sorgente dati sottostante.",
    "0x80330000":
      "La sintassi dell'ID del nodo non è valida oppure fa riferimento a un nodo non valido per l'operazione.",
    "0x80340000":
      "L'ID del nodo fa riferimento a un nodo che non esiste nello spazio degli indirizzi del server.",
    "0x80350000": "L'attributo non è supportato per il Node specificato.",
    "0x80360000":
      "La sintassi del parametro dell'intervallo di indici non è valida.",
    "0x80370000": "Non esistono dati nell'intervallo di indici specificato.",
    "0x80EA0000":
      "I dati scritti non corrispondono all'IndexRange specificato.",
    "0x80380000": "La codifica dei dati non è valida.",
    "0x80390000":
      "Il server non supporta la codifica dati richiesta per il nodo.",
    "0x803A0000":
      "Il livello di accesso non consente la lettura o la sottoscrizione al Node.",
    "0x803B0000": "Il livello di accesso non consente la scrittura sul Node.",
    "0x803C0000": "Il valore è fuori intervallo.",
    "0x40F20000": "Il valore è superiore all'intervallo consentito.",
    "0x40F30000": "Il valore è inferiore all'intervallo consentito.",
    "0x803D0000": "L'operazione richiesta non è supportata.",
    "0x803E0000":
      "Un elemento richiesto non è stato trovato oppure un'operazione di ricerca è terminata senza successo.",
    "0x803F0000":
      "L'oggetto non può essere utilizzato perché è stato eliminato.",
    "0x80400000": "L'operazione richiesta non è implementata.",
    "0x80410000": "La modalità di monitoraggio non è valida.",
    "0x80420000":
      "L'ID dell'elemento monitorato non fa riferimento a un elemento monitorato valido.",
    "0x80430000":
      "Il parametro del filtro dell'elemento monitorato non è valido.",
    "0x80440000":
      "Il server non supporta il filtro dell'elemento monitorato richiesto.",
    "0x80450000":
      "Un filtro di monitoraggio non può essere utilizzato in combinazione con l'attributo specificato.",
    "0x80460000": "Un parametro strutturato obbligatorio era mancante o null.",
    "0x80470000": "Il filtro degli eventi non è valido.",
    "0x80480000": "Il filtro dei contenuti non è valido.",
    "0x80C10000": "È stato fornito in un filtro un operatore non riconosciuto.",
    "0x80C20000":
      "È stato fornito un operatore valido, ma il server non supporta questo operatore di filtro.",
    "0x80C30000":
      "Il numero di operandi forniti per l'operatore di filtro era inferiore a quello previsto per l'operando fornito.",
    "0x80490000":
      "L'operando utilizzato in un filtro dei contenuti non è valido.",
    "0x80C40000":
      "L'elemento referenziato non è un elemento valido nel filtro dei contenuti.",
    "0x80C50000": "Il valore letterale referenziato non è un valore valido.",
    "0x804A0000": "Il punto di continuazione fornito non è più valido.",
    "0x804B0000":
      "L'operazione non può essere elaborata perché tutti i punti di continuazione sono stati allocati.",
    "0x804C0000":
      "L'ID del tipo di riferimento non fa riferimento a un nodo di tipo di riferimento valido.",
    "0x804D0000": "La direzione di browse non è valida.",
    "0x804E0000": "Il nodo non fa parte della vista.",
    "0x81120000":
      "Il numero non è stato accettato a causa di un overflow numerico.",
    "0x80ED0000":
      "La locale nell'operazione di scrittura richiesta non è supportata.",
    "0x80F00000":
      "La variabile non ha né un valore predefinito né un valore iniziale.",
    "0x804F0000": "ServerUri non è un URI valido.",
    "0x80500000": "Non è stato specificato alcun ServerName.",
    "0x80510000": "Non è stato specificato alcun DiscoveryUrl.",
    "0x80520000": "Il file semaforo specificato dal client non è valido.",
    "0x80530000": "Il tipo di richiesta del token di sicurezza non è valido.",
    "0x80540000":
      "La modalità di sicurezza non soddisfa i requisiti impostati dal server.",
    "0x80550000":
      "La policy di sicurezza non soddisfa i requisiti impostati dal server.",
    "0x80560000": "Il server ha raggiunto il numero massimo di sessioni.",
    "0x80570000": "La firma del token utente è mancante o non valida.",
    "0x80580000":
      "La firma generata con il certificato client è mancante o non valida.",
    "0x80590000":
      "Il client non ha fornito almeno un certificato software valido e conforme ai requisiti di profilo del server.",
    "0x80C60000":
      "Il server non supporta la modifica dell'identità utente assegnata alla sessione.",
    "0x805A0000":
      "La richiesta è stata annullata dal client tramite il servizio Cancel.",
    "0x805B0000": "L'ID del nodo padre non fa riferimento a un nodo valido.",
    "0x805C0000":
      "Non è stato possibile creare il riferimento perché viola i vincoli imposti dal modello dati.",
    "0x805D0000":
      "L'ID del nodo richiesto è stato rifiutato perché non valido oppure perché il server non consente al client di specificare gli ID dei nodi.",
    "0x805E0000": "L'ID del nodo richiesto è già utilizzato da un altro nodo.",
    "0x805F0000": "La classe del nodo non è valida.",
    "0x80600000": "Il nome di browse non è valido.",
    "0x80610000":
      "Il nome di browse non è univoco tra i nodi che condividono la stessa relazione con il nodo padre.",
    "0x80620000":
      "Gli attributi del nodo non sono validi per la classe del nodo.",
    "0x80630000":
      "L'ID del nodo di definizione del tipo non fa riferimento a un nodo di tipo appropriato.",
    "0x80640000": "L'ID del nodo sorgente non fa riferimento a un nodo valido.",
    "0x80650000":
      "L'ID del nodo di destinazione non fa riferimento a un nodo valido.",
    "0x80660000": "Il tipo di riferimento tra i nodi è già definito.",
    "0x80670000":
      "Il server non consente questo tipo di autoriferimento su questo nodo.",
    "0x80680000":
      "Il tipo di riferimento non è valido per un riferimento a un server remoto.",
    "0x80690000": "Il server non consentirà l'eliminazione del nodo.",
    "0x40BC0000":
      "Il server non è riuscito a eliminare tutti i riferimenti di destinazione.",
    "0x806A0000": "L'indice del server non è valido.",
    "0x806B0000": "L'ID della vista non fa riferimento a un nodo vista valido.",
    "0x80C90000":
      "Il timestamp della vista non è disponibile o non è supportato.",
    "0x80CA0000": "I parametri della vista non sono coerenti tra loro.",
    "0x80CB0000":
      "La versione della vista non è disponibile o non è supportata.",
    "0x40C00000":
      "L'elenco dei riferimenti potrebbe non essere completo perché il sistema sottostante non è disponibile.",
    "0x00BA0000":
      "Il server avrebbe dovuto seguire un riferimento a un nodo su un server remoto ma non lo ha fatto. Il set di risultati potrebbe essere incompleto.",
    "0x80C80000":
      "Il Nodeid fornito non era un Nodeid di definizione del tipo.",
    "0x406C0000":
      "Uno dei riferimenti da seguire nel percorso relativo fa riferimento a un nodo nello spazio degli indirizzi di un altro server.",
    "0x806D0000":
      "L'operazione richiesta ha troppe corrispondenze da restituire.",
    "0x806E0000": "L'operazione richiesta richiede troppe risorse sul server.",
    "0x806F0000":
      "L'operazione richiesta non ha alcuna corrispondenza da restituire.",
    "0x80700000": "Il parametro dell'età massima non è valido.",
    "0x80E60000": "L'operazione non è consentita sul canale sicuro corrente.",
    "0x80710000": "Il parametro dei dettagli cronologici non è valido.",
    "0x80720000": "Il server non supporta l'operazione richiesta.",
    "0x80BD0000": "Il timestamp definito da restituire non era valido.",
    "0x80730000":
      "Il server non supporta la scrittura della combinazione fornita di valore, stato e timestamp.",
    "0x80740000":
      "Il valore fornito per l'attributo non è dello stesso tipo del valore dell'attributo.",
    "0x80750000":
      "L'ID del metodo non fa riferimento a un metodo per l'oggetto specificato.",
    "0x80760000":
      "Il client non ha specificato tutti gli argomenti di input per il metodo.",
    "0x81110000":
      "L'attributo eseguibile non consente l'esecuzione del metodo.",
    "0x80770000": "Il server ha raggiunto il numero massimo di sottoscrizioni.",
    "0x80780000":
      "Il server ha raggiunto il numero massimo di richieste Publish in coda.",
    "0x80790000":
      "Non è disponibile alcuna sottoscrizione per questa sessione.",
    "0x807A0000": "Il numero di sequenza è sconosciuto al server.",
    "0x00DF0000":
      "Il server non supporta la coda di ritrasmissione e la conferma dei numeri di sequenza non è disponibile.",
    "0x807B0000": "Il messaggio di notifica richiesto non è più disponibile.",
    "0x807C0000":
      "Il client della sessione corrente non supporta uno o più Profile necessari per la sottoscrizione.",
    "0x80BF0000": "La macchina a sottostati non è attualmente attiva.",
    "0x81150000": "Esiste già una regola equivalente.",
    "0x807D0000":
      "Il server non può elaborare la richiesta perché è troppo occupato.",
    "0x807E0000": "Il tipo di messaggio specificato nell'header non è valido.",
    "0x807F0000": "SecureChannelId e/o TokenId non sono attualmente in uso.",
    "0x80800000":
      "La dimensione del blocco di messaggio specificato nell'header è troppo grande.",
    "0x80810000": "Non ci sono risorse sufficienti per elaborare la richiesta.",
    "0x80820000": "Si è verificato un errore interno.",
    "0x80830000": "Il server non riconosce la QueryString specificata.",
    "0x80840000":
      "La richiesta non può essere inviata a causa di un'interruzione della rete.",
    "0x80850000":
      "Si è verificato un timeout durante l'elaborazione della richiesta.",
    "0x80860000": "Il canale sicuro è stato chiuso.",
    "0x80870000": "Il token è scaduto o non è riconosciuto.",
    "0x80880000": "Il numero di sequenza non è valido.",
    "0x80BE0000":
      "Le applicazioni non dispongono di versioni di protocollo compatibili.",
    "0x80890000":
      "Esiste un problema di configurazione che influisce sull'utilità del valore.",
    "0x808A0000":
      "La variabile dovrebbe ricevere il proprio valore da un'altra variabile, ma non è mai stata configurata per farlo.",
    "0x808B0000":
      "Si è verificato un guasto nel dispositivo/nella sorgente dati che genera il valore e ciò ha influito sul valore.",
    "0x808C0000":
      "Si è verificato un guasto nel sensore dal quale il valore viene derivato dal dispositivo/dalla sorgente dati.",
    "0x808D0000": "La sorgente dei dati non è operativa.",
    "0x808E0000": "Il filtro deadband non è valido.",
    "0x408F0000":
      "La comunicazione con la sorgente dati è fallita. Il valore della variabile è l'ultimo valore che aveva una qualità Good.",
    "0x40900000":
      "Il componente che aggiornava questo valore ha smesso di farlo.",
    "0x40910000":
      "Il valore è un valore operativo che è stato sovrascritto manualmente.",
    "0x40920000":
      "Il valore è un valore iniziale per una variabile che normalmente riceve il proprio valore da un'altra variabile.",
    "0x40930000": "Il valore si trova a uno dei limiti del sensore.",
    "0x40940000":
      "Il valore è al di fuori dell'intervallo di valori definito per questo parametro.",
    "0x40950000":
      "Il valore dei dati deriva da più sorgenti e dispone di un numero di sorgenti Good inferiore a quello richiesto.",
    "0x00960000": "Il valore è stato sovrascritto.",
    "0x00EB0000":
      "Il valore deriva da più sorgenti e dispone del numero richiesto di sorgenti Good, ma meno del numero totale di sorgenti Good.",
    "0x80970000":
      "Questo aggiornamento della Condition è fallito; è già in corso un'operazione di aggiornamento della Condition.",
    "0x80980000": "Questa Condition è già stata disabilitata.",
    "0x80CC0000": "Questa Condition è già stata abilitata.",
    "0x80990000": "Proprietà non disponibile; questa Condition è disabilitata.",
    "0x809A0000": "L'ID evento specificato non è riconosciuto.",
    "0x80BB0000": "L'evento non può essere riconosciuto.",
    "0x80CD0000": "La Condition della finestra di dialogo non è attiva.",
    "0x80CE0000": "La risposta non è valida per la finestra di dialogo.",
    "0x80CF0000": "Il ramo della Condition è già stato riconosciuto.",
    "0x80D00000": "Il ramo della Condition è già stato confermato.",
    "0x80D10000": "La Condition è già stata sospesa.",
    "0x80D20000": "La Condition non è attualmente sospesa.",
    "0x80D30000":
      "Il tempo di sospensione non rientra in un intervallo accettabile.",
    "0x809B0000":
      "Non esistono dati per l'intervallo di tempo o il filtro eventi richiesto.",
    "0x80D70000":
      "Non sono stati trovati dati per fornire il valore limite superiore o inferiore.",
    "0x80D80000": "Il server non può recuperare un limite per la variabile.",
    "0x809D0000":
      "Mancano dati a causa dell'avvio, dell'arresto o della perdita della raccolta.",
    "0x809E0000":
      "I dati previsti non sono disponibili per l'intervallo di tempo richiesto a causa di un volume non montato, di un archivio o nastro offline o di un motivo analogo di indisponibilità temporanea.",
    "0x809F0000":
      "I dati o l'evento non sono stati inseriti correttamente perché esiste una voce corrispondente.",
    "0x80A00000":
      "I dati o l'evento non sono stati aggiornati correttamente perché non esiste una voce corrispondente.",
    "0x80A10000":
      "Il client ha richiesto dati storici utilizzando un TimestampsToReturn non supportato dal server.",
    "0x00A20000":
      "I dati o l'evento sono stati inseriti correttamente nel database storico.",
    "0x00A30000":
      "Il campo dati o evento è stato sostituito correttamente nel database storico.",
    "0x40A40000":
      "Il valore aggregato deriva da più valori e dispone di un numero di valori Good inferiore a quello richiesto.",
    "0x00A50000":
      "Non esistono dati per l'intervallo di tempo o il filtro eventi richiesto.",
    "0x00A60000":
      "Nell'intervallo di tempo sono disponibili ulteriori dati oltre al numero di valori richiesto.",
    "0x80D40000":
      "Il numero richiesto di Aggregates non corrisponde al numero richiesto di NodeIds.",
    "0x80D50000": "L'Aggregate richiesto non è supportato dal server.",
    "0x80D60000":
      "Non è stato possibile derivare il valore aggregato a causa di dati di input non validi.",
    "0x80DA0000":
      "La configurazione dell'aggregato non è valida per il nodo specificato.",
    "0x00D90000":
      "La richiesta specifica campi non validi per l'EventType o che non possono essere salvati dallo historian.",
    "0x80E40000":
      "La richiesta è stata rifiutata dal server perché non soddisfaceva i criteri impostati dal server.",
    "0x81130000": "La richiesta non è ancora stata elaborata dal server.",
    "0x80E80000":
      "L'operazione non è consentita perché è in corso una transazione.",
    "0x80F10000":
      "L'operazione è fallita e tutte le modifiche che facevano parte della transazione vengono annullate.",
    "0x811F0000":
      "L'identità del dispositivo necessita di un ticket prima di poter essere accettata.",
    "0x81200000":
      "L'identità del dispositivo necessita di un ticket prima di poter essere accettata.",
    "0x80E90000":
      "L'operazione richiesta non è consentita perché il Node è bloccato da un'altra applicazione.",
    "0x80EC0000":
      "L'operazione richiesta non è consentita perché il Node non è bloccato dall'applicazione.",
    "0x00DC0000":
      "Il valore non proviene dalla sorgente reale ed è stato modificato dal server.",
    "0x00DD0000":
      "Si è verificato un errore durante l'esecuzione di queste post-azioni.",
    "0x40DE0000":
      "La relativa EngineeringUnit è stata modificata, ma il valore della Variable viene ancora fornito in base all'unità precedente.",
    "0x00E00000":
      "Un valore dipendente è stato modificato, ma la modifica non è stata applicata al dispositivo.",
    "0x80E10000":
      "La relativa EngineeringUnit è stata modificata, ma questa modifica non è stata applicata al dispositivo. Il valore della Variable dipende ancora dall'unità precedente, ma il suo stato è attualmente Bad.",
    "0x40E20000":
      "Un valore dipendente è stato modificato, ma la modifica non è stata applicata al dispositivo. La qualità della variabile dominante è incerta.",
    "0x80E30000":
      "Un valore dipendente è stato modificato, ma la modifica non è stata applicata al dispositivo. La qualità della variabile dominante è Bad.",
    "0x01160000":
      "Viene fornito con un valore della Variable dominante quando una Variable dipendente è stata modificata ma la modifica non è stata applicata.",
    "0x01170000":
      "Viene fornito con un valore della Variable dipendente quando una Variable dominante è stata modificata ma la modifica non è stata applicata.",
    "0x01180000":
      "Viene fornito con un valore della Variable dipendente quando una Variable dominante o dipendente è stata modificata ma la modifica non è stata applicata.",
    "0x81190000":
      "Viene fornito con un valore della Variable quando la Variable è stata modificata ma il valore non è valido.",
    "0x811A0000":
      "Viene fornito con un valore della Variable quando una Variable sorgente è stata modificata ma il valore non è valido.",
    "0x811B0000":
      "Viene fornito con un valore della Variable dipendente quando una Variable dominante è stata modificata e il valore non è valido.",
    "0x811C0000":
      "Viene fornito con un valore della Variable dipendente quando una Variable dominante è stata modificata, il valore non è valido e la modifica non è stata applicata.",
    "0x811D0000":
      "Viene fornito con un valore della Variable dipendente quando una Variable dominante o dipendente è stata modificata e il valore non è valido.",
    "0x811E0000":
      "Viene fornito con un valore della Variable dipendente quando una Variable dominante o dipendente è stata modificata, il valore non è valido e la modifica non è stata applicata.",
    "0x00A70000": "Il livello di comunicazione ha generato un evento.",
    "0x00A80000": "Il sistema è in fase di arresto.",
    "0x00A90000": "L'operazione non è terminata e deve essere richiamata.",
    "0x00AA0000": "Si è verificato un timeout non critico.",
    "0x80AB0000": "Uno o più argomenti non sono validi.",
    "0x80AC0000":
      "Non è stato possibile stabilire una connessione di rete con il server remoto.",
    "0x80AD0000": "Il server si è disconnesso dal client.",
    "0x80AE0000": "La connessione di rete è stata chiusa.",
    "0x80AF0000":
      "L'operazione non può essere completata perché l'oggetto è chiuso, non inizializzato o in un altro stato non valido.",
    "0x80B00000": "Non è possibile spostarsi oltre la fine del flusso.",
    "0x80B10000":
      "Al momento non sono disponibili dati da leggere da un flusso non bloccante.",
    "0x80B20000": "L'operazione asincrona è in attesa di una risposta.",
    "0x80B30000": "L'operazione asincrona è stata abbandonata dal chiamante.",
    "0x80B40000":
      "Il flusso non ha restituito tutti i dati richiesti, probabilmente perché è un flusso non bloccante.",
    "0x80B50000":
      "È richiesto un comportamento non bloccante e l'operazione bloccherebbe.",
    "0x80B60000": "Un valore aveva una sintassi non valida.",
    "0x80B70000":
      "L'operazione non può essere completata perché tutte le connessioni disponibili sono in uso.",
    "0x42080000":
      "Il valore potrebbe non essere accurato perché il trasduttore è in modalità manuale.",
    "0x42090000": "Il valore è simulato.",
    "0x420A0000":
      "Il valore potrebbe non essere accurato a causa di un errore di calibrazione del sensore.",
    "0x420F0000":
      "Il valore potrebbe non essere accurato a causa di un problema di configurazione.",
    "0x04010000":
      "La sorgente del valore supporta l'handshake a cascata e il valore è stato inizializzato in base a una richiesta di inizializzazione da un secondario della cascata.",
    "0x04020000":
      "La sorgente del valore supporta l'handshake a cascata e richiede l'inizializzazione di un primario della cascata.",
    "0x04030000":
      "La sorgente del valore supporta l'handshake a cascata; tuttavia, lo stato corrente della sorgente non consente la cascata.",
    "0x04040000":
      "La sorgente del valore supporta l'handshake a cascata; tuttavia, la sorgente non ha selezionato il corrispondente primario della cascata da utilizzare.",
    "0x04070000":
      "È attiva una condizione di stato di guasto nella sorgente del valore.",
    "0x04080000":
      "Alla destinazione viene richiesta una condizione di stato di guasto.",
    "0x04090000":
      "Il valore è accurato e la sorgente del segnale supporta l'handshake a cascata.",
    "0x80E70000":
      "Il DataSet specificato per la creazione del DataSetWriter non è valido.",
  },
  nl: {
    "0x00000000": "De bewerking is geslaagd.",
    "0x40000000": "Het resultaat van de bewerking was onzeker.",
    "0x80000000": "De bewerking is mislukt.",
    "0x80010000": "Er is een onverwachte fout opgetreden.",
    "0x80020000":
      "Er is een interne fout opgetreden als gevolg van een programmeer- of configuratiefout.",
    "0x80030000": "Er is onvoldoende geheugen om de bewerking te voltooien.",
    "0x80040000": "Een besturingssysteemresource is niet beschikbaar.",
    "0x80050000": "Er is een communicatiefout op laag niveau opgetreden.",
    "0x80060000":
      "De codering is gestopt vanwege ongeldige gegevens in de objecten die worden geserialiseerd.",
    "0x80070000":
      "De decodering is gestopt vanwege ongeldige gegevens in de stream.",
    "0x80080000":
      "De door de stack opgelegde limieten voor berichtcodering/-decodering zijn overschreden.",
    "0x80B80000":
      "De grootte van het aanvraagbericht overschrijdt de door de server ingestelde limieten.",
    "0x80B90000":
      "De grootte van het antwoordbericht overschrijdt de door de client of server ingestelde limieten.",
    "0x80090000": "Er is een niet-herkend antwoord van de server ontvangen.",
    "0x800A0000": "Er is een time-out opgetreden voor de bewerking.",
    "0x800B0000": "De server ondersteunt de aangevraagde service niet.",
    "0x800C0000":
      "De bewerking is geannuleerd omdat de toepassing wordt afgesloten.",
    "0x800D0000":
      "De bewerking kon niet worden voltooid omdat de client niet met de server is verbonden.",
    "0x800E0000": "De server is gestopt en kan geen aanvragen verwerken.",
    "0x800F0000":
      "Er kon geen verwerking plaatsvinden omdat er niets te doen was.",
    "0x80100000":
      "De aanvraag kon niet worden verwerkt omdat er te veel bewerkingen waren opgegeven.",
    "0x80DB0000":
      "De aanvraag kon niet worden verwerkt omdat er te veel bewaakte items in het abonnement staan.",
    "0x80110000":
      "Het extensieobject kan niet worden (gede)serialiseerd omdat de gegevenstype-ID niet wordt herkend.",
    "0x80120000": "Het als parameter opgegeven certificaat is niet geldig.",
    "0x80130000":
      "Er is een fout opgetreden bij het verifiëren van de beveiliging.",
    "0x81140000":
      "Het certificaat voldoet niet aan de vereisten van het beveiligingsbeleid.",
    "0x80140000": "Het certificaat is verlopen of nog niet geldig.",
    "0x80150000":
      "Een certificaat van de uitgever is verlopen of nog niet geldig.",
    "0x80160000":
      "De HostName die is gebruikt om verbinding te maken met een server komt niet overeen met een HostName in het certificaat.",
    "0x80170000":
      "De URI die in ApplicationDescription is opgegeven komt niet overeen met de URI in het certificaat.",
    "0x80180000":
      "Het certificaat mag niet worden gebruikt voor de aangevraagde bewerking.",
    "0x80190000":
      "Het certificaat van de uitgever mag niet worden gebruikt voor de aangevraagde bewerking.",
    "0x801A0000": "Het certificaat wordt niet vertrouwd.",
    "0x801B0000":
      "Er kon niet worden vastgesteld of het certificaat is ingetrokken.",
    "0x801C0000":
      "Er kon niet worden vastgesteld of het certificaat van de uitgever is ingetrokken.",
    "0x801D0000": "Het certificaat is ingetrokken.",
    "0x801E0000": "Het certificaat van de uitgever is ingetrokken.",
    "0x810D0000": "De certificaatketen is onvolledig.",
    "0x801F0000":
      "De gebruiker heeft geen toestemming om de aangevraagde bewerking uit te voeren.",
    "0x80200000": "Het gebruikersidentiteitstoken is niet geldig.",
    "0x80210000":
      "Het gebruikersidentiteitstoken is geldig, maar de server heeft het geweigerd.",
    "0x80220000": "Het opgegeven beveiligde kanaal is niet meer geldig.",
    "0x80230000":
      "De tijdstempel ligt buiten het door de server toegestane bereik.",
    "0x80240000":
      "De nonce lijkt geen willekeurige waarde te zijn of heeft niet de juiste lengte.",
    "0x80250000": "De sessie-ID is niet geldig.",
    "0x80260000": "De sessie is door de client gesloten.",
    "0x80270000":
      "De sessie kan niet worden gebruikt omdat ActivateSession niet is aangeroepen.",
    "0x80280000": "De abonnement-ID is niet geldig.",
    "0x802A0000": "De header van de aanvraag ontbreekt of is ongeldig.",
    "0x802B0000":
      "De parameter voor de terug te geven tijdstempels is ongeldig.",
    "0x802C0000": "De aanvraag is door de client geannuleerd.",
    "0x80E50000": "Er zijn te veel argumenten opgegeven.",
    "0x810E0000":
      "De server heeft een licentie nodig om in het algemeen te werken of om een service of bewerking uit te voeren, maar de bestaande licentie is verlopen.",
    "0x810F0000":
      "De server heeft op basis van de geïnstalleerde licenties limieten voor het aantal toegestane bewerkingen/objecten en deze limieten zijn overschreden.",
    "0x81100000":
      "De server beschikt niet over een licentie die nodig is om in het algemeen te werken of om een service of bewerking uit te voeren.",
    "0x80EE0000":
      "De server beschikt momenteel niet over de middelen om de aanvraag te verwerken.",
    "0x00EF0000":
      "De aanmelding van de gebruiker is geslaagd, maar de gebruiker moet het wachtwoord wijzigen.",
    "0x002D0000": "Het abonnement is overgedragen naar een andere sessie.",
    "0x002E0000": "De verwerking wordt asynchroon voltooid.",
    "0x002F0000": "De bemonstering is vertraagd vanwege beperkte middelen.",
    "0x00300000": "De geschreven waarde is geaccepteerd, maar is begrensd.",
    "0x80310000":
      "Communicatie met de gegevensbron is gedefinieerd maar niet tot stand gebracht en er is geen laatst bekende waarde beschikbaar.",
    "0x80320000":
      "Er wordt gewacht tot de server waarden van de onderliggende gegevensbron verkrijgt.",
    "0x80330000":
      "De syntaxis van de node-ID is ongeldig of verwijst naar een node die niet geldig is voor de bewerking.",
    "0x80340000":
      "De node-ID verwijst naar een node die niet bestaat in de adresruimte van de server.",
    "0x80350000":
      "Het attribuut wordt niet ondersteund voor de opgegeven Node.",
    "0x80360000": "De syntaxis van de indexbereikparameter is ongeldig.",
    "0x80370000": "Er bestaan geen gegevens binnen het opgegeven indexbereik.",
    "0x80EA0000":
      "De geschreven gegevens komen niet overeen met de opgegeven IndexRange.",
    "0x80380000": "De gegevenscodering is ongeldig.",
    "0x80390000":
      "De server ondersteunt de aangevraagde gegevenscodering voor de node niet.",
    "0x803A0000":
      "Het toegangsniveau staat het lezen van of abonneren op de Node niet toe.",
    "0x803B0000": "Het toegangsniveau staat schrijven naar de Node niet toe.",
    "0x803C0000": "De waarde lag buiten het bereik.",
    "0x40F20000": "De waarde ligt boven het toegestane bereik.",
    "0x40F30000": "De waarde ligt onder het toegestane bereik.",
    "0x803D0000": "De aangevraagde bewerking wordt niet ondersteund.",
    "0x803E0000":
      "Een aangevraagd item is niet gevonden of een zoekbewerking is zonder succes beëindigd.",
    "0x803F0000":
      "Het object kan niet worden gebruikt omdat het is verwijderd.",
    "0x80400000": "De aangevraagde bewerking is niet geïmplementeerd.",
    "0x80410000": "De bewakingsmodus is ongeldig.",
    "0x80420000":
      "De ID van het bewaakte item verwijst niet naar een geldig bewaakt item.",
    "0x80430000": "De filterparameter van het bewaakte item is ongeldig.",
    "0x80440000":
      "De server ondersteunt het aangevraagde filter voor bewaakte items niet.",
    "0x80450000":
      "Een bewakingsfilter kan niet worden gebruikt in combinatie met het opgegeven attribuut.",
    "0x80460000":
      "Een verplichte gestructureerde parameter ontbrak of was null.",
    "0x80470000": "Het gebeurtenisfilter is ongeldig.",
    "0x80480000": "Het inhoudsfilter is ongeldig.",
    "0x80C10000": "Er is een niet-herkende operator opgegeven in een filter.",
    "0x80C20000":
      "Er is een geldige operator opgegeven, maar de server ondersteunt deze filteroperator niet.",
    "0x80C30000":
      "Het aantal voor de filteroperator opgegeven operanden was kleiner dan verwacht voor de opgegeven operand.",
    "0x80490000":
      "De operand die in een inhoudsfilter wordt gebruikt is ongeldig.",
    "0x80C40000":
      "Het gerefereerde element is geen geldig element in het inhoudsfilter.",
    "0x80C50000": "De gerefereerde literal is geen geldige waarde.",
    "0x804A0000": "Het opgegeven vervolgpunct is niet meer geldig.",
    "0x804B0000":
      "De bewerking kon niet worden verwerkt omdat alle vervolgpunten zijn toegewezen.",
    "0x804C0000":
      "De referentietype-ID verwijst niet naar een geldige referentietypenode.",
    "0x804D0000": "De bladerichting is ongeldig.",
    "0x804E0000": "De node maakt geen deel uit van de weergave.",
    "0x81120000":
      "Het getal is niet geaccepteerd vanwege een numerieke overflow.",
    "0x80ED0000":
      "De locale in de aangevraagde schrijfbewerking wordt niet ondersteund.",
    "0x80F00000":
      "De variabele heeft geen standaardwaarde en geen initiële waarde.",
    "0x804F0000": "ServerUri is geen geldige URI.",
    "0x80500000": "Er is geen ServerName opgegeven.",
    "0x80510000": "Er is geen DiscoveryUrl opgegeven.",
    "0x80520000": "Het door de client opgegeven semafoorbestand is ongeldig.",
    "0x80530000": "Het type beveiligingstokenaanvraag is ongeldig.",
    "0x80540000":
      "De beveiligingsmodus voldoet niet aan de door de server gestelde eisen.",
    "0x80550000":
      "Het beveiligingsbeleid voldoet niet aan de door de server gestelde eisen.",
    "0x80560000": "De server heeft het maximale aantal sessies bereikt.",
    "0x80570000":
      "De handtekening van het gebruikerstoken ontbreekt of is ongeldig.",
    "0x80580000":
      "De met het clientcertificaat gegenereerde handtekening ontbreekt of is ongeldig.",
    "0x80590000":
      "De client heeft niet ten minste één softwarecertificaat aangeleverd dat geldig is en voldoet aan de profielvereisten van de server.",
    "0x80C60000":
      "De server ondersteunt het wijzigen van de aan de sessie toegewezen gebruikersidentiteit niet.",
    "0x805A0000":
      "De aanvraag is door de client geannuleerd met de Cancel-service.",
    "0x805B0000":
      "De ID van de bovenliggende node verwijst niet naar een geldige node.",
    "0x805C0000":
      "De referentie kon niet worden gemaakt omdat deze beperkingen schendt die door het gegevensmodel zijn opgelegd.",
    "0x805D0000":
      "De aangevraagde node-ID is geweigerd omdat deze ongeldig was of omdat de server niet toestaat dat node-ID's door de client worden opgegeven.",
    "0x805E0000":
      "De aangevraagde node-ID wordt al door een andere node gebruikt.",
    "0x805F0000": "De nodeklasse is ongeldig.",
    "0x80600000": "De bladeraanduiding is ongeldig.",
    "0x80610000":
      "De bladeraanduiding is niet uniek onder nodes die dezelfde relatie met de bovenliggende node delen.",
    "0x80620000": "De nodeattributen zijn niet geldig voor de nodeklasse.",
    "0x80630000":
      "De node-ID van de typedefinitie verwijst niet naar een geschikte typenode.",
    "0x80640000": "De bron-node-ID verwijst niet naar een geldige node.",
    "0x80650000": "De doel-node-ID verwijst niet naar een geldige node.",
    "0x80660000": "Het referentietype tussen de nodes is al gedefinieerd.",
    "0x80670000":
      "De server staat dit type zelfreferentie op deze node niet toe.",
    "0x80680000":
      "Het referentietype is niet geldig voor een verwijzing naar een externe server.",
    "0x80690000": "De server staat niet toe dat de node wordt verwijderd.",
    "0x40BC0000": "De server kon niet alle doelreferenties verwijderen.",
    "0x806A0000": "De serverindex is ongeldig.",
    "0x806B0000": "De weergave-ID verwijst niet naar een geldige weergavenode.",
    "0x80C90000":
      "De tijdstempel van de weergave is niet beschikbaar of wordt niet ondersteund.",
    "0x80CA0000": "De weergaveparameters zijn niet onderling consistent.",
    "0x80CB0000":
      "De weergaveversie is niet beschikbaar of wordt niet ondersteund.",
    "0x40C00000":
      "De lijst met referenties is mogelijk onvolledig omdat het onderliggende systeem niet beschikbaar is.",
    "0x00BA0000":
      "De server had een referentie naar een node op een externe server moeten volgen, maar deed dit niet. De resultatenset kan onvolledig zijn.",
    "0x80C80000": "De opgegeven Nodeid was geen Nodeid van een typedefinitie.",
    "0x406C0000":
      "Een van de referenties die in het relatieve pad moeten worden gevolgd verwijst naar een node in de adresruimte van een andere server.",
    "0x806D0000":
      "De aangevraagde bewerking heeft te veel overeenkomsten om terug te geven.",
    "0x806E0000":
      "De aangevraagde bewerking vereist te veel middelen op de server.",
    "0x806F0000":
      "De aangevraagde bewerking heeft geen overeenkomst om terug te geven.",
    "0x80700000": "De parameter voor de maximale leeftijd is ongeldig.",
    "0x80E60000":
      "De bewerking is niet toegestaan via het huidige beveiligde kanaal.",
    "0x80710000": "De parameter voor historische details is ongeldig.",
    "0x80720000": "De server ondersteunt de aangevraagde bewerking niet.",
    "0x80BD0000": "De gedefinieerde terug te geven tijdstempel was ongeldig.",
    "0x80730000":
      "De server ondersteunt het schrijven van de opgegeven combinatie van waarde, status en tijdstempels niet.",
    "0x80740000":
      "De voor het attribuut opgegeven waarde is niet van hetzelfde type als de waarde van het attribuut.",
    "0x80750000":
      "De methode-ID verwijst niet naar een methode voor het opgegeven object.",
    "0x80760000":
      "De client heeft niet alle invoerargumenten voor de methode opgegeven.",
    "0x81110000":
      "Het uitvoerbare attribuut staat uitvoering van de methode niet toe.",
    "0x80770000": "De server heeft het maximale aantal abonnementen bereikt.",
    "0x80780000":
      "De server heeft het maximale aantal in de wachtrij geplaatste Publish-aanvragen bereikt.",
    "0x80790000": "Er is geen abonnement beschikbaar voor deze sessie.",
    "0x807A0000": "Het volgnummer is onbekend bij de server.",
    "0x00DF0000":
      "De server ondersteunt geen retransmissiewachtrij en bevestiging van volgnummers is niet beschikbaar.",
    "0x807B0000": "Het aangevraagde meldingsbericht is niet meer beschikbaar.",
    "0x807C0000":
      "De client van de huidige sessie ondersteunt één of meer Profiles niet die voor het abonnement noodzakelijk zijn.",
    "0x80BF0000": "De subtoestandsmachine is momenteel niet actief.",
    "0x81150000": "Er bestaat al een equivalente regel.",
    "0x807D0000":
      "De server kan de aanvraag niet verwerken omdat hij te druk is.",
    "0x807E0000": "Het type bericht dat in de header is opgegeven is ongeldig.",
    "0x807F0000":
      "SecureChannelId en/of TokenId zijn momenteel niet in gebruik.",
    "0x80800000":
      "De grootte van het in de header opgegeven berichtfragment is te groot.",
    "0x80810000": "Er zijn onvoldoende middelen om de aanvraag te verwerken.",
    "0x80820000": "Er is een interne fout opgetreden.",
    "0x80830000": "De server herkent de opgegeven QueryString niet.",
    "0x80840000":
      "De aanvraag kon niet worden verzonden vanwege een netwerkonderbreking.",
    "0x80850000":
      "Er is een time-out opgetreden tijdens het verwerken van de aanvraag.",
    "0x80860000": "Het beveiligde kanaal is gesloten.",
    "0x80870000": "Het token is verlopen of wordt niet herkend.",
    "0x80880000": "Het volgnummer is ongeldig.",
    "0x80BE0000": "De toepassingen hebben geen compatibele protocolversies.",
    "0x80890000":
      "Er is een probleem met de configuratie dat de bruikbaarheid van de waarde beïnvloedt.",
    "0x808A0000":
      "De variabele zou zijn waarde van een andere variabele moeten ontvangen, maar is daar nooit voor geconfigureerd.",
    "0x808B0000":
      "Er is een storing opgetreden in het apparaat/de gegevensbron die de waarde genereert, waardoor de waarde is beïnvloed.",
    "0x808C0000":
      "Er is een storing opgetreden in de sensor waarvan de waarde door het apparaat/de gegevensbron wordt afgeleid.",
    "0x808D0000": "De bron van de gegevens is niet operationeel.",
    "0x808E0000": "Het deadbandfilter is ongeldig.",
    "0x408F0000":
      "De communicatie met de gegevensbron is mislukt. De variabelewaarde is de laatste waarde met een Good-kwaliteit.",
    "0x40900000": "Het onderdeel dat deze waarde bijwerkte is daarmee gestopt.",
    "0x40910000":
      "De waarde is een operationele waarde die handmatig is overschreven.",
    "0x40920000":
      "De waarde is een initiële waarde voor een variabele die normaal haar waarde van een andere variabele ontvangt.",
    "0x40930000": "De waarde bevindt zich op een van de sensorgrenzen.",
    "0x40940000":
      "De waarde ligt buiten het voor deze parameter gedefinieerde waardenbereik.",
    "0x40950000":
      "De gegevenswaarde is afgeleid van meerdere bronnen en heeft minder dan het vereiste aantal Good-bronnen.",
    "0x00960000": "De waarde is overschreven.",
    "0x00EB0000":
      "De waarde is afgeleid van meerdere bronnen en heeft het vereiste aantal Good-bronnen, maar minder dan het volledige aantal Good-bronnen.",
    "0x80970000":
      "Deze Condition-verversing is mislukt; er is al een Condition-verversingsbewerking actief.",
    "0x80980000": "Deze Condition is al uitgeschakeld.",
    "0x80CC0000": "Deze Condition is al ingeschakeld.",
    "0x80990000":
      "Eigenschap niet beschikbaar; deze Condition is uitgeschakeld.",
    "0x809A0000": "De opgegeven gebeurtenis-ID wordt niet herkend.",
    "0x80BB0000": "De gebeurtenis kan niet worden bevestigd.",
    "0x80CD0000": "De dialoog-Condition is niet actief.",
    "0x80CE0000": "Het antwoord is niet geldig voor de dialoog.",
    "0x80CF0000": "De Condition-tak is al bevestigd.",
    "0x80D00000": "De Condition-tak is al definitief bevestigd.",
    "0x80D10000": "De Condition is al opgeschort.",
    "0x80D20000": "De Condition is momenteel niet opgeschort.",
    "0x80D30000":
      "De opschortingstijd ligt niet binnen een aanvaardbaar bereik.",
    "0x809B0000":
      "Er bestaan geen gegevens voor het aangevraagde tijdsbereik of gebeurtenisfilter.",
    "0x80D70000":
      "Er zijn geen gegevens gevonden om een boven- of ondergrenswaarde te leveren.",
    "0x80D80000": "De server kan geen grens voor de variabele ophalen.",
    "0x809D0000":
      "Gegevens ontbreken doordat de verzameling is gestart, gestopt of verloren gegaan.",
    "0x809E0000":
      "Verwachte gegevens zijn niet beschikbaar voor het aangevraagde tijdsbereik vanwege een niet-gemount volume, een offline archief of tape, of een vergelijkbare reden voor tijdelijke onbeschikbaarheid.",
    "0x809F0000":
      "De gegevens of gebeurtenis zijn niet succesvol ingevoegd omdat er een overeenkomende invoer bestaat.",
    "0x80A00000":
      "De gegevens of gebeurtenis zijn niet succesvol bijgewerkt omdat er geen overeenkomende invoer bestaat.",
    "0x80A10000":
      "De client heeft historische gegevens aangevraagd met een TimestampsToReturn die de server niet ondersteunt.",
    "0x00A20000":
      "De gegevens of gebeurtenis zijn succesvol in de historische database ingevoegd.",
    "0x00A30000":
      "Het gegevens- of gebeurtenisveld is succesvol vervangen in de historische database.",
    "0x40A40000":
      "De aggregatiewaarde is afgeleid van meerdere waarden en heeft minder dan het vereiste aantal Good-waarden.",
    "0x00A50000":
      "Er bestaan geen gegevens voor het aangevraagde tijdsbereik of gebeurtenisfilter.",
    "0x00A60000":
      "Er zijn meer gegevens beschikbaar in het tijdsbereik dan het aangevraagde aantal waarden.",
    "0x80D40000":
      "Het aangevraagde aantal Aggregates komt niet overeen met het aangevraagde aantal NodeIds.",
    "0x80D50000":
      "De aangevraagde Aggregate wordt niet door de server ondersteund.",
    "0x80D60000":
      "De aggregatiewaarde kon niet worden afgeleid vanwege ongeldige invoergegevens.",
    "0x80DA0000":
      "De aggregatieconfiguratie is niet geldig voor de opgegeven node.",
    "0x00D90000":
      "De aanvraag specificeert velden die niet geldig zijn voor het EventType of niet door de historian kunnen worden opgeslagen.",
    "0x80E40000":
      "De aanvraag is door de server geweigerd omdat deze niet voldeed aan de door de server gestelde criteria.",
    "0x81130000": "De aanvraag is nog niet door de server verwerkt.",
    "0x80E80000":
      "De bewerking is niet toegestaan omdat er een transactie actief is.",
    "0x80F10000":
      "De bewerking is mislukt en alle wijzigingen die deel uitmaakten van de transactie worden teruggedraaid.",
    "0x811F0000":
      "De apparaatidentiteit heeft een ticket nodig voordat deze kan worden geaccepteerd.",
    "0x81200000":
      "De apparaatidentiteit heeft een ticket nodig voordat deze kan worden geaccepteerd.",
    "0x80E90000":
      "De aangevraagde bewerking is niet toegestaan omdat de Node door een andere toepassing is vergrendeld.",
    "0x80EC0000":
      "De aangevraagde bewerking is niet toegestaan omdat de Node niet door de toepassing is vergrendeld.",
    "0x00DC0000":
      "De waarde komt niet uit de echte bron en is door de server bewerkt.",
    "0x00DD0000":
      "Er is een fout opgetreden bij de uitvoering van deze na-acties.",
    "0x40DE0000":
      "De gerelateerde EngineeringUnit is gewijzigd, maar de Variable-waarde wordt nog steeds geleverd op basis van de vorige eenheid.",
    "0x00E00000":
      "Een afhankelijke waarde is gewijzigd, maar de wijziging is niet op het apparaat toegepast.",
    "0x80E10000":
      "De gerelateerde EngineeringUnit is gewijzigd, maar deze wijziging is niet op het apparaat toegepast. De Variable-waarde is nog steeds afhankelijk van de vorige eenheid, maar de status is momenteel Bad.",
    "0x40E20000":
      "Een afhankelijke waarde is gewijzigd, maar de wijziging is niet op het apparaat toegepast. De kwaliteit van de dominante variabele is onzeker.",
    "0x80E30000":
      "Een afhankelijke waarde is gewijzigd, maar de wijziging is niet op het apparaat toegepast. De kwaliteit van de dominante variabele is Bad.",
    "0x01160000":
      "Dit wordt geleverd met een dominante Variable-waarde wanneer een afhankelijke Variable is gewijzigd, maar de wijziging niet is toegepast.",
    "0x01170000":
      "Dit wordt geleverd met een afhankelijke Variable-waarde wanneer een dominante Variable is gewijzigd, maar de wijziging niet is toegepast.",
    "0x01180000":
      "Dit wordt geleverd met een afhankelijke Variable-waarde wanneer een dominante of afhankelijke Variable is gewijzigd, maar de wijziging niet is toegepast.",
    "0x81190000":
      "Dit wordt geleverd met een Variable-waarde wanneer de Variable is gewijzigd, maar de waarde niet geldig is.",
    "0x811A0000":
      "Dit wordt geleverd met een Variable-waarde wanneer een bron-Variable is gewijzigd, maar de waarde niet geldig is.",
    "0x811B0000":
      "Dit wordt geleverd met een afhankelijke Variable-waarde wanneer een dominante Variable is gewijzigd en de waarde niet geldig is.",
    "0x811C0000":
      "Dit wordt geleverd met een afhankelijke Variable-waarde wanneer een dominante Variable is gewijzigd, de waarde niet geldig is en de wijziging niet is toegepast.",
    "0x811D0000":
      "Dit wordt geleverd met een afhankelijke Variable-waarde wanneer een dominante of afhankelijke Variable is gewijzigd en de waarde niet geldig is.",
    "0x811E0000":
      "Dit wordt geleverd met een afhankelijke Variable-waarde wanneer een dominante of afhankelijke Variable is gewijzigd, de waarde niet geldig is en de wijziging niet is toegepast.",
    "0x00A70000": "De communicatielaag heeft een gebeurtenis gegenereerd.",
    "0x00A80000": "Het systeem wordt afgesloten.",
    "0x00A90000":
      "De bewerking is niet voltooid en moet opnieuw worden aangeroepen.",
    "0x00AA0000": "Er is een niet-kritieke time-out opgetreden.",
    "0x80AB0000": "Een of meer argumenten zijn ongeldig.",
    "0x80AC0000":
      "Er kon geen netwerkverbinding met de externe server tot stand worden gebracht.",
    "0x80AD0000": "De server heeft de verbinding met de client verbroken.",
    "0x80AE0000": "De netwerkverbinding is gesloten.",
    "0x80AF0000":
      "De bewerking kan niet worden voltooid omdat het object gesloten, niet geïnitialiseerd of in een andere ongeldige toestand is.",
    "0x80B00000": "Er kan niet voorbij het einde van de stream worden gegaan.",
    "0x80B10000":
      "Er zijn momenteel geen gegevens beschikbaar om uit een niet-blokkerende stream te lezen.",
    "0x80B20000": "De asynchrone bewerking wacht op een antwoord.",
    "0x80B30000": "De asynchrone bewerking is door de aanroeper verlaten.",
    "0x80B40000":
      "De stream heeft niet alle aangevraagde gegevens teruggegeven, mogelijk omdat het een niet-blokkerende stream is.",
    "0x80B50000":
      "Niet-blokkerend gedrag is vereist en de bewerking zou blokkeren.",
    "0x80B60000": "Een waarde had een ongeldige syntaxis.",
    "0x80B70000":
      "De bewerking kon niet worden voltooid omdat alle beschikbare verbindingen in gebruik zijn.",
    "0x42080000":
      "De waarde is mogelijk niet nauwkeurig omdat de transducer in handmatige modus staat.",
    "0x42090000": "De waarde is gesimuleerd.",
    "0x420A0000":
      "De waarde is mogelijk niet nauwkeurig vanwege een kalibratiefout van de sensor.",
    "0x420F0000":
      "De waarde is mogelijk niet nauwkeurig vanwege een configuratieprobleem.",
    "0x04010000":
      "De waardebron ondersteunt cascade-handshaking en de waarde is geïnitialiseerd op basis van een initialisatieverzoek van een secundaire cascade.",
    "0x04020000":
      "De waardebron ondersteunt cascade-handshaking en vraagt om initialisatie van een primaire cascade.",
    "0x04030000":
      "De waardebron ondersteunt cascade-handshaking; de huidige toestand van de bron staat cascade echter niet toe.",
    "0x04040000":
      "De waardebron ondersteunt cascade-handshaking; de bron heeft de bijbehorende primaire cascade echter nog niet geselecteerd voor gebruik.",
    "0x04070000": "Er is een actieve fouttoestand in de waardebron.",
    "0x04080000": "Er wordt een fouttoestand van de bestemming aangevraagd.",
    "0x04090000":
      "De waarde is nauwkeurig en de signaalbron ondersteunt cascade-handshaking.",
    "0x80E70000":
      "De DataSet die is opgegeven voor het maken van de DataSetWriter is ongeldig.",
  },
  pt: {
    "0x00000000": "A operação foi concluída com êxito.",
    "0x40000000": "A operação teve um resultado incerto.",
    "0x80000000": "A operação falhou.",
    "0x80010000": "Ocorreu um erro inesperado.",
    "0x80020000":
      "Ocorreu um erro interno como resultado de um erro de programação ou configuração.",
    "0x80030000": "Não há memória suficiente para concluir a operação.",
    "0x80040000": "Um recurso do sistema operacional não está disponível.",
    "0x80050000": "Ocorreu um erro de comunicação de baixo nível.",
    "0x80060000":
      "A codificação foi interrompida devido a dados inválidos nos objetos que estavam sendo serializados.",
    "0x80070000":
      "A decodificação foi interrompida devido a dados inválidos no fluxo.",
    "0x80080000":
      "Os limites de codificação/decodificação de mensagens impostos pela pilha foram excedidos.",
    "0x80B80000":
      "O tamanho da mensagem de solicitação excede os limites definidos pelo servidor.",
    "0x80B90000":
      "O tamanho da mensagem de resposta excede os limites definidos pelo cliente ou servidor.",
    "0x80090000": "Foi recebida do servidor uma resposta não reconhecida.",
    "0x800A0000": "A operação excedeu o tempo limite.",
    "0x800B0000": "O servidor não oferece suporte ao serviço solicitado.",
    "0x800C0000":
      "A operação foi cancelada porque o aplicativo está sendo encerrado.",
    "0x800D0000":
      "A operação não pôde ser concluída porque o cliente não está conectado ao servidor.",
    "0x800E0000":
      "O servidor foi interrompido e não pode processar solicitações.",
    "0x800F0000":
      "Nenhum processamento pôde ser realizado porque não havia nada a fazer.",
    "0x80100000":
      "A solicitação não pôde ser processada porque especificou operações demais.",
    "0x80DB0000":
      "A solicitação não pôde ser processada porque há itens monitorados demais na assinatura.",
    "0x80110000":
      "O objeto de extensão não pode ser (des)serializado porque o ID do tipo de dados não é reconhecido.",
    "0x80120000": "O certificado fornecido como parâmetro não é válido.",
    "0x80130000": "Ocorreu um erro ao verificar a segurança.",
    "0x81140000":
      "O certificado não atende aos requisitos da política de segurança.",
    "0x80140000": "O certificado expirou ou ainda não é válido.",
    "0x80150000": "Um certificado do emissor expirou ou ainda não é válido.",
    "0x80160000":
      "O HostName usado para se conectar a um servidor não corresponde a nenhum HostName no certificado.",
    "0x80170000":
      "O URI especificado em ApplicationDescription não corresponde ao URI no certificado.",
    "0x80180000":
      "O certificado não pode ser usado para a operação solicitada.",
    "0x80190000":
      "O certificado do emissor não pode ser usado para a operação solicitada.",
    "0x801A0000": "O certificado não é confiável.",
    "0x801B0000": "Não foi possível determinar se o certificado foi revogado.",
    "0x801C0000":
      "Não foi possível determinar se o certificado do emissor foi revogado.",
    "0x801D0000": "O certificado foi revogado.",
    "0x801E0000": "O certificado do emissor foi revogado.",
    "0x810D0000": "A cadeia de certificados está incompleta.",
    "0x801F0000":
      "O usuário não tem permissão para executar a operação solicitada.",
    "0x80200000": "O token de identidade do usuário não é válido.",
    "0x80210000":
      "O token de identidade do usuário é válido, mas o servidor o rejeitou.",
    "0x80220000": "O canal seguro especificado não é mais válido.",
    "0x80230000": "O timestamp está fora do intervalo permitido pelo servidor.",
    "0x80240000":
      "O nonce parece não ser um valor aleatório ou não tem o comprimento correto.",
    "0x80250000": "O ID da sessão não é válido.",
    "0x80260000": "A sessão foi encerrada pelo cliente.",
    "0x80270000":
      "A sessão não pode ser usada porque ActivateSession não foi chamado.",
    "0x80280000": "O ID da assinatura não é válido.",
    "0x802A0000": "O cabeçalho da solicitação está ausente ou é inválido.",
    "0x802B0000": "O parâmetro de timestamps a retornar é inválido.",
    "0x802C0000": "A solicitação foi cancelada pelo cliente.",
    "0x80E50000": "Foram fornecidos argumentos demais.",
    "0x810E0000":
      "O servidor requer uma licença para operar em geral ou para executar um serviço ou operação, mas a licença existente expirou.",
    "0x810F0000":
      "O servidor tem limites para o número de operações/objetos permitidos, com base nas licenças instaladas, e esses limites foram excedidos.",
    "0x81100000":
      "O servidor não possui uma licença necessária para operar em geral ou para executar um serviço ou operação.",
    "0x80EE0000":
      "O servidor não possui recursos suficientes para processar a solicitação neste momento.",
    "0x00EF0000":
      "O login do usuário foi bem-sucedido, mas o usuário precisa alterar a senha.",
    "0x002D0000": "A assinatura foi transferida para outra sessão.",
    "0x002E0000": "O processamento será concluído de forma assíncrona.",
    "0x002F0000":
      "A amostragem ficou mais lenta devido a limitações de recursos.",
    "0x00300000":
      "O valor gravado foi aceito, mas foi limitado ao intervalo permitido.",
    "0x80310000":
      "A comunicação com a fonte de dados está definida, mas não estabelecida, e não há um último valor conhecido disponível.",
    "0x80320000":
      "Aguardando o servidor obter valores da fonte de dados subjacente.",
    "0x80330000":
      "A sintaxe do ID do nó não é válida ou faz referência a um nó que não é válido para a operação.",
    "0x80340000":
      "O ID do nó faz referência a um nó que não existe no espaço de endereçamento do servidor.",
    "0x80350000": "O atributo não é compatível com o Node especificado.",
    "0x80360000": "A sintaxe do parâmetro de intervalo de índices é inválida.",
    "0x80370000":
      "Não existem dados dentro do intervalo de índices especificado.",
    "0x80EA0000":
      "Os dados gravados não correspondem ao IndexRange especificado.",
    "0x80380000": "A codificação dos dados é inválida.",
    "0x80390000":
      "O servidor não oferece suporte à codificação de dados solicitada para o nó.",
    "0x803A0000":
      "O nível de acesso não permite leitura nem assinatura do Node.",
    "0x803B0000": "O nível de acesso não permite gravação no Node.",
    "0x803C0000": "O valor estava fora do intervalo.",
    "0x40F20000": "O valor está acima do intervalo permitido.",
    "0x40F30000": "O valor está abaixo do intervalo permitido.",
    "0x803D0000": "A operação solicitada não é suportada.",
    "0x803E0000":
      "Um item solicitado não foi encontrado ou uma operação de pesquisa terminou sem êxito.",
    "0x803F0000": "O objeto não pode ser usado porque foi excluído.",
    "0x80400000": "A operação solicitada não está implementada.",
    "0x80410000": "O modo de monitoramento é inválido.",
    "0x80420000":
      "O ID do item monitorado não faz referência a um item monitorado válido.",
    "0x80430000": "O parâmetro de filtro do item monitorado não é válido.",
    "0x80440000":
      "O servidor não oferece suporte ao filtro de item monitorado solicitado.",
    "0x80450000":
      "Um filtro de monitoramento não pode ser usado em combinação com o atributo especificado.",
    "0x80460000":
      "Um parâmetro estruturado obrigatório estava ausente ou era null.",
    "0x80470000": "O filtro de eventos não é válido.",
    "0x80480000": "O filtro de conteúdo não é válido.",
    "0x80C10000": "Foi fornecido um operador não reconhecido em um filtro.",
    "0x80C20000":
      "Foi fornecido um operador válido, mas o servidor não oferece suporte a esse operador de filtro.",
    "0x80C30000":
      "O número de operandos fornecidos para o operador de filtro era menor do que o esperado para o operando fornecido.",
    "0x80490000": "O operando usado em um filtro de conteúdo não é válido.",
    "0x80C40000":
      "O elemento referenciado não é um elemento válido no filtro de conteúdo.",
    "0x80C50000": "O literal referenciado não é um valor válido.",
    "0x804A0000": "O ponto de continuação fornecido não é mais válido.",
    "0x804B0000":
      "A operação não pôde ser processada porque todos os pontos de continuação foram alocados.",
    "0x804C0000":
      "O ID do tipo de referência não faz referência a um nó de tipo de referência válido.",
    "0x804D0000": "A direção de browse não é válida.",
    "0x804E0000": "O nó não faz parte da visualização.",
    "0x81120000": "O número não foi aceito devido a um estouro numérico.",
    "0x80ED0000":
      "A localidade na operação de gravação solicitada não é suportada.",
    "0x80F00000": "A variável não possui valor padrão nem valor inicial.",
    "0x804F0000": "ServerUri não é um URI válido.",
    "0x80500000": "Nenhum ServerName foi especificado.",
    "0x80510000": "Nenhum DiscoveryUrl foi especificado.",
    "0x80520000":
      "O arquivo de semáforo especificado pelo cliente não é válido.",
    "0x80530000": "O tipo de solicitação do token de segurança não é válido.",
    "0x80540000":
      "O modo de segurança não atende aos requisitos definidos pelo servidor.",
    "0x80550000":
      "A política de segurança não atende aos requisitos definidos pelo servidor.",
    "0x80560000": "O servidor atingiu o número máximo de sessões.",
    "0x80570000":
      "A assinatura do token do usuário está ausente ou é inválida.",
    "0x80580000":
      "A assinatura gerada com o certificado do cliente está ausente ou é inválida.",
    "0x80590000":
      "O cliente não forneceu pelo menos um certificado de software válido que atenda aos requisitos de perfil do servidor.",
    "0x80C60000":
      "O servidor não oferece suporte à alteração da identidade do usuário atribuída à sessão.",
    "0x805A0000":
      "A solicitação foi cancelada pelo cliente usando o serviço Cancel.",
    "0x805B0000": "O ID do nó pai não faz referência a um nó válido.",
    "0x805C0000":
      "A referência não pôde ser criada porque viola restrições impostas pelo modelo de dados.",
    "0x805D0000":
      "O ID de nó solicitado foi rejeitado porque era inválido ou porque o servidor não permite que IDs de nó sejam especificados pelo cliente.",
    "0x805E0000": "O ID de nó solicitado já está sendo usado por outro nó.",
    "0x805F0000": "A classe do nó não é válida.",
    "0x80600000": "O nome de browse não é válido.",
    "0x80610000":
      "O nome de browse não é exclusivo entre nós que compartilham a mesma relação com o nó pai.",
    "0x80620000": "Os atributos do nó não são válidos para a classe do nó.",
    "0x80630000":
      "O ID de nó da definição de tipo não faz referência a um nó de tipo apropriado.",
    "0x80640000": "O ID do nó de origem não faz referência a um nó válido.",
    "0x80650000": "O ID do nó de destino não faz referência a um nó válido.",
    "0x80660000": "O tipo de referência entre os nós já está definido.",
    "0x80670000":
      "O servidor não permite esse tipo de autorreferência nesse nó.",
    "0x80680000":
      "O tipo de referência não é válido para uma referência a um servidor remoto.",
    "0x80690000": "O servidor não permitirá que o nó seja excluído.",
    "0x40BC0000":
      "O servidor não conseguiu excluir todas as referências de destino.",
    "0x806A0000": "O índice do servidor não é válido.",
    "0x806B0000":
      "O ID da visualização não faz referência a um nó de visualização válido.",
    "0x80C90000":
      "O timestamp da visualização não está disponível ou não é suportado.",
    "0x80CA0000":
      "Os parâmetros da visualização não são consistentes entre si.",
    "0x80CB0000":
      "A versão da visualização não está disponível ou não é suportada.",
    "0x40C00000":
      "A lista de referências pode estar incompleta porque o sistema subjacente não está disponível.",
    "0x00BA0000":
      "O servidor deveria ter seguido uma referência para um nó em um servidor remoto, mas não o fez. O conjunto de resultados pode estar incompleto.",
    "0x80C80000": "O Nodeid fornecido não era um Nodeid de definição de tipo.",
    "0x406C0000":
      "Uma das referências a seguir no caminho relativo faz referência a um nó no espaço de endereçamento de outro servidor.",
    "0x806D0000":
      "A operação solicitada possui correspondências demais para retornar.",
    "0x806E0000": "A operação solicitada requer recursos demais no servidor.",
    "0x806F0000":
      "A operação solicitada não possui nenhuma correspondência para retornar.",
    "0x80700000": "O parâmetro de idade máxima é inválido.",
    "0x80E60000": "A operação não é permitida pelo canal seguro atual.",
    "0x80710000": "O parâmetro de detalhes históricos não é válido.",
    "0x80720000": "O servidor não oferece suporte à operação solicitada.",
    "0x80BD0000": "O timestamp definido para retornar era inválido.",
    "0x80730000":
      "O servidor não oferece suporte à gravação da combinação fornecida de valor, status e timestamps.",
    "0x80740000":
      "O valor fornecido para o atributo não é do mesmo tipo que o valor do atributo.",
    "0x80750000":
      "O ID do método não faz referência a um método para o objeto especificado.",
    "0x80760000":
      "O cliente não especificou todos os argumentos de entrada do método.",
    "0x81110000": "O atributo executável não permite a execução do método.",
    "0x80770000": "O servidor atingiu o número máximo de assinaturas.",
    "0x80780000":
      "O servidor atingiu o número máximo de solicitações Publish enfileiradas.",
    "0x80790000": "Não há nenhuma assinatura disponível para esta sessão.",
    "0x807A0000": "O número de sequência é desconhecido para o servidor.",
    "0x00DF0000":
      "O servidor não oferece suporte à fila de retransmissão e a confirmação de números de sequência não está disponível.",
    "0x807B0000":
      "A mensagem de notificação solicitada não está mais disponível.",
    "0x807C0000":
      "O cliente da sessão atual não oferece suporte a um ou mais Profiles necessários para a assinatura.",
    "0x80BF0000": "A máquina de subestados não está ativa no momento.",
    "0x81150000": "Uma regra equivalente já existe.",
    "0x807D0000":
      "O servidor não pode processar a solicitação porque está ocupado demais.",
    "0x807E0000": "O tipo de mensagem especificado no cabeçalho é inválido.",
    "0x807F0000": "SecureChannelId e/ou TokenId não estão em uso no momento.",
    "0x80800000":
      "O tamanho do fragmento da mensagem especificado no cabeçalho é grande demais.",
    "0x80810000": "Não há recursos suficientes para processar a solicitação.",
    "0x80820000": "Ocorreu um erro interno.",
    "0x80830000": "O servidor não reconhece a QueryString especificada.",
    "0x80840000":
      "A solicitação não pôde ser enviada devido a uma interrupção de rede.",
    "0x80850000":
      "Ocorreu um tempo limite durante o processamento da solicitação.",
    "0x80860000": "O canal seguro foi fechado.",
    "0x80870000": "O token expirou ou não é reconhecido.",
    "0x80880000": "O número de sequência não é válido.",
    "0x80BE0000":
      "Os aplicativos não possuem versões de protocolo compatíveis.",
    "0x80890000":
      "Há um problema com a configuração que afeta a utilidade do valor.",
    "0x808A0000":
      "A variável deveria receber seu valor de outra variável, mas nunca foi configurada para isso.",
    "0x808B0000":
      "Ocorreu uma falha no dispositivo/fonte de dados que gera o valor, afetando o valor.",
    "0x808C0000":
      "Ocorreu uma falha no sensor do qual o valor é derivado pelo dispositivo/fonte de dados.",
    "0x808D0000": "A fonte dos dados não está operacional.",
    "0x808E0000": "O filtro de banda morta não é válido.",
    "0x408F0000":
      "A comunicação com a fonte de dados falhou. O valor da variável é o último valor que tinha qualidade Good.",
    "0x40900000": "O componente que atualizava esse valor parou de fazê-lo.",
    "0x40910000":
      "O valor é um valor operacional que foi sobrescrito manualmente.",
    "0x40920000":
      "O valor é um valor inicial para uma variável que normalmente recebe seu valor de outra variável.",
    "0x40930000": "O valor está em um dos limites do sensor.",
    "0x40940000":
      "O valor está fora do intervalo de valores definido para este parâmetro.",
    "0x40950000":
      "O valor dos dados é derivado de várias fontes e possui menos fontes Good do que o número necessário.",
    "0x00960000": "O valor foi sobrescrito.",
    "0x00EB0000":
      "O valor é derivado de várias fontes e possui o número necessário de fontes Good, mas menos do que o número total de fontes Good.",
    "0x80970000":
      "Esta atualização da Condition falhou; uma operação de atualização da Condition já está em andamento.",
    "0x80980000": "Esta Condition já foi desabilitada.",
    "0x80CC0000": "Esta Condition já foi habilitada.",
    "0x80990000":
      "Propriedade não disponível; esta Condition está desabilitada.",
    "0x809A0000": "O ID de evento especificado não é reconhecido.",
    "0x80BB0000": "O evento não pode ser reconhecido.",
    "0x80CD0000": "A Condition do diálogo não está ativa.",
    "0x80CE0000": "A resposta não é válida para o diálogo.",
    "0x80CF0000": "O ramo da Condition já foi reconhecido.",
    "0x80D00000": "O ramo da Condition já foi confirmado.",
    "0x80D10000": "A Condition já foi suspensa.",
    "0x80D20000": "A Condition não está suspensa no momento.",
    "0x80D30000":
      "O tempo de suspensão não está dentro de um intervalo aceitável.",
    "0x809B0000":
      "Não existem dados para o intervalo de tempo ou filtro de eventos solicitado.",
    "0x80D70000":
      "Nenhum dado foi encontrado para fornecer um valor de limite superior ou inferior.",
    "0x80D80000": "O servidor não pode recuperar um limite para a variável.",
    "0x809D0000":
      "Há dados ausentes porque a coleta foi iniciada, interrompida ou perdida.",
    "0x809E0000":
      "Os dados esperados não estão disponíveis para o intervalo de tempo solicitado devido a um volume não montado, um arquivo ou fita offline ou motivo semelhante de indisponibilidade temporária.",
    "0x809F0000":
      "Os dados ou o evento não foram inseridos com êxito porque existe uma entrada correspondente.",
    "0x80A00000":
      "Os dados ou o evento não foram atualizados com êxito porque não existe uma entrada correspondente.",
    "0x80A10000":
      "O cliente solicitou histórico usando um TimestampsToReturn que o servidor não oferece suporte.",
    "0x00A20000":
      "Os dados ou o evento foram inseridos com êxito no banco de dados histórico.",
    "0x00A30000":
      "O campo de dados ou evento foi substituído com êxito no banco de dados histórico.",
    "0x40A40000":
      "O valor agregado é derivado de vários valores e possui menos valores Good do que o número necessário.",
    "0x00A50000":
      "Não existem dados para o intervalo de tempo ou filtro de eventos solicitado.",
    "0x00A60000":
      "Há mais dados disponíveis no intervalo de tempo além do número de valores solicitados.",
    "0x80D40000":
      "O número solicitado de Aggregates não corresponde ao número solicitado de NodeIds.",
    "0x80D50000": "O Aggregate solicitado não é suportado pelo servidor.",
    "0x80D60000":
      "O valor agregado não pôde ser derivado devido a entradas de dados inválidas.",
    "0x80DA0000":
      "A configuração do agregado não é válida para o nó especificado.",
    "0x00D90000":
      "A solicitação especifica campos que não são válidos para o EventType ou que não podem ser salvos pelo historian.",
    "0x80E40000":
      "A solicitação foi rejeitada pelo servidor porque não atendeu aos critérios definidos pelo servidor.",
    "0x81130000": "A solicitação ainda não foi processada pelo servidor.",
    "0x80E80000":
      "A operação não é permitida porque há uma transação em andamento.",
    "0x80F10000":
      "A operação falhou e todas as alterações que faziam parte da transação foram revertidas.",
    "0x811F0000":
      "A identidade do dispositivo precisa de um ticket antes de poder ser aceita.",
    "0x81200000":
      "A identidade do dispositivo precisa de um ticket antes de poder ser aceita.",
    "0x80E90000":
      "A operação solicitada não é permitida porque o Node está bloqueado por outro aplicativo.",
    "0x80EC0000":
      "A operação solicitada não é permitida porque o Node não está bloqueado pelo aplicativo.",
    "0x00DC0000": "O valor não vem da fonte real e foi editado pelo servidor.",
    "0x00DD0000": "Ocorreu um erro durante a execução dessas pós-ações.",
    "0x40DE0000":
      "A EngineeringUnit relacionada foi alterada, mas o valor da Variable ainda é fornecido com base na unidade anterior.",
    "0x00E00000":
      "Um valor dependente foi alterado, mas a alteração não foi aplicada ao dispositivo.",
    "0x80E10000":
      "A EngineeringUnit relacionada foi alterada, mas essa alteração não foi aplicada ao dispositivo. O valor da Variable ainda depende da unidade anterior, mas seu status atual é Bad.",
    "0x40E20000":
      "Um valor dependente foi alterado, mas a alteração não foi aplicada ao dispositivo. A qualidade da variável dominante é incerta.",
    "0x80E30000":
      "Um valor dependente foi alterado, mas a alteração não foi aplicada ao dispositivo. A qualidade da variável dominante é Bad.",
    "0x01160000":
      "É fornecido com um valor de Variable dominante quando uma Variable dependente foi alterada, mas a alteração não foi aplicada.",
    "0x01170000":
      "É fornecido com um valor de Variable dependente quando uma Variable dominante foi alterada, mas a alteração não foi aplicada.",
    "0x01180000":
      "É fornecido com um valor de Variable dependente quando uma Variable dominante ou dependente foi alterada, mas a alteração não foi aplicada.",
    "0x81190000":
      "É fornecido com um valor de Variable quando a Variable foi alterada, mas o valor não é válido.",
    "0x811A0000":
      "É fornecido com um valor de Variable quando uma Variable de origem foi alterada, mas o valor não é válido.",
    "0x811B0000":
      "É fornecido com um valor de Variable dependente quando uma Variable dominante foi alterada e o valor não é válido.",
    "0x811C0000":
      "É fornecido com um valor de Variable dependente quando uma Variable dominante foi alterada, o valor não é válido e a alteração não foi aplicada.",
    "0x811D0000":
      "É fornecido com um valor de Variable dependente quando uma Variable dominante ou dependente foi alterada e o valor não é válido.",
    "0x811E0000":
      "É fornecido com um valor de Variable dependente quando uma Variable dominante ou dependente foi alterada, o valor não é válido e a alteração não foi aplicada.",
    "0x00A70000": "A camada de comunicação gerou um evento.",
    "0x00A80000": "O sistema está sendo encerrado.",
    "0x00A90000": "A operação não terminou e precisa ser chamada novamente.",
    "0x00AA0000": "Ocorreu um tempo limite não crítico.",
    "0x80AB0000": "Um ou mais argumentos são inválidos.",
    "0x80AC0000":
      "Não foi possível estabelecer uma conexão de rede com o servidor remoto.",
    "0x80AD0000": "O servidor se desconectou do cliente.",
    "0x80AE0000": "A conexão de rede foi fechada.",
    "0x80AF0000":
      "A operação não pode ser concluída porque o objeto está fechado, não inicializado ou em algum outro estado inválido.",
    "0x80B00000": "Não é possível avançar além do fim do fluxo.",
    "0x80B10000":
      "No momento, não há dados disponíveis para leitura em um fluxo não bloqueante.",
    "0x80B20000": "A operação assíncrona está aguardando uma resposta.",
    "0x80B30000": "A operação assíncrona foi abandonada pelo chamador.",
    "0x80B40000":
      "O fluxo não retornou todos os dados solicitados, possivelmente porque é um fluxo não bloqueante.",
    "0x80B50000":
      "É necessário comportamento não bloqueante e a operação bloquearia.",
    "0x80B60000": "Um valor tinha uma sintaxe inválida.",
    "0x80B70000":
      "A operação não pôde ser concluída porque todas as conexões disponíveis estão em uso.",
    "0x42080000":
      "O valor pode não ser preciso porque o transdutor está em modo manual.",
    "0x42090000": "O valor é simulado.",
    "0x420A0000":
      "O valor pode não ser preciso devido a uma falha de calibração do sensor.",
    "0x420F0000":
      "O valor pode não ser preciso devido a um problema de configuração.",
    "0x04010000":
      "A fonte do valor oferece suporte a handshake em cascata e o valor foi inicializado com base em uma solicitação de inicialização de um secundário da cascata.",
    "0x04020000":
      "A fonte do valor oferece suporte a handshake em cascata e está solicitando a inicialização de um primário da cascata.",
    "0x04030000":
      "A fonte do valor oferece suporte a handshake em cascata; no entanto, o estado atual da fonte não permite cascata.",
    "0x04040000":
      "A fonte do valor oferece suporte a handshake em cascata; no entanto, a fonte ainda não selecionou o primário de cascata correspondente para uso.",
    "0x04070000": "Há uma condição de estado de falha ativa na fonte do valor.",
    "0x04080000":
      "Uma condição de estado de falha está sendo solicitada ao destino.",
    "0x04090000":
      "O valor é preciso e a fonte do sinal oferece suporte a handshake em cascata.",
    "0x80E70000":
      "O DataSet especificado para a criação do DataSetWriter é inválido.",
  },
  zh: {
    "0x00000000": "操作成功。",
    "0x40000000": "操作结果不确定。",
    "0x80000000": "操作失败。",
    "0x80010000": "发生了意外错误。",
    "0x80020000": "由于编程或配置错误，发生了内部错误。",
    "0x80030000": "内存不足，无法完成操作。",
    "0x80040000": "操作系统资源不可用。",
    "0x80050000": "发生了底层通信错误。",
    "0x80060000": "由于被序列化对象中的数据无效，编码已停止。",
    "0x80070000": "由于数据流中的数据无效，解码已停止。",
    "0x80080000": "已超过协议栈规定的消息编码/解码限制。",
    "0x80B80000": "请求消息大小超过了服务器设定的限制。",
    "0x80B90000": "响应消息大小超过了客户端或服务器设定的限制。",
    "0x80090000": "从服务器收到了无法识别的响应。",
    "0x800A0000": "操作超时。",
    "0x800B0000": "服务器不支持所请求的服务。",
    "0x800C0000": "由于应用程序正在关闭，操作已取消。",
    "0x800D0000": "由于客户端未连接到服务器，操作无法完成。",
    "0x800E0000": "服务器已停止，无法处理任何请求。",
    "0x800F0000": "由于没有可执行的工作，未进行任何处理。",
    "0x80100000": "由于请求指定了过多操作，无法处理该请求。",
    "0x80DB0000": "由于订阅中包含过多受监控项，无法处理该请求。",
    "0x80110000": "由于无法识别数据类型 ID，扩展对象无法进行（反）序列化。",
    "0x80120000": "作为参数提供的证书无效。",
    "0x80130000": "验证安全性时发生错误。",
    "0x81140000": "证书不符合安全策略的要求。",
    "0x80140000": "证书已过期或尚未生效。",
    "0x80150000": "颁发者证书已过期或尚未生效。",
    "0x80160000":
      "用于连接服务器的 HostName 与证书中的任何 HostName 均不匹配。",
    "0x80170000": "ApplicationDescription 中指定的 URI 与证书中的 URI 不匹配。",
    "0x80180000": "该证书不能用于所请求的操作。",
    "0x80190000": "颁发者证书不能用于所请求的操作。",
    "0x801A0000": "该证书不受信任。",
    "0x801B0000": "无法确定该证书是否已被吊销。",
    "0x801C0000": "无法确定颁发者证书是否已被吊销。",
    "0x801D0000": "该证书已被吊销。",
    "0x801E0000": "颁发者证书已被吊销。",
    "0x810D0000": "证书链不完整。",
    "0x801F0000": "用户无权执行所请求的操作。",
    "0x80200000": "用户身份令牌无效。",
    "0x80210000": "用户身份令牌有效，但服务器已拒绝它。",
    "0x80220000": "指定的安全通道已不再有效。",
    "0x80230000": "时间戳超出了服务器允许的范围。",
    "0x80240000": "Nonce 看起来不是随机值，或其长度不正确。",
    "0x80250000": "会话 ID 无效。",
    "0x80260000": "会话已由客户端关闭。",
    "0x80270000": "由于尚未调用 ActivateSession，无法使用该会话。",
    "0x80280000": "订阅 ID 无效。",
    "0x802A0000": "请求头缺失或无效。",
    "0x802B0000": "返回时间戳参数无效。",
    "0x802C0000": "请求已由客户端取消。",
    "0x80E50000": "提供的参数过多。",
    "0x810E0000":
      "服务器通常运行或执行某个服务/操作需要许可证，但现有许可证已过期。",
    "0x810F0000":
      "服务器根据已安装的许可证对允许的操作/对象数量有限制，并且这些限制已被超过。",
    "0x81100000": "服务器没有通常运行或执行某个服务/操作所需的许可证。",
    "0x80EE0000": "服务器当前没有足够资源处理该请求。",
    "0x00EF0000": "用户登录成功，但必须更改密码。",
    "0x002D0000": "订阅已转移到另一个会话。",
    "0x002E0000": "处理将异步完成。",
    "0x002F0000": "由于资源限制，采样速度已降低。",
    "0x00300000": "写入的值已被接受，但被限制在允许范围内。",
    "0x80310000": "与数据源的通信已定义但尚未建立，并且没有可用的最后已知值。",
    "0x80320000": "正在等待服务器从底层数据源获取值。",
    "0x80330000": "Node ID 的语法无效，或其引用的节点对该操作无效。",
    "0x80340000": "Node ID 引用了服务器地址空间中不存在的节点。",
    "0x80350000": "指定的 Node 不支持该属性。",
    "0x80360000": "索引范围参数的语法无效。",
    "0x80370000": "指定的索引范围内不存在数据。",
    "0x80EA0000": "写入的数据与指定的 IndexRange 不匹配。",
    "0x80380000": "数据编码无效。",
    "0x80390000": "服务器不支持该节点所请求的数据编码。",
    "0x803A0000": "访问级别不允许读取或订阅该 Node。",
    "0x803B0000": "访问级别不允许写入该 Node。",
    "0x803C0000": "该值超出范围。",
    "0x40F20000": "该值高于允许范围。",
    "0x40F30000": "该值低于允许范围。",
    "0x803D0000": "不支持所请求的操作。",
    "0x803E0000": "未找到请求的项目，或搜索操作未成功结束。",
    "0x803F0000": "由于对象已被删除，无法使用该对象。",
    "0x80400000": "所请求的操作尚未实现。",
    "0x80410000": "监控模式无效。",
    "0x80420000": "监控项 ID 未引用有效的受监控项。",
    "0x80430000": "受监控项过滤器参数无效。",
    "0x80440000": "服务器不支持所请求的受监控项过滤器。",
    "0x80450000": "监控过滤器不能与指定属性组合使用。",
    "0x80460000": "缺少必需的结构化参数，或该参数为 null。",
    "0x80470000": "事件过滤器无效。",
    "0x80480000": "内容过滤器无效。",
    "0x80C10000": "过滤器中提供了无法识别的运算符。",
    "0x80C20000": "提供了有效的运算符，但服务器不支持此过滤运算符。",
    "0x80C30000": "为过滤运算符提供的操作数数量少于该操作数所预期的数量。",
    "0x80490000": "内容过滤器中使用的操作数无效。",
    "0x80C40000": "所引用的元素不是内容过滤器中的有效元素。",
    "0x80C50000": "所引用的字面量不是有效值。",
    "0x804A0000": "提供的继续点已不再有效。",
    "0x804B0000": "由于所有继续点都已分配，无法处理该操作。",
    "0x804C0000": "引用类型 ID 未引用有效的引用类型节点。",
    "0x804D0000": "浏览方向无效。",
    "0x804E0000": "该节点不属于该视图。",
    "0x81120000": "由于数值溢出，该数字未被接受。",
    "0x80ED0000": "请求的写操作中的区域设置不受支持。",
    "0x80F00000": "该变量既没有默认值，也没有初始值。",
    "0x804F0000": "ServerUri 不是有效的 URI。",
    "0x80500000": "未指定 ServerName。",
    "0x80510000": "未指定 DiscoveryUrl。",
    "0x80520000": "客户端指定的信号量文件无效。",
    "0x80530000": "安全令牌请求类型无效。",
    "0x80540000": "安全模式不符合服务器设定的要求。",
    "0x80550000": "安全策略不符合服务器设定的要求。",
    "0x80560000": "服务器已达到最大会话数。",
    "0x80570000": "用户令牌签名缺失或无效。",
    "0x80580000": "使用客户端证书生成的签名缺失或无效。",
    "0x80590000":
      "客户端未提供至少一个有效且符合服务器配置文件要求的软件证书。",
    "0x80C60000": "服务器不支持更改分配给会话的用户身份。",
    "0x805A0000": "客户端通过 Cancel 服务取消了该请求。",
    "0x805B0000": "父节点 ID 未引用有效节点。",
    "0x805C0000": "由于违反了数据模型施加的约束，无法创建该引用。",
    "0x805D0000":
      "请求的 Node ID 被拒绝，因为它无效，或服务器不允许客户端指定 Node ID。",
    "0x805E0000": "请求的 Node ID 已被另一个节点使用。",
    "0x805F0000": "节点类无效。",
    "0x80600000": "浏览名称无效。",
    "0x80610000": "在与父节点具有相同关系的节点中，浏览名称不唯一。",
    "0x80620000": "节点属性对该节点类无效。",
    "0x80630000": "类型定义 Node ID 未引用适当的类型节点。",
    "0x80640000": "源 Node ID 未引用有效节点。",
    "0x80650000": "目标 Node ID 未引用有效节点。",
    "0x80660000": "节点之间的引用类型已定义。",
    "0x80670000": "服务器不允许该节点使用这种自引用类型。",
    "0x80680000": "该引用类型不适用于指向远程服务器的引用。",
    "0x80690000": "服务器不允许删除该节点。",
    "0x40BC0000": "服务器无法删除所有目标引用。",
    "0x806A0000": "服务器索引无效。",
    "0x806B0000": "视图 ID 未引用有效的视图节点。",
    "0x80C90000": "视图时间戳不可用或不受支持。",
    "0x80CA0000": "视图参数彼此不一致。",
    "0x80CB0000": "视图版本不可用或不受支持。",
    "0x40C00000": "由于底层系统不可用，引用列表可能不完整。",
    "0x00BA0000":
      "服务器本应跟随指向远程服务器中节点的引用，但未这样做。结果集可能不完整。",
    "0x80C80000": "提供的 Nodeid 不是类型定义 Nodeid。",
    "0x406C0000":
      "相对路径中要跟随的某个引用指向另一台服务器地址空间中的节点。",
    "0x806D0000": "所请求的操作匹配项过多，无法返回。",
    "0x806E0000": "所请求的操作需要服务器上的过多资源。",
    "0x806F0000": "所请求的操作没有可返回的匹配项。",
    "0x80700000": "最大年龄参数无效。",
    "0x80E60000": "当前安全通道不允许执行该操作。",
    "0x80710000": "历史详细信息参数无效。",
    "0x80720000": "服务器不支持所请求的操作。",
    "0x80BD0000": "定义的返回时间戳无效。",
    "0x80730000": "服务器不支持写入所提供的值、状态和时间戳组合。",
    "0x80740000": "为该属性提供的值与该属性值的类型不同。",
    "0x80750000": "方法 ID 未引用指定对象的方法。",
    "0x80760000": "客户端未指定该方法的所有输入参数。",
    "0x81110000": "可执行属性不允许执行该方法。",
    "0x80770000": "服务器已达到最大订阅数。",
    "0x80780000": "服务器已达到排队的 Publish 请求最大数量。",
    "0x80790000": "此会话没有可用订阅。",
    "0x807A0000": "服务器不知道该序列号。",
    "0x00DF0000": "服务器不支持重传队列，并且无法确认序列号。",
    "0x807B0000": "请求的通知消息已不再可用。",
    "0x807C0000": "当前会话的客户端不支持订阅所需的一个或多个 Profile。",
    "0x80BF0000": "子状态机当前未激活。",
    "0x81150000": "已存在等效规则。",
    "0x807D0000": "服务器过于繁忙，无法处理该请求。",
    "0x807E0000": "请求头中指定的消息类型无效。",
    "0x807F0000": "SecureChannelId 和/或 TokenId 当前未被使用。",
    "0x80800000": "请求头中指定的消息块大小过大。",
    "0x80810000": "没有足够资源处理该请求。",
    "0x80820000": "发生了内部错误。",
    "0x80830000": "服务器无法识别指定的 QueryString。",
    "0x80840000": "由于网络中断，无法发送该请求。",
    "0x80850000": "处理请求时发生超时。",
    "0x80860000": "安全通道已关闭。",
    "0x80870000": "令牌已过期或无法识别。",
    "0x80880000": "序列号无效。",
    "0x80BE0000": "应用程序的协议版本不兼容。",
    "0x80890000": "配置存在问题，影响该值的可用性。",
    "0x808A0000": "该变量应从另一个变量获取其值，但从未被配置为这样做。",
    "0x808B0000": "生成该值的设备/数据源发生故障，并影响了该值。",
    "0x808C0000": "设备/数据源用于推导该值的传感器发生故障。",
    "0x808D0000": "数据源未处于运行状态。",
    "0x808E0000": "死区过滤器无效。",
    "0x408F0000": "与数据源的通信已失败。变量值为最后一个质量良好的值。",
    "0x40900000": "负责更新该值的组件已停止更新。",
    "0x40910000": "该值是被手动覆盖的运行值。",
    "0x40920000": "该值是一个变量的初始值，而该变量通常从另一个变量获取其值。",
    "0x40930000": "该值处于某个传感器极限值。",
    "0x40940000": "该值超出了为此参数定义的值范围。",
    "0x40950000": "该数据值来自多个来源，但 Good 来源的数量少于要求。",
    "0x00960000": "该值已被覆盖。",
    "0x00EB0000":
      "该值来自多个来源，并具有要求数量的 Good 来源，但少于全部 Good 来源数量。",
    "0x80970000":
      "此次 Condition 刷新失败，因为已有另一个 Condition 刷新操作正在进行。",
    "0x80980000": "该 Condition 已被禁用。",
    "0x80CC0000": "该 Condition 已被启用。",
    "0x80990000": "属性不可用，因为该 Condition 已被禁用。",
    "0x809A0000": "无法识别指定的事件 ID。",
    "0x80BB0000": "该事件无法被确认。",
    "0x80CD0000": "对话框 Condition 未激活。",
    "0x80CE0000": "该响应对该对话框无效。",
    "0x80CF0000": "该 Condition 分支已被确认。",
    "0x80D00000": "该 Condition 分支已被确认完成。",
    "0x80D10000": "该 Condition 已被搁置。",
    "0x80D20000": "该 Condition 当前未被搁置。",
    "0x80D30000": "搁置时间不在可接受范围内。",
    "0x809B0000": "请求的时间范围或事件过滤器内不存在数据。",
    "0x80D70000": "未找到用于提供上限或下限值的数据。",
    "0x80D80000": "服务器无法检索该变量的边界值。",
    "0x809D0000": "由于采集启动、停止或丢失，数据缺失。",
    "0x809E0000":
      "由于卷未挂载、归档或磁带离线，或其他类似的临时不可用原因，请求时间范围内的预期数据不可用。",
    "0x809F0000": "由于存在匹配条目，数据或事件未成功插入。",
    "0x80A00000": "由于不存在匹配条目，数据或事件未成功更新。",
    "0x80A10000":
      "客户端使用服务器不支持的 TimestampsToReturn 请求了历史数据。",
    "0x00A20000": "数据或事件已成功插入历史数据库。",
    "0x00A30000": "数据或事件字段已成功在历史数据库中替换。",
    "0x40A40000": "聚合值来自多个值，但 Good 值数量少于要求。",
    "0x00A50000": "请求的时间范围或事件过滤器内不存在数据。",
    "0x00A60000": "该时间范围内还有超过请求数量的更多数据可用。",
    "0x80D40000": "请求的 Aggregates 数量与请求的 NodeIds 数量不匹配。",
    "0x80D50000": "服务器不支持所请求的 Aggregate。",
    "0x80D60000": "由于输入数据无效，无法计算聚合值。",
    "0x80DA0000": "聚合配置对指定节点无效。",
    "0x00D90000": "请求指定了对 EventType 无效或无法由 historian 保存的字段。",
    "0x80E40000": "由于请求不符合服务器设定的条件，服务器拒绝了该请求。",
    "0x81130000": "服务器尚未处理该请求。",
    "0x80E80000": "由于事务正在进行，不允许执行该操作。",
    "0x80F10000": "操作失败，事务中的所有更改均已回滚。",
    "0x811F0000": "设备身份需要票据后才能被接受。",
    "0x81200000": "设备身份需要票据后才能被接受。",
    "0x80E90000": "不允许执行所请求的操作，因为该 Node 已被其他应用程序锁定。",
    "0x80EC0000": "不允许执行所请求的操作，因为该 Node 尚未被该应用程序锁定。",
    "0x00DC0000": "该值并非来自真实数据源，而是已由服务器编辑。",
    "0x00DD0000": "执行这些后置操作时发生错误。",
    "0x40DE0000":
      "相关 EngineeringUnit 已更改，但 Variable 值仍基于之前的单位提供。",
    "0x00E00000": "某个依赖值已更改，但更改尚未应用到设备。",
    "0x80E10000":
      "相关 EngineeringUnit 已更改，但该更改尚未应用到设备。Variable 值仍依赖之前的单位，但其当前状态为 Bad。",
    "0x40E20000":
      "某个依赖值已更改，但更改尚未应用到设备。主导变量的质量为不确定。",
    "0x80E30000":
      "某个依赖值已更改，但更改尚未应用到设备。主导变量的质量为 Bad。",
    "0x01160000":
      "当依赖 Variable 已更改但更改尚未应用时，会随主导 Variable 值一起提供该状态。",
    "0x01170000":
      "当主导 Variable 已更改但更改尚未应用时，会随依赖 Variable 值一起提供该状态。",
    "0x01180000":
      "当主导或依赖 Variable 已更改但更改尚未应用时，会随依赖 Variable 值一起提供该状态。",
    "0x81190000":
      "当 Variable 已更改但该值不合法时，会随 Variable 值一起提供该状态。",
    "0x811A0000":
      "当源 Variable 已更改但该值不合法时，会随 Variable 值一起提供该状态。",
    "0x811B0000":
      "当主导 Variable 已更改且该值不合法时，会随依赖 Variable 值一起提供该状态。",
    "0x811C0000":
      "当主导 Variable 已更改、该值不合法且更改尚未应用时，会随依赖 Variable 值一起提供该状态。",
    "0x811D0000":
      "当主导或依赖 Variable 已更改且该值不合法时，会随依赖 Variable 值一起提供该状态。",
    "0x811E0000":
      "当主导或依赖 Variable 已更改、该值不合法且更改尚未应用时，会随依赖 Variable 值一起提供该状态。",
    "0x00A70000": "通信层已触发一个事件。",
    "0x00A80000": "系统正在关闭。",
    "0x00A90000": "操作尚未完成，需要再次调用。",
    "0x00AA0000": "发生了非关键性超时。",
    "0x80AB0000": "一个或多个参数无效。",
    "0x80AC0000": "无法与远程服务器建立网络连接。",
    "0x80AD0000": "服务器已断开与客户端的连接。",
    "0x80AE0000": "网络连接已关闭。",
    "0x80AF0000":
      "由于对象已关闭、未初始化或处于其他无效状态，无法完成该操作。",
    "0x80B00000": "无法移动到数据流末尾之后。",
    "0x80B10000": "当前没有可从非阻塞流中读取的数据。",
    "0x80B20000": "异步操作正在等待响应。",
    "0x80B30000": "调用方已放弃该异步操作。",
    "0x80B40000": "数据流未返回所有请求的数据（可能因为这是一个非阻塞流）。",
    "0x80B50000": "要求使用非阻塞行为，但该操作会造成阻塞。",
    "0x80B60000": "某个值的语法无效。",
    "0x80B70000": "由于所有可用连接都正在使用，无法完成该操作。",
    "0x42080000": "由于传感器处于手动模式，该值可能不准确。",
    "0x42090000": "该值为模拟值。",
    "0x420A0000": "由于传感器校准故障，该值可能不准确。",
    "0x420F0000": "由于配置问题，该值可能不准确。",
    "0x04010000":
      "值源支持级联握手，并已根据来自级联从级的初始化请求初始化该值。",
    "0x04020000": "值源支持级联握手，并正在请求初始化级联主级。",
    "0x04030000": "值源支持级联握手，但该源当前状态不允许级联。",
    "0x04040000": "值源支持级联握手，但该源尚未选择相应的级联主级供使用。",
    "0x04070000": "值源中存在活动的故障状态条件。",
    "0x04080000": "正在向目标请求一个故障状态条件。",
    "0x04090000": "该值是准确的，并且信号源支持级联握手。",
    "0x80E70000": "为创建 DataSetWriter 指定的 DataSet 无效。",
  },
  tr: {
    "0x00000000": "İşlem başarıyla tamamlandı.",
    "0x40000000": "İşlemin sonucu belirsizdi.",
    "0x80000000": "İşlem başarısız oldu.",
    "0x80010000": "Beklenmeyen bir hata oluştu.",
    "0x80020000":
      "Bir programlama veya yapılandırma hatası sonucunda dahili bir hata oluştu.",
    "0x80030000": "İşlemi tamamlamak için yeterli bellek yok.",
    "0x80040000": "Bir işletim sistemi kaynağı kullanılamıyor.",
    "0x80050000": "Düşük seviyeli bir iletişim hatası oluştu.",
    "0x80060000":
      "Serileştirilen nesnelerde geçersiz veri bulunduğu için kodlama durduruldu.",
    "0x80070000": "Akışta geçersiz veri bulunduğu için kod çözme durduruldu.",
    "0x80080000":
      "Yığın tarafından uygulanan mesaj kodlama/kod çözme sınırları aşıldı.",
    "0x80B80000":
      "İstek mesajının boyutu, sunucu tarafından belirlenen sınırları aşıyor.",
    "0x80B90000":
      "Yanıt mesajının boyutu, istemci veya sunucu tarafından belirlenen sınırları aşıyor.",
    "0x80090000": "Sunucudan tanınmayan bir yanıt alındı.",
    "0x800A0000": "İşlem zaman aşımına uğradı.",
    "0x800B0000": "Sunucu istenen hizmeti desteklemiyor.",
    "0x800C0000": "Uygulama kapandığı için işlem iptal edildi.",
    "0x800D0000": "İstemci sunucuya bağlı olmadığı için işlem tamamlanamadı.",
    "0x800E0000": "Sunucu durdu ve hiçbir isteği işleyemiyor.",
    "0x800F0000":
      "Yapılacak bir şey olmadığı için herhangi bir işlem gerçekleştirilemedi.",
    "0x80100000": "İstek çok fazla işlem belirttiği için işlenemedi.",
    "0x80DB0000":
      "Abonelikte çok fazla izlenen öğe olduğu için istek işlenemedi.",
    "0x80110000":
      "Veri türü kimliği tanınmadığı için extension object serileştirilemez veya seriden çıkarılamaz.",
    "0x80120000": "Parametre olarak sağlanan sertifika geçerli değil.",
    "0x80130000": "Güvenlik doğrulanırken bir hata oluştu.",
    "0x81140000":
      "Sertifika, güvenlik politikasının gereksinimlerini karşılamıyor.",
    "0x80140000": "Sertifikanın süresi dolmuş veya henüz geçerli değil.",
    "0x80150000":
      "Veren kuruluş sertifikasının süresi dolmuş veya henüz geçerli değil.",
    "0x80160000":
      "Sunucuya bağlanmak için kullanılan HostName, sertifikadaki HostName ile eşleşmiyor.",
    "0x80170000":
      "ApplicationDescription içinde belirtilen URI, sertifikadaki URI ile eşleşmiyor.",
    "0x80180000": "Sertifika istenen işlem için kullanılamaz.",
    "0x80190000": "Veren kuruluş sertifikası istenen işlem için kullanılamaz.",
    "0x801A0000": "Sertifikaya güvenilmiyor.",
    "0x801B0000": "Sertifikanın iptal edilip edilmediği belirlenemedi.",
    "0x801C0000":
      "Veren kuruluş sertifikasının iptal edilip edilmediği belirlenemedi.",
    "0x801D0000": "Sertifika iptal edilmiş.",
    "0x801E0000": "Veren kuruluş sertifikası iptal edilmiş.",
    "0x810D0000": "Sertifika zinciri eksik.",
    "0x801F0000": "Kullanıcının istenen işlemi gerçekleştirme izni yok.",
    "0x80200000": "Kullanıcı kimlik belirteci geçerli değil.",
    "0x80210000":
      "Kullanıcı kimlik belirteci geçerli ancak sunucu bunu reddetti.",
    "0x80220000": "Belirtilen güvenli kanal artık geçerli değil.",
    "0x80230000": "Zaman damgası, sunucunun izin verdiği aralığın dışında.",
    "0x80240000":
      "Nonce değeri rastgele görünmüyor veya doğru uzunlukta değil.",
    "0x80250000": "Oturum kimliği geçerli değil.",
    "0x80260000": "Oturum istemci tarafından kapatıldı.",
    "0x80270000": "ActivateSession çağrılmadığı için oturum kullanılamaz.",
    "0x80280000": "Abonelik kimliği geçerli değil.",
    "0x802A0000": "İstek başlığı eksik veya geçersiz.",
    "0x802B0000": "Döndürülecek zaman damgaları parametresi geçersiz.",
    "0x802C0000": "İstek istemci tarafından iptal edildi.",
    "0x80E50000": "Çok fazla argüman sağlandı.",
    "0x810E0000":
      "Sunucunun genel olarak çalışması veya bir hizmet ya da işlem gerçekleştirmesi için lisans gerekir, ancak mevcut lisansın süresi dolmuş.",
    "0x810F0000":
      "Sunucunun yüklü lisanslara bağlı olarak izin verilen işlem/nesne sayısı için sınırları vardır ve bu sınırlar aşılmıştır.",
    "0x81100000":
      "Sunucuda genel olarak çalışmak veya bir hizmet ya da işlem gerçekleştirmek için gereken lisans bulunmuyor.",
    "0x80EE0000":
      "Sunucu şu anda isteği işlemek için yeterli kaynağa sahip değil.",
    "0x00EF0000":
      "Kullanıcı oturum açtı ancak parolasını değiştirmesi gerekiyor.",
    "0x002D0000": "Abonelik başka bir oturuma aktarıldı.",
    "0x002E0000": "İşlem eşzamansız olarak tamamlanacak.",
    "0x002F0000": "Kaynak sınırlamaları nedeniyle örnekleme yavaşladı.",
    "0x00300000": "Yazılan değer kabul edildi ancak sınırlandırıldı.",
    "0x80310000":
      "Veri kaynağıyla iletişim tanımlı ancak kurulmamış ve kullanılabilir son bilinen değer yok.",
    "0x80320000":
      "Sunucunun temel veri kaynağından değerleri alması bekleniyor.",
    "0x80330000":
      "NodeId sözdizimi geçersiz veya işlem için geçerli olmayan bir düğüme başvuruyor.",
    "0x80340000":
      "NodeId, sunucu adres alanında bulunmayan bir düğüme başvuruyor.",
    "0x80350000": "Öznitelik belirtilen Node için desteklenmiyor.",
    "0x80360000": "Dizin aralığı parametresinin sözdizimi geçersiz.",
    "0x80370000": "Belirtilen dizin aralığında veri yok.",
    "0x80EA0000": "Yazılan veri belirtilen IndexRange ile eşleşmiyor.",
    "0x80380000": "Veri kodlaması geçersiz.",
    "0x80390000": "Sunucu, düğüm için istenen veri kodlamasını desteklemiyor.",
    "0x803A0000":
      "Erişim seviyesi Node'un okunmasına veya abone olunmasına izin vermiyor.",
    "0x803B0000": "Erişim seviyesi Node'a yazmaya izin vermiyor.",
    "0x803C0000": "Değer aralık dışındaydı.",
    "0x40F20000": "Değer izin verilen aralığın üzerinde.",
    "0x40F30000": "Değer izin verilen aralığın altında.",
    "0x803D0000": "İstenen işlem desteklenmiyor.",
    "0x803E0000":
      "İstenen öğe bulunamadı veya arama işlemi başarısız sonuçlandı.",
    "0x803F0000": "Nesne silindiği için kullanılamaz.",
    "0x80400000": "İstenen işlem uygulanmamış.",
    "0x80410000": "İzleme modu geçersiz.",
    "0x80420000": "İzlenen öğe kimliği geçerli bir izlenen öğeye başvurmuyor.",
    "0x80430000": "İzlenen öğe filtre parametresi geçerli değil.",
    "0x80440000": "Sunucu istenen izlenen öğe filtresini desteklemiyor.",
    "0x80450000":
      "Bir izleme filtresi belirtilen öznitelikle birlikte kullanılamaz.",
    "0x80460000": "Zorunlu yapılandırılmış bir parametre eksik veya null.",
    "0x80470000": "Olay filtresi geçerli değil.",
    "0x80480000": "İçerik filtresi geçerli değil.",
    "0x80C10000": "Filtrede tanınmayan bir operatör sağlandı.",
    "0x80C20000":
      "Geçerli bir operatör sağlandı ancak sunucu bu filtre operatörünü desteklemiyor.",
    "0x80C30000":
      "Filtre operatörü için sağlanan operand sayısı beklenenden az.",
    "0x80490000": "İçerik filtresinde kullanılan operand geçerli değil.",
    "0x80C40000": "Başvurulan öğe içerik filtresinde geçerli bir öğe değil.",
    "0x80C50000": "Başvurulan literal geçerli bir değer değil.",
    "0x804A0000": "Sağlanan continuation point artık geçerli değil.",
    "0x804B0000":
      "Tüm continuation point'ler tahsis edildiği için işlem gerçekleştirilemedi.",
    "0x804C0000":
      "Referans türü kimliği geçerli bir referans türü düğümüne başvurmuyor.",
    "0x804D0000": "Browse yönü geçerli değil.",
    "0x804E0000": "Düğüm görünümün bir parçası değil.",
    "0x81120000": "Sayısal taşma nedeniyle sayı kabul edilmedi.",
    "0x80ED0000": "İstenen yazma işlemindeki yerel ayar desteklenmiyor.",
    "0x80F00000": "Değişkenin varsayılan veya başlangıç değeri yok.",
    "0x804F0000": "ServerUri geçerli bir URI değil.",
    "0x80500000": "ServerName belirtilmedi.",
    "0x80510000": "DiscoveryUrl belirtilmedi.",
    "0x80520000":
      "İstemci tarafından belirtilen semaphore dosyası geçerli değil.",
    "0x80530000": "Güvenlik belirteci istek türü geçerli değil.",
    "0x80540000":
      "Güvenlik modu sunucu tarafından belirlenen gereksinimleri karşılamıyor.",
    "0x80550000":
      "Güvenlik politikası sunucu tarafından belirlenen gereksinimleri karşılamıyor.",
    "0x80560000": "Sunucu maksimum oturum sayısına ulaştı.",
    "0x80570000": "Kullanıcı belirteci imzası eksik veya geçersiz.",
    "0x80580000":
      "İstemci sertifikasıyla oluşturulan imza eksik veya geçersiz.",
    "0x80590000":
      "İstemci, geçerli ve sunucu profil gereksinimlerini karşılayan en az bir yazılım sertifikası sağlamadı.",
    "0x80C60000":
      "Sunucu, oturuma atanmış kullanıcı kimliğinin değiştirilmesini desteklemiyor.",
    "0x805A0000": "İstek, istemci tarafından Cancel hizmeti ile iptal edildi.",
    "0x805B0000": "Üst NodeId geçerli bir düğüme başvurmuyor.",
    "0x805C0000":
      "Referans, veri modeli tarafından uygulanan kısıtlamaları ihlal ettiği için oluşturulamadı.",
    "0x805D0000":
      "İstenen NodeId, geçersiz olduğu veya sunucu istemcinin NodeId belirtmesine izin vermediği için reddedildi.",
    "0x805E0000":
      "İstenen NodeId zaten başka bir düğüm tarafından kullanılıyor.",
    "0x805F0000": "Düğüm sınıfı geçerli değil.",
    "0x80600000": "Browse adı geçersiz.",
    "0x80610000":
      "Browse adı, üst düğümle aynı ilişkiyi paylaşan düğümler arasında benzersiz değil.",
    "0x80620000": "Düğüm öznitelikleri düğüm sınıfı için geçerli değil.",
    "0x80630000": "Tür tanımı NodeId uygun bir tür düğümüne başvurmuyor.",
    "0x80640000": "Kaynak NodeId geçerli bir düğüme başvurmuyor.",
    "0x80650000": "Hedef NodeId geçerli bir düğüme başvurmuyor.",
    "0x80660000": "Düğümler arasındaki referans türü zaten tanımlı.",
    "0x80670000": "Sunucu bu düğümde bu tür öz referansa izin vermiyor.",
    "0x80680000":
      "Referans türü uzak bir sunucuya yapılan referans için geçerli değil.",
    "0x80690000": "Sunucu düğümün silinmesine izin vermiyor.",
    "0x40BC0000": "Sunucu tüm hedef referansları silemedi.",
    "0x806A0000": "Sunucu dizini geçerli değil.",
    "0x806B0000": "Görünüm kimliği geçerli bir görünüm düğümüne başvurmuyor.",
    "0x80C90000": "Görünüm zaman damgası kullanılamıyor veya desteklenmiyor.",
    "0x80CA0000": "Görünüm parametreleri birbiriyle tutarlı değil.",
    "0x80CB0000": "Görünüm sürümü kullanılamıyor veya desteklenmiyor.",
    "0x40C00000":
      "Temel sistem kullanılamadığı için referans listesi eksik olabilir.",
    "0x00BA0000":
      "Sunucunun uzak bir sunucudaki düğüme olan referansı izlemesi gerekiyordu ancak izlemedi. Sonuç kümesi eksik olabilir.",
    "0x80C80000": "Sağlanan NodeId bir tür tanımı NodeId değildi.",
    "0x406C0000":
      "Göreli yolda izlenecek referanslardan biri başka bir sunucunun adres alanındaki bir düğüme başvuruyor.",
    "0x806D0000": "İstenen işlem döndürülemeyecek kadar çok eşleşmeye sahip.",
    "0x806E0000": "İstenen işlem sunucuda çok fazla kaynak gerektiriyor.",
    "0x806F0000": "İstenen işlem için döndürülecek eşleşme yok.",
    "0x80700000": "Maksimum yaş parametresi geçersiz.",
    "0x80E60000": "İşleme mevcut güvenli kanal üzerinden izin verilmiyor.",
    "0x80710000": "Geçmiş ayrıntıları parametresi geçerli değil.",
    "0x80720000": "Sunucu istenen işlemi desteklemiyor.",
    "0x80BD0000": "Döndürülecek tanımlı zaman damgası geçersizdi.",
    "0x80730000":
      "Sunucu sağlanan değer, durum ve zaman damgası birleşiminin yazılmasını desteklemiyor.",
    "0x80740000":
      "Öznitelik için sağlanan değer, özniteliğin değeriyle aynı türde değil.",
    "0x80750000":
      "Yöntem kimliği belirtilen nesne için bir yönteme başvurmuyor.",
    "0x80760000": "İstemci yöntem için tüm giriş argümanlarını belirtmedi.",
    "0x81110000":
      "Executable özniteliği yöntemin çalıştırılmasına izin vermiyor.",
    "0x80770000": "Sunucu maksimum abonelik sayısına ulaştı.",
    "0x80780000": "Sunucu kuyruktaki maksimum publish isteği sayısına ulaştı.",
    "0x80790000": "Bu oturum için kullanılabilir abonelik yok.",
    "0x807A0000": "Sıra numarası sunucu tarafından bilinmiyor.",
    "0x00DF0000":
      "Sunucu yeniden iletim kuyruğunu desteklemiyor ve sıra numaralarının onaylanması kullanılamıyor.",
    "0x807B0000": "İstenen bildirim mesajı artık kullanılamıyor.",
    "0x807C0000":
      "Geçerli oturumun istemcisi abonelik için gerekli olan bir veya daha fazla Profile'ı desteklemiyor.",
    "0x80BF0000": "Alt durum makinesi şu anda etkin değil.",
    "0x81150000": "Eşdeğer bir kural zaten mevcut.",
    "0x807D0000": "Sunucu çok meşgul olduğu için isteği işleyemiyor.",
    "0x807E0000": "Başlıkta belirtilen mesaj türü geçersiz.",
    "0x807F0000": "SecureChannelId ve/veya TokenId şu anda kullanımda değil.",
    "0x80800000": "Başlıkta belirtilen mesaj parçasının boyutu çok büyük.",
    "0x80810000": "İsteği işlemek için yeterli kaynak yok.",
    "0x80820000": "Dahili bir hata oluştu.",
    "0x80830000": "Sunucu belirtilen QueryString'i tanımıyor.",
    "0x80840000": "Ağ kesintisi nedeniyle istek gönderilemedi.",
    "0x80850000": "İstek işlenirken zaman aşımı oluştu.",
    "0x80860000": "Güvenli kanal kapatıldı.",
    "0x80870000": "Belirtecin süresi dolmuş veya tanınmıyor.",
    "0x80880000": "Sıra numarası geçerli değil.",
    "0x80BE0000": "Uygulamaların protokol sürümleri birbiriyle uyumlu değil.",
    "0x80890000":
      "Yapılandırmada, değerin kullanılabilirliğini etkileyen bir sorun var.",
    "0x808A0000":
      "Değişken değerini başka bir değişkenden almalı ancak bunu yapacak şekilde hiç yapılandırılmamış.",
    "0x808B0000":
      "Değeri üreten cihaz/veri kaynağında değeri etkileyen bir arıza oluştu.",
    "0x808C0000":
      "Cihazın/veri kaynağının değeri türettiği sensörde bir arıza oluştu.",
    "0x808D0000": "Veri kaynağı çalışır durumda değil.",
    "0x808E0000": "Deadband filtresi geçerli değil.",
    "0x408F0000":
      "Veri kaynağıyla iletişim başarısız oldu. Değişken değeri, iyi kaliteye sahip son değerdir.",
    "0x40900000": "Bu değeri güncelleyen kaynak güncellemeyi durdurdu.",
    "0x40910000":
      "Değer, manuel olarak üzerine yazılmış operasyonel bir değerdir.",
    "0x40920000":
      "Değer, normalde değerini başka bir değişkenden alan bir değişkenin başlangıç değeridir.",
    "0x40930000": "Değer sensör sınırlarından birinde.",
    "0x40940000":
      "Değer, bu parametre için tanımlanan değer aralığının dışında.",
    "0x40950000":
      "Veri değeri birden fazla kaynaktan türetiliyor ve gerekli sayıdan daha az Good kaynağa sahip.",
    "0x00960000": "Değerin üzerine yazıldı.",
    "0x00EB0000":
      "Değer birden fazla kaynaktan türetiliyor ve gerekli sayıda Good kaynağa sahip, ancak tüm Good kaynakların sayısından daha az.",
    "0x80970000":
      "Bu Condition yenilemesi başarısız oldu; zaten bir Condition yenileme işlemi devam ediyor.",
    "0x80980000": "Bu condition zaten devre dışı bırakılmış.",
    "0x80CC0000": "Bu condition zaten etkinleştirilmiş.",
    "0x80990000": "Özellik kullanılamıyor; bu condition devre dışı.",
    "0x809A0000": "Belirtilen olay kimliği tanınmıyor.",
    "0x80BB0000": "Olay onaylanamaz.",
    "0x80CD0000": "Dialog condition etkin değil.",
    "0x80CE0000": "Yanıt dialog için geçerli değil.",
    "0x80CF0000": "Condition dalı zaten onaylanmış.",
    "0x80D00000": "Condition dalı zaten doğrulanmış.",
    "0x80D10000": "Condition zaten shelve edilmiş.",
    "0x80D20000": "Condition şu anda shelve edilmiş değil.",
    "0x80D30000": "Shelving süresi kabul edilebilir aralıkta değil.",
    "0x809B0000": "İstenen zaman aralığı veya olay filtresi için veri yok.",
    "0x80D70000": "Üst veya alt sınır değerini sağlamak için veri bulunamadı.",
    "0x80D80000": "Sunucu değişken için sınır değeri alamıyor.",
    "0x809D0000":
      "Veri, toplama işleminin başlatılması/durdurulması/kaybedilmesi nedeniyle eksik.",
    "0x809E0000":
      "Beklenen veri; bağlanmamış bir birim, çevrimdışı arşiv veya teyp ya da benzer geçici kullanılamama nedeni yüzünden istenen zaman aralığı için kullanılamıyor.",
    "0x809F0000":
      "Eşleşen bir kayıt bulunduğu için veri veya olay başarıyla eklenemedi.",
    "0x80A00000":
      "Eşleşen kayıt bulunmadığı için veri veya olay başarıyla güncellenemedi.",
    "0x80A10000":
      "İstemci, sunucunun desteklemediği bir TimestampsToReturn kullanarak geçmiş verisi istedi.",
    "0x00A20000": "Veri veya olay geçmiş veritabanına başarıyla eklendi.",
    "0x00A30000":
      "Veri veya olay alanı geçmiş veritabanında başarıyla değiştirildi.",
    "0x40A40000":
      "Toplam değer birden fazla değerden türetiliyor ve gerekli sayıdan daha az Good değere sahip.",
    "0x00A50000": "İstenen zaman aralığı veya olay filtresi için veri yok.",
    "0x00A60000":
      "Zaman aralığında istenen değer sayısından daha fazla veri mevcut.",
    "0x80D40000":
      "İstenen Aggregate sayısı, istenen NodeId sayısıyla eşleşmiyor.",
    "0x80D50000": "İstenen Aggregate sunucu tarafından desteklenmiyor.",
    "0x80D60000":
      "Geçersiz veri girişleri nedeniyle aggregate değer türetilemedi.",
    "0x80DA0000":
      "Aggregate yapılandırması belirtilen düğüm için geçerli değil.",
    "0x00D90000":
      "İstek, EventType için geçerli olmayan veya historian tarafından kaydedilemeyen alanlar belirtiyor.",
    "0x80E40000":
      "İstek, sunucu tarafından belirlenen kriterleri karşılamadığı için reddedildi.",
    "0x81130000": "İstek sunucu tarafından henüz işlenmedi.",
    "0x80E80000": "Bir transaction devam ettiği için işleme izin verilmiyor.",
    "0x80F10000":
      "İşlem başarısız oldu ve transaction kapsamındaki tüm değişiklikler geri alındı.",
    "0x811F0000":
      "Cihaz kimliğinin kabul edilmeden önce bir ticket'a ihtiyacı var.",
    "0x81200000": "Cihaz kimliği bileti geçersiz.",
    "0x80E90000":
      "Node farklı bir uygulama tarafından kilitlendiği için istenen işleme izin verilmiyor.",
    "0x80EC0000":
      "Node uygulama tarafından kilitlenmediği için istenen işleme izin verilmiyor.",
    "0x00DC0000":
      "Değer gerçek kaynaktan gelmiyor ve sunucu tarafından düzenlenmiş.",
    "0x00DD0000": "Bu post-action'ların yürütülmesi sırasında hata oluştu.",
    "0x40DE0000":
      "İlgili EngineeringUnit değiştirildi ancak Variable Value hâlâ önceki birime göre sağlanıyor.",
    "0x00E00000":
      "Bağımlı bir değer değiştirildi ancak değişiklik cihaza uygulanmadı.",
    "0x80E10000":
      "İlgili EngineeringUnit değiştirildi ancak bu değişiklik cihaza uygulanmadı. Variable Value hâlâ önceki birime bağlı ancak durumu şu anda Bad.",
    "0x40E20000":
      "Bağımlı bir değer değiştirildi ancak değişiklik cihaza uygulanmadı. Baskın değişkenin kalitesi belirsiz.",
    "0x80E30000":
      "Bağımlı bir değer değiştirildi ancak değişiklik cihaza uygulanmadı. Baskın değişkenin kalitesi Bad.",
    "0x01160000":
      "Bağımlı bir Variable değiştiğinde ancak değişiklik uygulanmadığında baskın Variable değeriyle birlikte iletilir.",
    "0x01170000":
      "Baskın bir Variable değiştiğinde ancak değişiklik uygulanmadığında bağımlı Variable değeriyle birlikte iletilir.",
    "0x01180000":
      "Baskın veya bağımlı bir Variable değiştiğinde ancak değişiklik uygulanmadığında bağımlı Variable değeriyle birlikte iletilir.",
    "0x81190000":
      "Variable değiştiğinde ancak değer geçerli olmadığında Variable değeriyle birlikte iletilir.",
    "0x811A0000":
      "Kaynak Variable değiştiğinde ancak değer geçerli olmadığında Variable değeriyle birlikte iletilir.",
    "0x811B0000":
      "Baskın Variable değiştiğinde ve değer geçerli olmadığında bağımlı Variable değeriyle birlikte iletilir.",
    "0x811C0000":
      "Baskın Variable değiştiğinde, değer geçerli olmadığında ve değişiklik uygulanmadığında bağımlı Variable değeriyle birlikte iletilir.",
    "0x811D0000":
      "Baskın veya bağımlı Variable değiştiğinde ve değer geçerli olmadığında bağımlı Variable değeriyle birlikte iletilir.",
    "0x811E0000":
      "Baskın veya bağımlı Variable değiştiğinde, değer geçerli olmadığında ve değişiklik uygulanmadığında bağımlı Variable değeriyle birlikte iletilir.",
    "0x00A70000": "İletişim katmanı bir olay oluşturdu.",
    "0x00A80000": "Sistem kapanıyor.",
    "0x00A90000": "İşlem tamamlanmadı ve yeniden çağrılması gerekiyor.",
    "0x00AA0000": "Kritik olmayan bir zaman aşımı oluştu.",
    "0x80AB0000": "Bir veya daha fazla argüman geçersiz.",
    "0x80AC0000": "Uzak sunucuyla ağ bağlantısı kurulamadı.",
    "0x80AD0000": "Sunucu istemci bağlantısını kesti.",
    "0x80AE0000": "Ağ bağlantısı kapatıldı.",
    "0x80AF0000":
      "Nesne kapalı, başlatılmamış veya başka bir geçersiz durumda olduğu için işlem tamamlanamıyor.",
    "0x80B00000": "Akışın sonunun ötesine geçilemez.",
    "0x80B10000": "Non-blocking bir akıştan okumak için şu anda veri yok.",
    "0x80B20000": "Eşzamansız işlem bir yanıt bekliyor.",
    "0x80B30000": "Eşzamansız işlem çağıran tarafından terk edildi.",
    "0x80B40000":
      "Akış istenen tüm verileri döndürmedi (muhtemelen non-blocking bir akış olduğu için).",
    "0x80B50000": "Non-blocking davranış gerekli ve işlem engelleyici olacak.",
    "0x80B60000": "Bir değerin sözdizimi geçersizdi.",
    "0x80B70000":
      "Tüm kullanılabilir bağlantılar kullanımda olduğu için işlem tamamlanamadı.",
    "0x42080000":
      "Transdüser manuel modda olduğu için değer doğru olmayabilir.",
    "0x42090000": "Değer simüle edilmiştir.",
    "0x420A0000":
      "Sensör kalibrasyon hatası nedeniyle değer doğru olmayabilir.",
    "0x420F0000": "Yapılandırma sorunu nedeniyle değer doğru olmayabilir.",
    "0x04010000":
      "Değer kaynağı cascade handshaking'i destekliyor ve değer, bir cascade secondary'den gelen başlatma isteğine göre başlatıldı.",
    "0x04020000":
      "Değer kaynağı cascade handshaking'i destekliyor ve bir cascade primary'nin başlatılmasını istiyor.",
    "0x04030000":
      "Değer kaynağı cascade handshaking'i destekliyor ancak kaynağın mevcut durumu cascade'e izin vermiyor.",
    "0x04040000":
      "Değer kaynağı cascade handshaking'i destekliyor ancak kullanım için ilgili cascade primary'yi seçmedi.",
    "0x04070000": "Değer kaynağında etkin bir fault state condition var.",
    "0x04080000": "Hedeften bir fault state condition isteniyor.",
    "0x04090000":
      "Değer doğrudur ve sinyal kaynağı cascade handshaking'i destekler.",
    "0x80E70000":
      "DataSetWriter oluşturulması için belirtilen DataSet geçersiz.",
  },
};
